import { createContext, useContext, useState, useCallback, useEffect } from "react";

const I18nContext = createContext(null);

const STORAGE_KEY = "dwrrah_lang";

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "ar";
  });

  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === "ar" ? "en" : "ar");
  }, [lang, setLang]);

  const dir = lang === "ar" ? "rtl" : "ltr";
  const isRTL = lang === "ar";

  // Apply dir/lang to <html>
  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [dir, lang]);

  const t = useCallback(
    (key, ...args) => {
      const dict = translations[lang];
      // Inline bilingual object form:
      //   t({ ar: "نص", en: "text" }) → returns the active-language string.
      // This lets pages render bilingual labels without registering a key,
      // and is critical for the AcademyNotFound page which previously
      // returned the object itself and crashed React with error #31.
      if (key && typeof key === "object" && !Array.isArray(key)) {
        const fromObj = key[lang] ?? key.ar ?? key.en ?? "";
        return typeof fromObj === "string" ? fromObj : String(fromObj ?? "");
      }
      // Determine vars + fallback from args (backward-safe):
      //   t(key)                     → no vars, no fallback
      //   t(key, "fallback")         → fallback only (legacy)
      //   t(key, { var: value })     → vars, no fallback
      //   t(key, { var }, "fallback")→ vars + fallback
      let vars = null;
      let fallback;
      if (args.length === 1) {
        if (args[0] && typeof args[0] === "object") vars = args[0];
        else fallback = args[0];
      } else if (args.length >= 2) {
        vars = args[0] && typeof args[0] === "object" ? args[0] : null;
        fallback = args[1];
      }
      let str = dict?.[key] ?? fallback ?? key;
      if (vars && typeof str === "string") {
        str = str.replace(/\{(\w+)\}/g, (_, k) =>
          vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ""
        );
      }
      return str;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, toggleLang, dir, isRTL, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
}

// ─── Translation dictionary ────────────────────────────────────────────────────
const translations = {
  ar: {
    // Common
    "common.loading": "جاري التحميل...",
    "common.save": "حفظ",
    "common.cancel": "إلغاء",
    "common.delete": "حذف",
    "common.edit": "تعديل",
    "common.add": "إضافة",
    "common.search": "بحث",
    "common.searchPlaceholder": "ابحث...",
    "common.filter": "تصفية",
    "common.actions": "إجراءات",
    "common.back": "رجوع",
    "common.next": "التالي",
    "common.prev": "السابق",
    "common.retry": "إعادة المحاولة",
    "common.close": "إغلاق",
    "common.confirm": "تأكيد",
    "common.yes": "نعم",
    "common.no": "لا",
    "common.or": "أو",
    "common.email": "البريد الإلكتروني",
    "common.password": "كلمة المرور",
    "common.name": "الاسم",
    "common.submit": "إرسال",
    "common.logout": "تسجيل الخروج",
    "common.login": "تسجيل الدخول",
    "common.signup": "إنشاء حساب",
    "common.menu": "القائمة",
    "common.noData": "لا توجد بيانات",
    "common.viewAll": "عرض الكل",
    "common.view": "عرض",
    "common.share": "مشاركة",
    "common.shareCopied": "تم نسخ رابط المشاركة",
    "common.shareCopyFailed": "تعذّر النسخ، انسخ الرابط يدوياً",
    "ins.seo.shareLinkHint": "اختياري: هذا رابط مشاركة سريع تستطيع نسخه ومشاركته فوراً. بعد تفعيل Cloudflare Worker على نطاق dwrrah.com (راجع docs/CLOUDFLARE_SOCIAL_PROXY_SETUP.md) سيعمل أي رابط مباشر من أكاديميتك تلقائياً مع شعارك وبياناتك في معاينات السوشل.",
    "common.status": "الحالة",
    "common.date": "التاريخ",
    "common.type": "النوع",
    "common.amount": "المبلغ",
    "common.all": "الكل",
    "common.active": "نشط",
    "common.free": "مجاني",
    "common.error": "حدث خطأ",
    "common.saved": "تم الحفظ",
    "common.saveError": "حدث خطأ أثناء الحفظ",
    "common.show": "إظهار",
    "common.hide": "إخفاء",
    "common.settings": "الإعدادات",
    "common.notifications": "الإشعارات",
    "common.send": "إرسال",
    "common.publish": "نشر",
    "common.unpublish": "إلغاء النشر",
    "common.create": "إنشاء",
    "common.update": "تحديث",
    "common.currency": "العملة",
    "common.description": "الوصف",
    "common.title": "العنوان",
    "common.image": "الصورة",
    "common.days": "أيام",
    "common.daily": "يومياً",
    "common.weekly": "أسبوعياً",
    "common.monthly": "شهري",
    "common.quarterly": "ربع سنوي",
    "common.annual": "سنوي",
    "common.lessons": "درس",
    "common.minutes": "دقيقة",
    "common.owner": "المالك",
    "common.manager": "المدير",
    "common.enterArrow": "الدخول ←",
    "common.enter": "الدخول",
    "common.buyNow": "اشترِ الآن",
    "common.getFree": "احصل مجاناً",
    "common.owned": "مُشترى",
    "common.paid": "مكتمل",
    "common.refunded": "مسترد",
    "common.expires": "ينتهي في",
    "common.course": "دورة",
    "common.courses": "دورات",
    "common.product": "منتج",
    "common.products": "منتجات",
    "common.session": "جلسة",
    "common.sessions": "جلسات",
    "common.certificate": "شهادة",
    "common.certificates": "شهادات",
    "common.student": "الطالب",
    "common.students": "الطلاب",
    "common.payments": "المدفوعات",
    "common.allRightsReserved": "جميع الحقوق محفوظة",
    "common.termsOfService": "شروط الاستخدام",
    "common.privacyPolicy": "سياسة الخصوصية",
    "common.subscribeNow": "اشترك الآن",
    "common.startNow": "ابدأ الآن",
    "common.bookNow": "احجز مكانك",
    "common.joinAcademy": "انضم للأكاديمية",
    "common.registration": "تسجيل",

    // Auth
    "auth.loginTitle": "تسجيل الدخول",
    "auth.loginSubtitle": "سجّل دخولك للوصول إلى لوحة التحكم",
    "auth.signupTitle": "أنشئ حسابك",
    "auth.signupSubtitle": "سجّل حسابك وابدأ تجربتك المجانية",
    "auth.fullName": "الاسم الكامل",
    "auth.forgotPassword": "نسيت كلمة المرور؟",
    "auth.forgotHint": "أدخل بريدك الإلكتروني أعلاه أولاً",
    "auth.noAccount": "ما عندك حساب؟",
    "auth.haveAccount": "عندك حساب؟",
    "auth.signupNow": "سجّل الآن",
    "auth.loginNow": "سجّل دخولك",
    "auth.orEmail": "أو بالبريد الإلكتروني",
    "auth.googleLogin": "الدخول بحساب Google",
    "auth.appleLogin": "الدخول بحساب Apple",
    "auth.googleSignup": "التسجيل بحساب Google",
    "auth.appleSignup": "التسجيل بحساب Apple",
    "auth.loggingIn": "جاري تسجيل الدخول...",
    "auth.signingUp": "جاري إنشاء الحساب...",
    "auth.signingIn": "جاري التسجيل...",
    "auth.createAccount": "إنشاء حساب",
    "auth.loginError": "خطأ في تسجيل الدخول",
    "auth.signupError": "خطأ في إنشاء الحساب",
    "auth.invalidCredentials": "البريد الإلكتروني أو كلمة المرور غير صحيحة",
    "auth.error": "خطأ",
    "auth.googleFail": "فشل تسجيل الدخول بحساب Google",
    "auth.appleFail": "فشل تسجيل الدخول بحساب Apple",
    "auth.googleSignupFail": "فشل التسجيل بحساب Google",
    "auth.appleSignupFail": "فشل التسجيل بحساب Apple",
    "auth.passwordMin": "كلمة المرور يجب أن تكون 6 أحرف على الأقل",
    "auth.resetSent": "تم إرسال رابط استعادة كلمة المرور إلى بريدك الإلكتروني",
    "auth.enterEmailFirst": "أدخل بريدك الإلكتروني أولاً",
    "auth.email": "البريد الإلكتروني",
    "auth.password": "كلمة المرور",
    "auth.confirmPassword": "تأكيد كلمة المرور",
    "auth.or": "أو",
    "auth.loginButton": "تسجيل الدخول",
    "auth.signupButton": "إنشاء الحساب",
    "auth.passwordMismatch": "كلمتا المرور غير متطابقتين",
    "auth.passwordTooShort": "كلمة المرور يجب أن تكون 6 أحرف على الأقل",
    "auth.signupCheckEmail": "تحقق من بريدك",
    "auth.signupCheckEmailDesc": "أرسلنا لك رابط تأكيد. افتح بريدك لإكمال إنشاء الحساب.",
    "auth.emailAlreadyRegistered": "هذا البريد مسجّل بالفعل. سجّل دخولك بدلاً من ذلك.",
    "auth.signupFailed": "تعذّر إنشاء الحساب. تحقق من البيانات وحاول مرة أخرى.",
    "auth.forgotTitle": "إعادة تعيين كلمة المرور",
    "auth.forgotSubtitle": "أدخل بريدك وسنرسل لك رمز التحقق",
    "auth.sendResetLink": "إرسال رمز التحقق",
    "auth.resetSentGeneric": "إذا كان البريد مسجّلاً لدينا، فسيصلك رمز التحقق.",
    "auth.otp.enterCode": "أدخل رمز التحقق المرسل إلى بريدك",
    "auth.otp.verifyButton": "تحقق من الرمز",
    "auth.otp.resend": "إعادة إرسال الرمز",
    "auth.otp.resendIn": "يمكنك إعادة الإرسال خلال {s} ثانية",
    "auth.otp.checkSpam": "تحقق من البريد المهمل (Spam). آخر رمز هو الرمز الصالح.",
    "auth.otp.invalid": "الرمز غير صحيح أو منتهي. حاول مرة أخرى.",
   "auth.otp.latestIsValid": "آخر رمز تم إرساله هو الرمز الصالح فقط.",
   "auth.admin.loginAfterReset": "تم تغيير كلمة المرور. الرجاء تسجيل الدخول من جديد.",
   "auth.admin.forgotTitle": "استعادة كلمة مرور الإدارة",
    "auth.newPassword": "كلمة المرور الجديدة",
    "auth.savePassword": "حفظ كلمة المرور",
    "auth.passwordChanged": "تم تغيير كلمة المرور بنجاح. سجّل الدخول الآن.",
    "auth.backToLogin": "العودة لتسجيل الدخول",
    "auth.emailNotRegistered": "هذا البريد الإلكتروني غير مسجل، تأكد من صحة البريد",
    "auth.checkEmail": "تحقق من بريدك الإلكتروني",
    "auth.confirmSent": "أرسلنا رابط تأكيد إلى",
    "auth.confirmAction": "يرجى فتح بريدك والضغط على الرابط لتفعيل حسابك.",
    "auth.backToLogin": "العودة لتسجيل الدخول",
    "auth.resendVerification": "إعادة إرسال رابط التحقق",
    "auth.resendIn": "يمكنك إعادة الإرسال خلال {s} ثانية",
    "auth.resendHint": "تحقق من مجلد الرسائل غير المرغوب فيها (Spam) إن لم يصل البريد.",
    "auth.resendSuccess": "تم إرسال رابط تحقق جديد إلى بريدك",
    "auth.resendError": "تعذّر إرسال رابط التحقق",
    "auth.resendRateLimit": "تم تجاوز الحد، حاول بعد قليل",
    "auth.emailNotConfirmed": "لم يتم تفعيل بريدك بعد، أرسلنا لك رابط تحقق جديد",
    "auth.err.weakPassword": "كلمة المرور ضعيفة وسهل تخمينها، اختر كلمة مرور أقوى (مثلاً: تحوي حروفاً كبيرة وصغيرة وأرقاماً ورموزاً، وبطول 8 أحرف أو أكثر).",
    "auth.err.passwordShort": "كلمة المرور قصيرة جداً، يجب أن تكون 6 أحرف أو أكثر.",
    "auth.err.emailNotConfirmed": "لم يتم تفعيل بريدك الإلكتروني بعد، يرجى تأكيد البريد أولاً.",
    "auth.err.userExists": "هذا البريد الإلكتروني مسجّل مسبقاً، حاول تسجيل الدخول أو استعادة كلمة المرور.",
    "auth.alreadyRegistered.title": "هذا البريد مسجّل لدينا مسبقاً",
    "auth.alreadyRegistered.body": "يرجى تسجيل الدخول بحسابك، وإذا نسيت كلمة المرور يمكنك طلب استعادتها.",
    "auth.err.rateLimit": "تم تجاوز عدد المحاولات، يرجى الانتظار قليلاً ثم المحاولة مرة أخرى.",
    "auth.err.invalidEmail": "صيغة البريد الإلكتروني غير صحيحة.",
    "auth.err.signupDisabled": "التسجيل مغلق حالياً، يرجى المحاولة لاحقاً.",
    "auth.err.network": "تعذّر الاتصال بالخادم، تأكد من اتصالك بالإنترنت وحاول مجدداً.",
    "auth.err.generic": "حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى.",
    "auth.otp.sentTo": "أرسلنا رمز التحقق المكوّن من 6 أرقام إلى",
    "auth.otp.enterBelow": "أدخل الرمز أدناه لتفعيل حسابك.",
    "auth.otp.verify": "تأكيد وتفعيل الحساب",
    "auth.otp.invalidLength": "أدخل رمز التحقق المكوّن من 6 أرقام.",
    "auth.otp.success": "تم تفعيل حسابك بنجاح",
    "auth.otp.error": "رمز التحقق غير صحيح أو منتهي الصلاحية",
    "auth.terms": "شروط الاستخدام",
    "auth.privacy": "سياسة الخصوصية",
    "auth.agreeTerms": "بالتسجيل أنت توافق على",
    "auth.and": "و",

    // Phone & profile completion
    "auth.phoneLabel": "رقم الجوال",
    "auth.phoneRequired": "الرجاء إدخال رقم الجوال",
    "auth.phoneInvalid": "رقم الجوال غير صحيح",
    "auth.phonePlaceholder": "5XXXXXXXX",
    "auth.completeProfile.title": "أهلاً بك يا {name} 👋",
    "auth.completeProfile.subtitle": "خطوة أخيرة وبتدخل! نحتاج رقم جوالك للتواصل بخصوص طلباتك.",
    "auth.completeProfile.cta": "متابعة",
    "auth.completeProfile.saving": "جاري الحفظ...",
    "auth.completeProfile.skip": "تخطّي حالياً",
    "auth.completeProfile.confirmExisting": "موافق، استخدم هذا الرقم",
    "auth.completeProfile.editPhone": "تعديل الرقم",
    "auth.completeProfile.savedToast": "تم حفظ بياناتك",

    // Signup benefits
    "signup.benefit1": "تجربة مجانية 14 يوم كاملة",
    "signup.benefit2": "بدون بطاقة ائتمان",
    "signup.benefit3": "إعداد في 5 دقائق",
    "signup.benefit4": "دعم عربي كامل",
    "signup.passwordPlaceholder": "6 أحرف على الأقل",
    "signup.namePlaceholder": "محمد أحمد",

    // Landing / Public
    "nav.home": "الرئيسية",
    "nav.features": "المميزات",
    "nav.howItWorks": "كيف تبدأ",
    "nav.pricing": "الأسعار",
    "nav.contact": "تواصل معنا",
    "nav.startFree": "ابدأ مجاناً",

    // Footer
    "footer.quickLinks": "روابط سريعة",
    "footer.pricing": "الأسعار",
    "footer.createAccount": "إنشاء حساب",
    "footer.contactTitle": "التواصل",
    "footer.legal": "قانوني",
    "footer.product": "المنتج",
    "footer.support": "الدعم",
    "footer.startFree": "ابدأ مجاناً",
    "footer.faq": "الأسئلة الشائعة",
    "footer.loginLink": "تسجيل الدخول",
    "footer.madeWith": "صُنع بـ 💜 للمدربين العرب",
    "footer.developedByCenter": "مطور بواسطة Saasmain ©",
    "footer.description": "منصة متكاملة تمكّن المدربين من إنشاء أكاديمياتهم الرقمية وبيع دوراتهم التدريبية باحترافية.",
    "footer.rights": "جميع الحقوق محفوظة.",
    "footer.madeByDwrrah": "صُنع بـ 💜 بواسطة منصة دورة",
    "footer.blog": "المدونة",
    "footer.trust.companyTitle": "معلومات الشركة",
    "footer.trust.developedBy": "شركة Saasmain LLC",
    "footer.trust.licensedIn": "شركة مسجلة ومرخصة في دولة الإمارات العربية المتحدة",
    "footer.trust.licenseNo": "رقم الرخصة التجارية: 2646101.01",
    "footer.trust.address": "الإمارات العربية المتحدة - الشارقة - مدينة الشارقة للإعلام",
    "footer.trust.regionTitle": "حضورنا في المنطقة",
    "footer.trust.regionLine": "منصة عربية تخدم المدربين وصنّاع المحتوى في السعودية والخليج والوطن العربي",
    "footer.trust.securePayments": "مدفوعات آمنة عبر مزود دفع موثوق",
    "footer.trust.verifyTitle": "التحقق من بيانات الرخصة",
    "footer.trust.licensedBy": "مرخصة بواسطة",
    "footer.trust.scanToVerify": "امسح الرمز للتحقق",
    "settings.attribution.title": "علامة دورة",
    "settings.attribution.toggle": "إظهار عبارة «صنع بحب بواسطة دورة» في الفوتر",
    "settings.attribution.lockedHint": "رقِّ إلى برو لإزالة عبارة «صنع بحب بواسطة دورة»",
    "settings.attribution.upgrade": "ترقية الآن",

    // Blog & Articles (Public)
    "blog.title": "المدونة",
    "blog.subtitle": "آخر المقالات والنصائح",
    "blog.readMore": "اقرأ المزيد",
    "blog.searchPlaceholder": "ابحث في المقالات...",
    "blog.allTags": "كل التصنيفات",
    "blog.noArticles": "لا توجد مقالات منشورة حالياً",
    "blog.readingTime": "{minutes} دقائق قراءة",
    "blog.backToBlog": "العودة للمدونة",
    "blog.relatedArticles": "مقالات ذات صلة",
    "blog.author": "Dwrrah | دورة",
    "blog.publishedOn": "نُشر في",
    "blog.share": "مشاركة المقال",

    // Admin - Marketing (Articles, Tags, Landing SEO)
    "admin.marketing.articles": "المقالات",
    "admin.marketing.tags": "التصنيفات",
    "admin.marketing.landingSeo": "SEO الرئيسية",
    "admin.marketing.coupons": "الكوبونات",
    "admin.marketing.redemptions": "الاستردادات",
    
    "admin.articles.title": "إدارة المقالات",
    "admin.articles.createArticle": "إنشاء مقال",
    "admin.articles.searchPlaceholder": "ابحث في المقالات...",
    "admin.articles.filterAll": "الكل",
    "admin.articles.filterDraft": "مسودة",
    "admin.articles.filterPublished": "منشور",
    "admin.articles.filterHidden": "مخفي",
    "admin.articles.statusDraft": "مسودة",
    "admin.articles.statusPublished": "منشور",
    "admin.articles.statusHidden": "مخفي",
    "admin.articles.pinned": "مثبّت",
    "admin.articles.pin": "تثبيت",
    "admin.articles.unpin": "إلغاء التثبيت",
    "admin.articles.publish": "نشر",
    "admin.articles.unpublish": "إخفاء",
    "admin.articles.noArticles": "لا توجد مقالات",
    "admin.articles.createFirst": "أنشئ أول مقال",
    "admin.articles.noResults": "لا توجد نتائج",
    
    "admin.articleEditor.title": "تحرير المقال",
    "admin.articleEditor.createTitle": "إنشاء مقال جديد",
    "admin.articleEditor.tabAr": "العربية",
    "admin.articleEditor.tabEn": "English",
    "admin.articleEditor.slug": "رابط المقال (slug)",
    "admin.articleEditor.slugPlaceholder": "my-article-slug",
    "admin.articleEditor.coverImage": "صورة الغلاف",
    "admin.articleEditor.uploadCover": "رفع صورة",
    "admin.articleEditor.removeCover": "إزالة",
    "admin.articleEditor.titleLabel": "العنوان",
    "admin.articleEditor.titlePlaceholder": "عنوان المقال...",
    "admin.articleEditor.excerptLabel": "ملخص قصير",
    "admin.articleEditor.excerptPlaceholder": "ملخص سريع للمقال...",
    "admin.articleEditor.bodyLabel": "محتوى المقال",
    "admin.articleEditor.bodyPlaceholder": "اكتب محتوى المقال هنا...",
    "admin.articleEditor.seoTitle": "عنوان SEO",
    "admin.articleEditor.seoDescription": "وصف SEO",
    "admin.articleEditor.ogImage": "صورة OG",
    "admin.articleEditor.tags": "التصنيفات",
    "admin.articleEditor.selectTags": "اختر تصنيفات...",
    "admin.articleEditor.status": "الحالة",
    "admin.articleEditor.pin": "تثبيت المقال",
    "admin.articleEditor.saving": "جاري الحفظ...",
    "admin.articleEditor.save": "حفظ",
    "admin.articleEditor.aiTranslate": "ترجمة ذكية",
    "admin.articleEditor.translating": "جاري الترجمة...",
    "admin.articleEditor.translationSuccess": "تمت الترجمة بنجاح",
    "admin.articleEditor.translationError": "حدث خطأ أثناء الترجمة",
    "admin.articleEditor.saveFirst": "احفظ المقال أولاً لتفعيل الترجمة الذكية",
    "admin.articleEditor.sourceLocale": "اللغة المصدر",
    "admin.articleEditor.saved": "تم الحفظ بنجاح",
    "admin.articleEditor.saveError": "حدث خطأ أثناء الحفظ",
    
    "admin.tags.title": "إدارة التصنيفات",
    "admin.tags.createTag": "إضافة تصنيف",
    "admin.tags.nameAr": "الاسم بالعربية",
    "admin.tags.nameEn": "الاسم بالإنجليزية",
    "admin.tags.slug": "Slug",
    "admin.tags.creating": "جاري الإضافة...",
    "admin.tags.noTags": "لا توجد تصنيفات",
    "admin.tags.deleteConfirm": "هل أنت متأكد من حذف هذا التصنيف؟",
    "admin.tags.deleting": "جاري الحذف...",
    "admin.tags.created": "تم إنشاء التصنيف",
    "admin.tags.deleted": "تم حذف التصنيف",
    
    "admin.landingSeo.title": "SEO صفحة الهبوط",
    "admin.landingSeo.subtitle": "إعدادات تحسين محركات البحث للصفحة الرئيسية",
    "admin.landingSeo.tabAr": "العربية",
    "admin.landingSeo.tabEn": "English",
    "admin.landingSeo.pageTitle": "عنوان الصفحة",
    "admin.landingSeo.metaDescription": "الوصف التعريفي",
    "admin.landingSeo.ogImage": "صورة OG",
    "admin.landingSeo.uploadOg": "رفع صورة OG",
    "admin.landingSeo.removeOg": "إزالة",
    "admin.landingSeo.canonical": "الرابط الأساسي (Canonical)",
    "admin.landingSeo.organizationName": "اسم المنظمة",
    "admin.landingSeo.organizationLogo": "شعار المنظمة (URL)",
    "admin.landingSeo.organizationUrl": "موقع المنظمة",
    "admin.landingSeo.navVisibility": "إظهار المدونة في القوائم",
    "admin.landingSeo.showInHeader": "إظهار في الهيدر",
    "admin.landingSeo.showInFooter": "إظهار في الفوتر",
    "admin.landingSeo.labelAr": "التسمية بالعربية",
    "admin.landingSeo.labelEn": "التسمية بالإنجليزية",
    "admin.landingSeo.saving": "جاري الحفظ...",
    "admin.landingSeo.save": "حفظ الإعدادات",
    "admin.landingSeo.saved": "تم حفظ الإعدادات",
    "admin.landingSeo.saveError": "حدث خطأ أثناء الحفظ",

    // Instructor sidebar & general
    "ins.dashboard": "الرئيسية",
    "ins.courses": "الدورات",
    "ins.questionBank": "بنك الأسئلة",
    "ins.sessions": "الجلسات",
    "ins.students": "الطلاب",
    "ins.messages": "الرسائل",
    "ins.comments": "التعليقات",
    "ins.autoEmail": "البريد التلقائي",
    "ins.payments": "المدفوعات",
    "ins.wallet": "المحفظة",
    "ins.branding": "الهوية البصرية",
    "ins.pages": "الصفحات العامة",
    "ins.products": "المنتجات",
    "ins.coupons": "الكوبونات",
    "ins.certificates": "الشهادات",
    "ins.settings": "الإعدادات",
    "ins.plan": "الخطة",
    "ins.planPro": "الاحترافية",
    "ins.planBasic": "الأساسية",
    "ins.profile": "الملف الشخصي",
    "ins.switchAcademies": "التبديل بين الأكاديميات",
    "ins.guide": "الدليل",
    "ins.myAcademy": "أكاديميتي",
    "ins.platformLink": "موقع منصة دورة",
    "ins.overview": "نظرة عامة",
    "ins.quizzes": "الاختبارات",
    "ins.createCourse": "أنشئ دورة",
    "ins.createSession": "أنشئ جلسة",
    "ins.createProduct": "أضف منتج",
    "ins.createCoupon": "إنشاء الكوبون",
    "ins.searchStudents": "ابحث عن طالب...",
    "ins.searchCoupons": "ابحث بالكود أو العنوان...",
    "ins.enrollments": "التسجيلات",
    "ins.revenue": "الإيرادات",
    "ins.content": "المحتوى",
    "ins.engagement": "التفاعل",
    "ins.academyName": "اسم الأكاديمية",
    "ins.contactEmail": "إيميل التواصل",
    "ins.primaryColor": "اللون الأساسي",
    "ins.secondaryColor": "اللون الثانوي",
    "ins.currencyTax": "العملة والضرائب",
    "ins.billing": "الفوترة",
    "ins.integrations": "التكامل",
    "ins.socialFooter": "الموقع والسوشال",
    "ins.notificationsSettings": "الإشعارات",
    "ins.subscription": "الاشتراك",
    "ins.upgradeBanner.title": "💡 الترقية ذكية ومرنة",
    "ins.upgradeBanner.line1": "من شهري إلى سنوي: تدفع فارق السعر فقط بحسب الأيام المتبقية.",
    "ins.upgradeBanner.line2": "من الأساسية إلى الاحترافية: نحسب رصيدك من الباقة الحالية ونخصمه تلقائياً.",
    "ins.upgradeBanner.line3": "الترقية فورية عند تأكيد الدفع — وتاريخ تجديدك لا يتغيّر.",
    "ins.manualPlanNotice.title": "باقتك مفعّلة يدوياً من الإدارة",
    "ins.manualPlanNotice.desc": "لا يوجد اشتراك دفع نشط مرتبط بحسابك حالياً، لذا عند الترقية سيتم احتساب السعر الكامل للباقة الجديدة دون احتساب فارق.",
    "ins.upgradeToast.fullPrice.title": "ستدفع السعر الكامل",
    "ins.upgradeToast.fullPrice.desc": "لا يوجد اشتراك دفع نشط حالياً، لذلك سيتم تحويلك لإكمال الدفع بالسعر الكامل للباقة الجديدة.",
    "ins.dataExport": "تصدير البيانات",
    "ins.domains": "النطاق المخصص",
    "ins.teamManagement": "إدارة الفريق",
    "ins.emailLogs": "سجل البريد",
    "ins.emailCampaign": "حملة بريدية",
    "ins.notificationHistory": "سجل الإشعارات",
    "ins.upcomingSessions": "الجلسات القادمة",
    "ins.weeklyDigest": "الملخص الأسبوعي",

    // Instructor pages
    "ins.pages.landing": "الرئيسية",
    "ins.pages.about": "من نحن",
    "ins.pages.terms": "الشروط والأحكام",
    "ins.pages.privacy": "سياسة الخصوصية",
    "ins.pages.courses": "الدورات",
    "ins.pages.sessions": "الجلسات",
    "ins.pages.custom": "مخصصة",
    "ins.pages.homepage": "الصفحة الرئيسية",

    // Instructor course
    "ins.course.settings": "إعدادات الدورة",
    "ins.course.create": "إنشاء الدورة",
    "ins.course.published": "منشورة",
    "ins.course.draft": "مسودة",
    "ins.course.completionOnly": "إكمال فقط",
    "ins.course.freeMovement": "الطالب يتنقل بحرية",
    "ins.course.quizNoBlock": "الكويز لا يمنع التقدم",
    "ins.course.dripContent": "إتاحة المحتوى (Drip)",
    "ins.course.sequential": "التسلسل",
    "ins.course.certEnabled": "شهادة الإتمام",
    "ins.course.discussion": "النقاشات",
    "ins.course.comments": "التعليقات",
    "ins.course.chooseYourCourse": "اختر الدورة المناسبة لك وابدأ رحلة التعلم",
    "ins.course.availableCourses": "الدورات المتاحة",
    "ins.course.productsGrantAccess": "المنتجات التي تمنح وصولاً لهذه الدورة",
    "ins.course.contentOnly": "الدورات والمحتوى والاختبارات فقط",

    // Instructor product
    "ins.product.bundle": "باقة",
    "ins.product.membership": "عضوية",
    "ins.product.sessionPackage": "باقة جلسات",
    "ins.product.singleSession": "جلسة خاصة",
    "ins.product.subscription": "اشتراك متكرر",
    "ins.product.subscriptionDesc": "اشتراك متكرر للوصول لمحتوى محدد",
    "ins.product.pricing": "التسعير",
    "ins.product.access": "الدورات والجلسات المشمولة في المنتج",
    "ins.product.specificTypes": "أنواع محددة",
    "ins.product.allCourses": "جميع الدورات",
    "ins.product.allSessions": "جميع الجلسات",
    "ins.product.bundles": "باقات",
    "ins.product.memberships": "عضويات",
    "ins.product.sessionPackages": "باقات جلسات",
    "ins.product.privateSession": "جلسة خاصة",

    // Instructor sessions
    "ins.session.group": "جماعية",
    "ins.session.private": "خاصة",
    "ins.session.liveNow": "الجلسة مباشرة الآن",
    "ins.session.liveSessions": "الجلسات المباشرة",
    "ins.session.liveAndRecording": "المباشرة + التسجيل",
    "ins.session.liveOnly": "المشاهدة المباشرة فقط",
    "ins.session.bookings": "الحجوزات",
    "ins.session.cancelSession": "إلغاء جلسة",
    "ins.session.bookSession": "حجز جلسة",
    "ins.session.sendInvites": "إرسال دعوة لكل الطلاب المسجلين",
    "ins.session.createFirst": "إنشاء أول جلسة أونلاين",
    "ins.session.createWithoutInvites": "إنشاء الجلسة بدون إرسال دعوات",

    // Instructor quiz
    "ins.quiz.title": "الاختبار",
    "ins.quiz.finalExam": "الاختبار النهائي",
    "ins.quiz.newQuiz": "اختبار جديد",
    "ins.quiz.multipleChoice": "اختيار متعدد",
    "ins.quiz.singleChoice": "اختيار واحد",
    "ins.quiz.multiSelect": "اختيار من متعدد",
    "ins.quiz.matching": "الأزواج",
    "ins.quiz.ordering": "الترتيب",
    "ins.quiz.fillBlank": "تعبئة الفراغ",
    "ins.quiz.question": "السؤال",
    "ins.quiz.answer": "الجواب",
    "ins.quiz.options": "الخيارات",
    "ins.quiz.acceptedAnswers": "الإجابات المقبولة",
    "ins.quiz.addOption": "إضافة خيار",
    "ins.quiz.addAltAnswer": "إضافة إجابة بديلة",
    "ins.quiz.grade": "الدرجة",
    "ins.quiz.items": "العناصر (بالترتيب الصحيح)",
    "ins.quiz.writeQuestion": "اكتب السؤال هنا...",
    "ins.quiz.linkedQuiz": "اختبار مربوط",
    "ins.quiz.inlineQuiz": "اختبار مضمّن",
    "ins.quiz.inlineHint": "اختبار مضمّن — احفظ الدرس ثم أنشئ اختبار",
    "ins.quiz.quickQuiz": "اختبار سريع بسؤالين افتراضيين",
    "ins.quiz.proOnly": "الاختبارات متاحة في الخطة المتقدمة فقط.",

    // Instructor wallet
    "ins.wallet.deposit": "إيداع",
    "ins.wallet.adminDeposit": "إيداع يدوي (إداري)",
    "ins.wallet.refund": "استرجاع",
    "ins.wallet.studentPurchase": "شراء طالب",
    "ins.wallet.paymentFailed": "فشل دفع",
    "ins.wallet.paymentReceipt": "إيصال دفع",

    // Instructor onboarding
    "ins.onboard.setupAcademy": "إعداد أكاديميتك",
    "ins.onboard.createFirstCourse": "إنشاء أول دورة تعليمية",
    "ins.onboard.createFirstSession": "إنشاء أول جلسة أونلاين",
    "ins.onboard.createLanding": "إنشاء صفحة الهبوط",
    "ins.onboard.createFirstProduct": "إنشاء أول منتج",
    "ins.onboard.chooseSlug": "اختر رابط مميز لأكاديميتك",
    "ins.onboard.launch": "الانطلاق!",
    "ins.onboard.firstCourse": "أول دورة",
    "ins.onboard.firstSession": "أول جلسة",
    "ins.onboard.firstProduct": "أول منتج",
    "ins.onboard.slugUsed": "الرابط مستخدم",
    "ins.onboard.slugMinLength": "الرابط يجب أن يكون 3 أحرف على الأقل",
    "ins.onboard.chooseAnotherSlug": "اختر رابطاً آخر",

    // Instructor dashboard
    "ins.dash.dashboardTitle": "لوحة التحكم",
    "ins.dash.healthScore": "مؤشر الصحة",
    "ins.dash.quickActions": "إجراءات سريعة",
    "ins.dash.coursePerformance": "أداء الدورات",
    "ins.dash.enrollmentRevenue": "التسجيلات والإيرادات",
    "ins.dash.operational": "الجلسات الحية والعمليات",
    "ins.dash.walletSummary": "ملخص المحفظة",
    "ins.dash.viewAll": "عرض الكل",
    "ins.dash.wallet": "المحفظة",
    "ins.dash.topCourses": "أفضل الدورات",
    "ins.dash.noCourses": "لا توجد دورات بعد",
    "ins.dash.course": "الدورة",
    "ins.dash.enrollments": "التسجيلات",
    "ins.dash.completion": "الإكمال",
    "ins.dash.revenue": "الإيرادات",
    "ins.dash.revenueWith": "الإيرادات ({currency})",
    "ins.dash.academyHealth": "صحة الأكاديمية",
    "ins.dash.outOf100": "من 100",
    "ins.dash.content": "المحتوى",
    "ins.dash.students": "الطلاب",
    "ins.dash.engagement": "التفاعل",
    "ins.dash.recentEnrollments": "آخر التسجيلات",
    "ins.dash.recentPayments": "آخر المدفوعات",
    "ins.dash.noRecentEnrollments": "لا توجد تسجيلات حديثة",
    "ins.dash.noRecentPayments": "لا توجد مدفوعات حديثة",

    // Instructor branding
    "ins.brand.coverImage": "الغلاف اللي تبيع منه دوراتك",
    "ins.brand.imageUpdated": "تم تحديث الصورة",
    "ins.brand.showPreview": "إظهار المعاينة",
    "ins.brand.hidePreview": "إخفاء المعاينة",

    // Instructor email triggers
    "ins.email.enrollment": "تأكيد التسجيل",
    "ins.email.purchaseConfirm": "تأكيد شراء",
    "ins.email.courseComplete": "إكمال دورة",
    "ins.email.certificateIssued": "إصدار شهادة",
    "ins.email.sessionReminder": "تذكير بجلسة",
    "ins.email.sessionCancel": "إلغاء جلسة",
    "ins.email.paymentReceipt": "إيصال الدفع",
    "ins.email.welcomeStudent": "ترحيب بطالب جديد",
    "ins.email.inactivityReminder": "تذكير الخمول",
    "ins.email.weeklyDigest": "الملخص الأسبوعي",
    "ins.email.onEnroll": "عند الاشتراك في دورة",
    "ins.email.onComplete": "عند إتمام دورة",
    "ins.email.afterPayment": "بعد كل عملية دفع",
    "ins.email.writeContent": "اكتب محتوى الإيميل...",
    "ins.email.studentEmail": "إيميل الطالب",
    "ins.email.courseStudents": "المشتركون في الدورة المرتبطة",
    "ins.email.specificStudent": "الطالب المعني فقط",
    "ins.email.productStudents": "الطلاب الذين اشتروا منتج معين",

    // Instructor messaging
    "ins.msg.newMessage": "رسالة جديدة",
    "ins.msg.writeReply": "اكتب ردك...",
    "ins.msg.writeGroupMsg": "اكتب رسالة للمجموعة...",
    "ins.msg.writeMessageContent": "اكتب محتوى الرسالة...",
    "ins.msg.selectStudents": "اختيار طلاب محددين من القائمة",
    "ins.msg.selectOneStudent": "اختر طالب واحد على الأقل",
    "ins.msg.manualSelect": "اختيار يدوي",

    // Instructor students
    "ins.stu.overview": "نظرة عامة",
    "ins.stu.lastLogin": "آخر دخول",
    "ins.stu.enrolledInCourse": "الطالب مسجل بالفعل في هذه الدورة.",
    "ins.stu.writeNote": "اكتب الملاحظة...",
    "ins.stu.aboutYou": "أخبرنا عنك...",
    "ins.stu.validEmail": "أدخل بريد إلكتروني صحيح",

    // Instructor settings
    "ins.set.general": "الإعدادات",
    "ins.set.resubscribe": "إعادة الاشتراك",
    "ins.set.nextRenewal": "التجديد القادم",
    "ins.set.inviteTeam": "دعوة عضو",
    "ins.set.position": "المنصب (اختياري)",
    "ins.set.googleCalConnected": "تم ربط Google Calendar بنجاح ✅",
    "ins.set.googleCalDisconnected": "تم فصل Google Calendar",
    "ins.set.domainSuccess": "النطاق المخصص مفعّل بنجاح.",
    "ins.set.titleRequired": "العنوان مطلوب",

    // Instructor coupon
    "ins.coupon.searchPlaceholder": "ابحث بالكود أو العنوان...",

    // Instructor certificate
    "ins.cert.defaultTemplate": "القالب الافتراضي",
    "ins.cert.signature": "التوقيع",
    "ins.cert.alreadyExists": "الشهادة موجودة مسبقاً",
    "ins.cert.issuedSuccess": "تم إصدار شهادة إتمام الدورة بنجاح.",
    "ins.cert.getCertForEachCourse": "احصل على شهادة إتمام لكل دورة",

    // Instructor page editor
    "ins.editor.hero": "البطل (Hero)",
    "ins.editor.mainTitle": "العنوان الرئيسي",
    "ins.editor.subtitle": "النص الفرعي",
    "ins.editor.text": "النص",
    "ins.editor.icon": "الأيقونة",
    "ins.editor.term": "المصطلح",
    "ins.editor.definition": "التعريف",
    "ins.editor.features": "المميزات",
    "ins.editor.testimonials": "آراء العملاء",
    "ins.editor.faq": "أسئلة وأجوبة قابلة للطي",
    "ins.editor.addSection": "إضافة قسم",
    "ins.editor.addItem": "إضافة عنصر",
    "ins.editor.headerCourses": "الدورات",
    "ins.editor.headerProducts": "المنتجات",
    "ins.editor.headerSessions": "الجلسات",

    // Instructor data export
    "ins.export.students": "الطلاب",
    "ins.export.enrollments": "التسجيلات",
    "ins.export.courses": "الدورات",
    "ins.export.payments": "المدفوعات",

    // Instructor lock screen
    "ins.lock.academyLocked": "أكاديميتك مقفلة",

    // Student sidebar
    "stu.browse": "تصفح",
    "stu.home": "الرئيسية",
    "stu.courses": "الدورات",
    "stu.products": "المنتجات",
    "stu.sessions": "الجلسات",
    "stu.myAccount": "حسابي",
    "stu.dashboard": "لوحة التحكم",
    "stu.myCourses": "دوراتي",
    "stu.mySessions": "جلساتي",
    "stu.mySubscriptions": "اشتراكاتي",
    "stu.explore": "استكشف",
    "stu.myPurchases": "مشترياتي",
    "stu.freeAccess": "الوصول المجاني",
    "stu.inbox": "الرسائل",
    "stu.certificates": "شهاداتي",
    "stu.profile": "الملف الشخصي",
    "stu.exit": "خروج",
    "stu.invites": "المنح والدعوات",
    "stu.settings": "الإعدادات",
    "stu.simNotAvailable": "غير متاح في وضع المحاكاة",
    "stu.academyPage": "صفحة الأكاديمية",
    "stu.academy": "الأكاديمية",
    "stu.joinAcademy": "انضم للأكاديمية",
    "stu.enterAcademy": "الدخول إلى الأكاديمية",
    "stu.officialDomain": "الدومين الرسمي",
    "stu.visitOnCustomDomain": "زيارة على الدومين المخصص",
    "stu.platformLink": "رابط المنصة",

    // Student welcome onboarding
    "stu.welcome.title": "أهلاً بك",
    "stu.welcome.badge": "أهلاً بك",
    "stu.welcome.subtitle": "يسعدنا انضمامك إلينا",
    "stu.welcome.skip": "تخطّي",
    "stu.welcome.startTour": "ابدأ جولة سريعة",
    "stu.welcome.fallback": "نتمنى لك تجربة تعلّم ممتازة. لنأخذك في جولة سريعة لنعرّفك على أهم الأقسام.",

    // Student explore (product-only discovery)
    "stu.exploreTitle": "استكشف المنتجات",
    "stu.exploreSubtitle": "المنتجات المتاحة في الأكاديمية — الدورات تُمنح من خلال المنتجات فقط",
    "stu.noQuizQuestions": "لا توجد أسئلة في هذا الاختبار بعد. تواصل مع المدرب.",
    "stu.matchingHint": "اضغط عنصرًا من العمود الأول ثم العنصر المطابق له من العمود الثاني",
    "stu.clearSelection": "إلغاء التحديد",
    "stu.clearMatch": "إلغاء الربط",
    "stu.confirmEmptySubmit": "لم تجب على أي سؤال. هل تريد التسليم فعلاً؟",
    "stu.discoverProducts": "اكتشف المزيد من المنتجات",
    "stu.noProducts": "لا توجد منتجات متاحة حالياً",
    "stu.noCourses": "لا توجد دورات متاحة حالياً",
    "stu.searchCourses": "ابحث عن دورة...",
    "stu.searchProducts": "ابحث عن منتج...",
    "stu.browseCourses": "استعرض الدورات المتاحة",
    "stu.startCourse": "ابدأ الدورة",

    // Student purchases
    "stu.purchasesTitle": "مشترياتي",
    "stu.purchasesSubtitle": "المنتجات التي لديك حق الوصول إليها",
    "stu.noPurchases": "لا توجد مشتريات بعد",
    "stu.exploreProducts": "استكشف المنتجات",
    "stu.sessionCredits": "أرصدة الجلسات:",
    "stu.productContent": "محتوى المنتج",
    "stu.viewProductPage": "عرض صفحة المنتج",
    "stu.purchaseDate": "تاريخ الشراء:",
    "stu.ownedProductTitle": "منتج مملوك",
    "stu.notOwnedYet": "هذا المنتج غير ضمن مشترياتك",
    "stu.notOwnedYetHint": "لم تشترِ هذا المنتج بعد ولم يُمنح لك. يمكنك مراجعة تفاصيله في صفحة الاكتشاف.",
    "stu.goToDiscovery": "الذهاب إلى صفحة المنتج",
    "stu.ownedSubscriptionManage": "إدارة الاشتراك",
    "stu.ownedContentSection": "محتوى المنتج المتاح لك",
    "stu.lfaUpgradeHint": "أنت تستخدم وصولاً مجانياً محدوداً لهذا المنتج.",
    "stu.lfaUpgradeCta": "ترقية للوصول الكامل",
    "stu.source.oneTime": "شراء لمرة واحدة",
    "stu.source.oneTimeHint": "وصول دائم للمحتوى المضمّن في هذا المنتج.",
    "stu.source.recurring": "اشتراك متجدد",
    "stu.source.recurringHint": "يتجدد تلقائياً حسب خطة الاشتراك.",
    "stu.source.renewsOn": "يتجدد في",
    "stu.source.grant": "منحة مجانية",
    "stu.source.grantHint": "تم منحك هذا المنتج من المدرّب — مفعّل تلقائياً.",
    "stu.source.limited": "وصول مجاني محدود",
    "stu.source.limitedHint": "وصول جزئي مؤقت — قم بالترقية للوصول الكامل.",
    "stu.source.timed": "وصول لمدة محددة",

    // Student subscriptions
    "stu.subscriptionsTitle": "اشتراكاتي",
    "stu.subscriptionsSubtitle": "الفواتير والمدفوعات والاشتراكات المتجددة",
    "stu.noSubscriptions": "لا توجد اشتراكات",
    "stu.recurringSubscriptions": "اشتراكات متجددة",
    "stu.nextRenewal": "التجديد القادم:",
    "stu.oneTimePayments": "مدفوعات لمرة واحدة",
    "stu.paymentHistory": "سجل المدفوعات",
    "stu.activeSubscriptions": "اشتراكات نشطة",

    // Student sessions
    "stu.bookAndJoin": "احجز وانضم",
    "stu.upcomingSessions": "جلسات قادمة",
    "stu.needSubscription": "تحتاج اشتراك",
    "stu.cancelSession": "إلغاء جلسة",

    // Student quiz
    "stu.issueCertificate": "إصدار شهادة",

    // Student email prefs
    "stu.emailPrefs.confirmEnroll": "تأكيد التسجيل",
    "stu.emailPrefs.purchaseConfirm": "تأكيد شراء",
    "stu.emailPrefs.courseComplete": "إكمال دورة",
    "stu.emailPrefs.certIssued": "إصدار شهادة",
    "stu.emailPrefs.sessionReminder": "تذكير جلسة",
    "stu.emailPrefs.cancelSession": "إلغاء جلسة",
    "stu.emailPrefs.paymentReceipt": "إيصال الدفع",
    "stu.emailPrefs.weeklyDigest": "الملخص الأسبوعي",
    "stu.emailPrefs.activated": "تم التفعيل",
    "stu.emailPrefs.cancelled": "تم الإلغاء",

    // Admin sidebar
    "admin.dashboard": "لوحة التحكم",
    "admin.academies": "الأكاديميات",
    "admin.subscriptions": "الاشتراكات",
    "admin.sales": "المبيعات",
    "admin.students": "الطلاب",
    "admin.notifications": "الإشعارات",
    "admin.auditLog": "سجل المراجعة",
    "admin.platformAdmin": "إدارة المنصة",
    "admin.dwrrah": "دورة | Dwrrah",
    "admin.logout": "تسجيل الخروج",
    "admin.team": "إدارة الفريق",
    "admin.teamDesc": "إدارة مسؤولي المنصة ودعوة أعضاء جدد",
    "admin.inviteAdmin": "دعوة مسؤول",
    "admin.inviteNew": "دعوة مسؤول جديد",
    "admin.sendInvite": "إرسال الدعوة",
    "admin.currentAdmins": "المسؤولون الحاليون",
    "admin.revokeAccess": "تعليق الوصول",
    "admin.noAccess": "ليس لديك صلاحية الوصول لهذه الصفحة",
    "admin.roleOwner": "مالك المنصة",
    "admin.roleAdmin": "مسؤول كامل",
    "admin.roleStaff": "موظف (عرض فقط)",
    "admin.roleAdminDesc": "مسؤول كامل — صلاحيات كاملة بدون إدارة مسؤولين",
    "admin.roleStaffDesc": "موظف — عرض فقط",
    "admin.statusActive": "نشط",
    "admin.statusPending": "بانتظار التفعيل",
    "admin.statusRevoked": "معلّق",
    "admin.inviteSuccess": "تمت الدعوة بنجاح",
    "admin.revokeSuccess": "تم تعليق الوصول",
    "admin.enterEmail": "أدخل البريد الإلكتروني",
    "admin.role": "الدور",
    "admin.dateAdded": "تاريخ الإضافة",
    "admin.status": "الحالة",

    // Admin dashboard
    "admin.dashboardTitle": "لوحة التحكم",
    "admin.totalAcademies": "إجمالي الأكاديميات",
    "admin.activeSubscriptions": "الاشتراكات النشطة",
    "admin.totalStudents": "إجمالي الطلاب",
    "admin.totalRevenue": "إجمالي الإيرادات",
    "admin.trialCount": "تجريبي",
    "admin.pastDueCount": "متأخر الدفع",
    "admin.totalCourses": "الدورات",
    "admin.totalProducts": "المنتجات",
    "admin.walletBalance": "رصيد المحافظ",
    "admin.planDistribution": "توزيع الباقات",
    "admin.statusDistribution": "حالات الأكاديميات",
    "admin.monthlyGrowth": "النمو الشهري",
    "admin.monthlyRevenue": "الإيرادات الشهرية",
    "admin.topAcademies": "أعلى الأكاديميات دخلاً",
    "admin.academyName": "الأكاديمية",
    "admin.revenue": "الإيرادات",
    "admin.academiesLabel": "أكاديميات",
    "admin.studentsLabel": "طلاب",
    "admin.revenueLabel": "الإيرادات",
    "admin.planPro": "احترافي",
    "admin.planBasic": "أساسي",
    "admin.planUnknown": "غير محدد",
    "admin.period7d": "آخر 7 أيام",
    "admin.period30d": "آخر 30 يوم",
    "admin.period90d": "آخر 3 أشهر",
    "admin.period6m": "آخر 6 أشهر",
    "admin.period1y": "آخر سنة",
    "admin.periodAll": "الكل",
    "admin.sar": "ر.س",

    // Variant × Status (4 plans × 6 statuses + Legacy)
    "admin.variantBasicMonthly": "أساسي شهري",
    "admin.variantBasicAnnual": "أساسي سنوي",
    "admin.variantProMonthly": "احترافي شهري",
    "admin.variantProAnnual": "احترافي سنوي",
    "admin.variantLegacy": "تجارب قديمة",
    "admin.variantLegacyDesc": "قبل اعتماد اختيار الدورة — لا تُحتسب ضمن variants",
    "admin.statusTrialActive": "تجربة نشطة",
    "admin.statusTrialCancelled": "تجربة ملغاة",
    "admin.statusTrialExpired": "تجربة منتهية",
    "admin.statusPaidActive": "مدفوع نشط",
    "admin.statusPaidCancelScheduled": "إلغاء مجدول",
    "admin.statusPaidExpired": "مدفوع منتهي",
    "admin.willNotRenew": "لن يتجدد",
    "admin.cycleMigrations": "تبديل دورة الفوترة",
    "admin.tierMigrations": "تبديل الباقة",
    "admin.variantStatusMatrix": "مصفوفة Variant × Status",
    "admin.desyncedAcademies": "أكاديميات بحالة عدم تطابق",
    "admin.desyncedDesc": "خطة الأكاديمية مختلفة عن خطة الاشتراك",
    "admin.sourceTrialInternal": "تجربة (داخلية)",
    "admin.sourceStripeSynced": "Stripe مُزامنة",
    "admin.sourceDesynced": "⚠️ غير مُزامنة",
    "admin.sourceLegacyTrial": "تجربة قديمة",

    // Admin settings
    "admin.settings": "الإعدادات",
    "admin.settingsDesc": "إعدادات المنصة العامة والأسعار والنظام",
    "admin.settingsPricing": "الأسعار",
    "admin.settingsConfig": "الإعدادات العامة",
    "admin.settingsSystem": "حالة النظام",
    "admin.settingsSaved": "تم حفظ الإعدادات بنجاح",
    "admin.settingsError": "حدث خطأ أثناء حفظ الإعدادات",
    "admin.basicPlanDesc": "الخطة الأساسية — كل ما يحتاجه المدرب للبدء",
    "admin.proPlanDesc": "الخطة الاحترافية — أدوات متقدمة وتجربة كاملة",
    "admin.monthlyPrice": "السعر الشهري",
    "admin.annualPrice": "السعر السنوي",
    "admin.annualSavings": "التوفير السنوي",
    "admin.savePrices": "حفظ الأسعار",
    "admin.saveConfig": "حفظ الإعدادات",
    "admin.commissionRefund": "العمولة والاسترداد",
    "admin.commissionRate": "نسبة العمولة",
    "admin.commissionNote": "النسبة المقتطعة من كل عملية بيع (0 = بدون عمولة)",
    "admin.refundDays": "مدة الاسترداد (أيام)",
    "admin.trialDays": "مدة التجربة المجانية (أيام)",
    "admin.storageLimits": "حدود التخزين",
    "admin.storageBasic": "تخزين الخطة الأساسية",
    "admin.storagePro": "تخزين الخطة الاحترافية",
    "admin.maxFileSize": "الحد الأقصى لحجم الملف",
    "admin.scheduledJobs": "المهام المجدولة",
    "admin.cronTrialExpiry": "التحقق من انتهاء الفترات التجريبية يومياً",
    "admin.cronSessionReminder": "تذكير بالجلسات القادمة كل ساعة",
    "admin.cronWeeklySettlement": "تسوية المحافظ أسبوعياً",
    "admin.cronReconcileStorage": "مطابقة التخزين أسبوعياً",
    "admin.cronEmailQueue": "معالجة قائمة البريد كل 5 ثوانٍ",
    "admin.active": "نشط",
    "admin.systemInfo": "معلومات النظام",
    "admin.emailSystem": "نظام البريد",
    "admin.paymentProcessor": "معالج الدفع",
    "admin.liveProvider": "مزود البث المباشر",
    "admin.contentProtection": "حماية المحتوى",
    "admin.aiProvider": "مزود الذكاء الاصطناعي",
    "admin.customDomain": "النطاق المخصص",
    "admin.notImplemented": "غير مفعّل بعد",

    // Phase 23 — Custom Domain Safe Fallback
    "customDomain.fallbackToast": "تم فتح الأكاديمية عبر رابط منصة دورة الرئيسي لأن الدومين المخصص غير متاح حالياً.",
    "customDomain.statusSuspended": "موقوف (الباقة)",
    "customDomain.statusRemovalPending": "بانتظار الإزالة",
    "customDomain.suspendedBanner": "الدومين المخصص موقوف لأن باقتك الحالية لا تشمل هذه الميزة. يتم تحويل الطلاب تلقائياً إلى رابط الأكاديمية الأساسي. لإعادة التفعيل، رقّ الباقة إلى Pro.",
    "customDomain.removalPendingBanner": "طلب إزالة هذا الدومين قيد التنفيذ. سيتم الحذف النهائي بعد {days} يوم. يمكنك إلغاء الطلب أو الحذف الفوري.",
    "customDomain.cancelRemoval": "إلغاء طلب الإزالة",
    "customDomain.forceRemoveNow": "إزالة فورية نهائية",
    "customDomain.confirmForceRemove": "هل تريد بالتأكيد الحذف الفوري والنهائي للدومين؟ سيتم كسر جميع الروابط المتداولة فوراً.",
    "customDomain.notMapped.title": "هذا الدومين لم يعد مفعّلاً",
    "customDomain.notMapped.body": "الدومين {host} لم يعد مربوطاً بأي أكاديمية على منصة دورة. سيتم تحويلك تلقائياً إلى الصفحة الرئيسية خلال لحظات.",
    "customDomain.notMapped.cta": "اذهب إلى dwrrah.com الآن",
    "ins.toast.cancel": "إلغاء",
    "customDomain.fallbackToast.domain_removed": "الأكاديمية لم تعد تستخدم هذا الدومين المخصص. تم تحويلك إلى الصفحة الرئيسية لمنصة دورة.",
    "customDomain.fallbackToast.domain_unknown": "الدومين الذي زرته لم يعد مفعّلاً. تم تحويلك إلى الصفحة الرئيسية لمنصة دورة.",

    // Admin tables & actions
    "admin.searchAcademies": "بحث بالاسم أو الرابط...",
    "admin.searchStudents": "بحث بالاسم أو البريد...",
    "admin.searchPayments": "بحث بالطالب أو المنتج...",
    "admin.searchAudit": "بحث بالمنفذ أو السبب...",
    "admin.newAcademy": "أكاديمية جديدة",
    "admin.plan": "الباقة",
    "admin.billingCycle": "دورة الفوترة",
    "admin.action": "الإجراء",
    "admin.enterReason": "يرجى إدخال السبب",
    "admin.writeReason": "اكتب سبب الإجراء...",
    "admin.actionSuccess": "تم تنفيذ الإجراء بنجاح",
    "admin.unauthorized": "غير مصرح",
    "admin.noAccessMsg": "هذا الحساب لا يملك صلاحية الدخول لإدارة المنصة",

    // Admin subscription actions
    "admin.changePlan": "تغيير الباقة",
    "admin.activateSubscription": "تفعيل الاشتراك",
    "admin.suspendSubscription": "تعليق الاشتراك",
    "admin.cancelSubscription": "إلغاء اشتراك",
    "admin.cancelEndPeriod": "إلغاء نهاية الفترة",
    "admin.extendTrial": "تمديد / تعيين تاريخ التجربة",
    "admin.setTrialDate": "تعيين تاريخ تجربة",
    "admin.scheduledPlan": "باقة مجدولة",
    "admin.trial": "تجربة",
    "admin.trialExpiring": "تجربة تنتهي قريباً",
    "admin.trialExpiry": "انتهاء التجربة",
    "admin.trialStart": "بدء تجربة",
    "admin.expired": "انتهت",
    "admin.suspended": "معلّق",
    "admin.cancelled": "ملغي",
    "admin.pastDue": "متأخرة الدفع",
    "admin.start": "بداية",
    "admin.end": "نهاية",
    "admin.daysRemaining": "أيام متبقية",
    "admin.annualRevenue": "الدخل السنوي المتوقع",
    "admin.retentionRate": "معدل الاحتفاظ",
    "admin.conversionRate": "معدل التحويل",
    "admin.churnRate": "معدل الفقد",
    "admin.retention": "احتفاظ",
    "admin.churn": "فقد",

    // Admin plan labels
    "admin.basicMonthly": "أساسي شهري",
    "admin.basicAnnual": "أساسي سنوي",
    "admin.proMonthly": "احترافي شهري",
    "admin.proAnnual": "احترافي سنوي",
    "admin.trialPeriod": "فترة تجريبية",

    // Admin payment types
    "admin.paymentType.purchase": "شراء طالب",
    "admin.paymentType.subscription": "اشتراك",
    "admin.paymentType.deposit": "إيداع",
    "admin.paymentType.refund": "استرجاع",
    "admin.paymentType.payment": "دفع",
    "admin.paymentType.paidStatus": "مدفوع",

    // Admin notifications
    "admin.notif.trialExpiry": "انتهاء تجربة",
    "admin.notif.newRegistration": "تسجيل",
    "admin.notif.planChange": "تغيير باقة",
    "admin.notif.trialExtend": "تمديد تجربة",
    "admin.notif.activate": "تفعيل",
    "admin.notif.suspend": "تعليق",

    // KPI
    "kpi.students": "الطلاب",
    "kpi.enrollments": "التسجيلات",
    "kpi.revenue": "الإيرادات",
    "kpi.completionRate": "معدل الإكمال",
    "kpi.certificates": "الشهادات",
    "kpi.sessions": "الجلسات",
    "kpi.quizAttempts": "محاولات الكويز",
    "kpi.passRate": "معدل النجاح",

    // Lock screen
    "lock.expired.title": "أكاديميتك مقفلة",
    "lock.expired.message": "انتهت فترة اشتراكك. جدد اشتراكك للمتابعة.",
    "lock.expired.detail": "صفحة الهبوط الخاصة بك غير مفعلة حالياً ودخلت وضع الصيانة حتى تجدد اشتراكك.",
    "lock.expired.button": "تجديد الاشتراك",
    "lock.pastDue.message": "تعذّر تحصيل الدفعة ولم يتم التجديد خلال المهلة. جدد اشتراكك للمتابعة.",
    "lock.cancelled.message": "تم إلغاء اشتراكك وانتهت فترة الخدمة. جدد اشتراكك للمتابعة.",
    "lock.suspended.title": "تم تعليق أكاديميتك",
    "lock.suspended.message": "تم تعليق أكاديميتك بواسطة إدارة المنصة.",
    "lock.suspended.detail": "تواصل مع الدعم لمعرفة السبب وإعادة تفعيل أكاديميتك.",
    "lock.suspended.button": "تواصل مع الدعم",
    "lock.academyLabel": "الأكاديمية",

    // Student dashboard
    "stu.welcome": "مرحباً",
    "stu.myCoursesStat": "دوراتي",
    "stu.activeSubscriptionsStat": "اشتراكات نشطة",
    "stu.upcomingSessionsStat": "جلسات قادمة",
    "stu.certificatesStat": "شهادات",
    "stu.nextRenewalLabel": "تجديد الاشتراك القادم",
    "stu.details": "التفاصيل",
    "stu.continueWhere": "أكمل من حيث توقفت",
    "stu.courseLabel": "الدورة",
    "stu.lessonsOf": "من",
    "stu.lesson": "درس",
    "stu.notEnrolledYet": "لم تسجّل في أي دورة بعد",
    "stu.exploreCourses": "استكشف الدورات والمنتجات",
    "stu.discoverMore": "اكتشف المزيد من الأكاديمية",
    "stu.quizLabel": "اختبار",
    // Dashboard v2 — Next Action Hero, Streak, Multi-course
    "stu.nextAction.title": "ما التالي؟",
    "stu.nextAction.liveNow": "جلسة مباشرة الآن — انضم",
    "stu.nextAction.upcomingSession": "جلسة قادمة",
    "stu.nextAction.joinSession": "انضم للجلسة",
    "stu.nextAction.viewSession": "عرض الجلسة",
    "stu.nextAction.continueLesson": "أكمل من حيث توقفت",
    "stu.nextAction.continueBtn": "تابع التعلم",
    "stu.nextAction.startCourse": "ابدأ دورتك الأولى",
    "stu.nextAction.exploreBtn": "استكشف الدورات",
    "stu.nextAction.almostDone": "أنت قريب من الإنجاز!",
    "stu.nextAction.lessonsLeft": "دروس متبقية فقط",
    "stu.streak.title": "نشاطك هذا الأسبوع",
    "stu.streak.daysActive": "أيام نشاط",
    "stu.streak.lessonsThisWeek": "درس هذا الأسبوع",
    "stu.streak.noActivity": "لم تتعلم هذا الأسبوع بعد — ابدأ اليوم!",
    "stu.streak.dayShort.0": "أحد",
    "stu.streak.dayShort.1": "اثن",
    "stu.streak.dayShort.2": "ثلا",
    "stu.streak.dayShort.3": "أرب",
    "stu.streak.dayShort.4": "خمي",
    "stu.streak.dayShort.5": "جمع",
    "stu.streak.dayShort.6": "سبت",
    "stu.continueAll": "كل دوراتي",
    // Progress ring + nudge + info-strip
    "stu.progressRing.title": "تقدمك الإجمالي",
    "stu.progressRing.subtitle": "عبر كل دوراتك",
    "stu.progressRing.empty": "ابدأ أول دورة لتظهر هنا",
    "stu.nudge.almostCert": "أوشكت على شهادة جديدة!",
    "stu.nudge.almostCertDesc": "تبقّى لك {n} درس فقط في {course}",
    "stu.nudge.continueNow": "أكمل الآن",
    "stu.info.courses": "دورات",
    "stu.info.subs": "اشتراكات",
    "stu.info.sessions": "جلسات",
    "stu.info.certs": "شهادات",
    "stu.analytics.toggle": "إحصاءات وتحليلات تفصيلية",

    // Student profile
    "stu.profileTitle": "الملف الشخصي",
    "stu.displayName": "الاسم المعروض",
    "stu.phone": "رقم الهاتف",
    "stu.bioLabel": "نبذة عنك",
    "stu.bioPlaceholder": "أخبرنا عنك...",
    "stu.imageUpdated": "تم تحديث الصورة",
    "stu.imageUploadFail": "فشل رفع الصورة",
    "stu.saveChanges": "حفظ التغييرات",

    // Student settings
    "stu.settingsTitle": "الإعدادات",
    "stu.settingsSubtitle": "إعدادات حسابك والتكاملات",
    "stu.personalInfoTitle": "البيانات الشخصية",
    "stu.notificationsTitle": "تفضيلات الإشعارات",
    "stu.notificationsSubtitle": "تحكم بإشعارات النظام والبريد لكل نوع",
    "stu.googleCalSync": "مزامنة جلساتك المحجوزة تلقائياً مع تقويم Google الخاص بك",
    "stu.featureNotAvailable": "هذه الميزة غير متاحة حالياً في هذه الأكاديمية",
    "stu.connected": "مربوط",
    "stu.disconnectGoogle": "فصل الربط",
    "stu.reconnectGoogle": "إعادة الربط",
    "stu.connectGoogle": "ربط Google Calendar",
    "stu.expiredConnection": "انتهت صلاحية الربط. يرجى إعادة الربط.",
    "stu.calendarSyncTitle": "كيف تعمل المزامنة؟",
    "stu.calendarSyncItem1": "عند حجز جلسة، يُضاف حدث تلقائياً في تقويمك",
    "stu.calendarSyncItem2": "تنبيه تلقائي قبل 30 دقيقة من الجلسة",
    "stu.calendarSyncItem3": "رابط الانضمام مضمّن في الحدث",
    "stu.googleCalConnected": "تم ربط Google Calendar بنجاح ✅",
    "stu.googleCalFail": "فشل ربط Google Calendar",
    "stu.googleCalDisconnected": "تم فصل Google Calendar",

    // Pro feature gating (student side)
    "stu.proFeature.locked": "هذه الميزة تحتاج إتاحة من المدرب",
    "stu.proFeature.requestAccess": "اطلب من المدرب الإتاحة",
    "stu.proFeature.requestSent": "تم إرسال طلبك للمدرب",
    "stu.proFeature.requestSuccess": "تم إرسال طلبك للمدرب بنجاح",
    "stu.proFeature.requestAlready": "سبق وأرسلت هذا الطلب",
    "stu.proFeature.requestError": "تعذر إرسال الطلب، حاول لاحقاً",

    // Student sessions
    "stu.mySessionsTitle": "جلساتي",
    "stu.completeRegFirst": "يرجى إكمال تسجيلك أولاً",
    "stu.linkCalendarBanner": "📅 اربط تقويمك لمزامنة مواعيد جلساتك تلقائياً",
    "stu.linkCalendar": "ربط التقويم",
    "stu.tabUpcoming": "القادمة",
    "stu.tabPast": "السابقة",
    "stu.tabInvited": "دعوات",
    "stu.noUpcomingSessions": "لا توجد جلسات قادمة",
    "stu.noPastSessions": "لا توجد جلسات سابقة",
    "stu.noInvitesNow": "لا توجد دعوات حالياً",
    "stu.browseSessionsHint": "تصفح الجلسات المتاحة واحجز مكانك",
    "stu.invitedSessionsHint": "هذه جلسات تمت دعوتك إليها — اضغط لعرض التفاصيل والحجز",
    // Unified sessions list (v1.07.14)
    "stu.sessions.searchPlaceholder": "ابحث باسم الجلسة",
    "stu.sessions.filterAll": "الكل",
    "stu.sessions.viewActive": "النشطة",
    "stu.sessions.filterUpcoming": "قادمة",
    "stu.sessions.filterLive": "مباشرة الآن",
    "stu.sessions.filterAwaiting": "بانتظار جدولة",
    "stu.sessions.filterPast": "منتهية",
    "stu.sessions.filterCancelled": "ملغاة",
    "stu.sessions.filterInvites": "دعوات",
    "stu.sessions.sortLabel": "الترتيب",
    "stu.sessions.sortNearest": "الأقرب موعدًا",
    "stu.sessions.sortFurthest": "الأبعد موعدًا",
    "stu.sessions.sortNewest": "الأحدث إنشاء",
    "stu.sessions.allEmpty": "لا توجد جلسات تطابق التصفية الحالية",
    "stu.sessions.awaitingTag": "بانتظار جدولة",
    "stu.sessions.scheduleNow": "جدولة الآن",
    "stu.sessions.invited": "مدعو",

    // Course viewer
    "stu.completeLesson": "إكمال الدرس",
    "stu.completed": "مكتمل",
    "stu.lessonCompletedToast": "تم إكمال الدرس ✓",
    "stu.prevLesson": "السابق",
    "stu.nextLesson": "التالي",
    "stu.noLessons": "لا توجد دروس في هذه الدورة",
    "stu.previewMode": "وضع المحاكاة — الاختبار يعمل بالكامل، لا يتم حفظ النتائج",
    "stu.previewLabel": "وضع المعاينة",
    "stu.previewOwner": "المالك",
    "stu.previewManager": "المدير",
    "stu.previewEditor": "محرر المحتوى",
    "stu.limitedFreeAccess": "وصول مجاني محدود",
    "stu.buyFull": "اشترِ الكامل",
    "stu.buyFullAccess": "اشترِ للوصول الكامل",
    "stu.lessonNotFree": "هذا الدرس غير متاح في النسخة المجانية",
    "stu.lessonNeedsSub": "هذا الدرس يتطلب اشتراك",
    "stu.lessonNotAvailable": "هذا الدرس غير متاح بعد",
    "stu.goToQuiz": "الذهاب للاختبار",
    "stu.cannotCompleteYet": "لا يمكن الإكمال بعد",
    "stu.waitSessionEnd": "يجب انتظار انتهاء موعد الجلسة المباشرة",
    "stu.mustAttendSession": "يجب حضور الجلسة المباشرة أولاً",
    "stu.congrats": "🎉 مبروك!",
    "stu.lessonDoneTitle": "تم إكمال الدرس",
    "stu.nextUp": "التالي:",
    "stu.continueToNext": "المتابعة للدرس التالي",
    "stu.moduleDoneTitle": "تم إكمال الوحدة",
    "stu.continueToNextModule": "المتابعة للوحدة التالية",
    "stu.contentDoneTitle": "أنهيت محتوى الدورة",
    "stu.finalExamRequired": "باقي الاختبار النهائي لإكمال الدورة",
    "stu.startFinalExam": "ابدأ الاختبار النهائي",
    "stu.retryFinalExam": "أعد الاختبار النهائي",
    "stu.courseCompletedTitle": "🎉 مبروك! أكملت الدورة",
    "stu.courseCompletedDesc": "أنجزت كل متطلبات الدورة بنجاح. استمر في التعلّم!",
    "stu.viewCertificate": "عرض الشهادة",
    "stu.replayCourse": "مراجعة الدورة من جديد",
    "stu.backToMyCourses": "العودة لدوراتي",
    "stu.finishCourse": "إنهاء الدورة",
    "stu.simCertNotAvailable": "الشهادة غير متاحة في وضع المحاكاة",
    "stu.simCertPreviewOnly": "هذه معاينة فقط — لن تُحفظ شهادة فعلية في وضع المحاكاة.",
    "stu.simCertPreviewNote": "معاينة وهمية بدون حفظ — هذه ليست شهادة قابلة للمشاركة.",
    "stu.previewCertificate": "معاينة الشهادة",
    "stu.startQuizCta": "ابدأ الاختبار",
    "stu.finalExamSection": "الاختبار النهائي",
    "stu.moduleQuizLabel": "اختبار الوحدة",
    "stu.continueToFinish": "متابعة لإنهاء الدورة",
    "stu.backToCourse": "العودة للدورة",
    "stu.completionNotYetTitle": "الدورة لم تكتمل بعد",
    "stu.completionScoreLabel": "النتيجة",
    "stu.completionPassingLabel": "النصاب المطلوب",
    "stu.completionFinalExamScore": "نتيجة الاختبار النهائي",
    "stu.completionWeightedScore": "النتيجة الموزونة",
    "stu.completionUnmetLessons": "لا تزال هناك دروس مطلوبة لم تكتمل بعد.",
    "stu.completionUnmetFinalExamPending": "أكملت كل المحتوى — يتبقى اجتياز الاختبار النهائي لإكمال الدورة.",
    "stu.completionUnmetFinalExamFailed": "نتيجتك في الاختبار النهائي أقل من النصاب المطلوب — يلزم إعادة المحاولة.",
    "stu.completionUnmetWeighted": "نتيجتك المرجّحة (الاختبارات + الاختبار النهائي) أقل من النصاب المطلوب.",
    "stu.completionUnmetFinalPlusQuizzes": "يلزم إكمال الاختبار النهائي والاختبارات لاحتساب النتيجة المطلوبة للإكمال.",
    "stu.completionUnmetUnknown": "لم تتحقق شروط إكمال الدورة المُعَدّة من قِبل المدرّب بعد.",
    "stu.completionUnmetRequiredQuiz": "يلزم اجتياز اختبار مطلوب لإكمال الدورة.",

    // Course detail
    "stu.courseNotFound": "الدورة غير موجودة",
    "stu.courseContent": "محتوى الدورة",
    "stu.previewCourse": "معاينة الدورة",
    "stu.internalPreview": "معاينة داخلية",
    "stu.previewAs": "وضع المعاينة — أنت ترى هذه الصفحة كـ",
    "stu.ownerRole": "مالك",
    "stu.managerRole": "مدير",
    "stu.editorRole": "محرر محتوى",
    "stu.freePreview": "استعراض مجاني",
    "stu.editLabel": "تعديل",

    // Session room
    "stu.backLabel": "العودة",
    "stu.previewNoBooKing": "وضع المعاينة — لن يتم تسجيل حجز أو حضور",
    "stu.sessionNotStarted": "الجلسة لم تبدأ بعد",
    "stu.sessionLiveNow": "الجلسة مباشرة الآن!",
    "stu.sessionFull": "الجلسة ممتلئة",
    "stu.bookAndJoinBtn": "احجز وانضم",
    "stu.sessionEnded": "انتهت الجلسة",
    "stu.watchRecording": "مشاهدة التسجيل",
    "stu.sessionCancelled": "الجلسة ملغاة",
    "stu.joinLinkOpensIn": "رابط الانضمام يفتح بعد",
    "stu.retryLabel": "إعادة المحاولة",
    "stu.bookingsCount": "حجز",
    "stu.internalPreviewBadge": "معاينة داخلية",
    "stu.freeSession": "مجانية",
    "stu.hasAccess": "لديك وصول",
    "stu.addToCalendar": "أضف للتقويم",
    "stu.calendarFileHint": "تحميل ملف تقويم (ICS) لإضافته لـ Google أو Apple أو Outlook",
    "stu.registeredInSession": "أنت مسجل في هذه الجلسة",
    "stu.cancelBooking": "إلغاء الحجز",
    "stu.sessionFullNoBook": "الجلسة ممتلئة — لا يمكن الحجز",
    "stu.bookYourSpot": "احجز مكانك",
    "stu.directBookNotAvail": "الحجز المباشر غير متاح لهذه الجلسة",

    // Course catalog
    "stu.coursesTitle": "الدورات",
    "stu.coursesAvailable": "دورة متاحة",
    "stu.searchCourse": "ابحث عن دورة...",
    "stu.comingSoonCourses": "مدربك يعمل على تجهيز دورات مميزة. ترقّب!",

    // Products catalog
    "stu.productsTitle": "المنتجات",
    "stu.searchProduct": "ابحث عن منتج...",
    "stu.comingSoonProducts": "مدربك يعمل على تجهيز منتجات مميزة لك. ترقّب!",
    "stu.featured": "مميز",
    "stu.perMonth": "/شهر",
    "stu.per3Months": "/3 أشهر",
    "stu.perYear": "/سنة",

    // Sessions catalog
    "stu.availableSessions": "الجلسات المتاحة",
    "stu.noScheduledSessions": "لا توجد جلسات مجدولة حالياً. مدربك سيضيف جلسات قريباً!",
    "stu.privateLabel": "خاصة",
    "stu.groupLabel": "جماعية",
    "stu.openLabel": "مفتوحة",
    "stu.fullLabel": "ممتلئة",
    "stu.detailsLabel": "التفاصيل",
    "stu.viewLabel": "عرض",

    // Email preferences
    "stu.emailPrefsTitle": "تفضيلات البريد",
    "stu.emailPrefsSubtitle": "تحكم في الإيميلات التلقائية التي تستلمها",
    "stu.emailWelcome": "ترحيب بطالب جديد",
    "stu.emailWelcomeDesc": "عند التسجيل في الأكاديمية",
    "stu.emailEnrollConfirm": "تأكيد التسجيل",
    "stu.emailEnrollConfirmDesc": "عند الاشتراك في دورة",
    "stu.emailCourseComplete": "إكمال دورة",
    "stu.emailCourseCompleteDesc": "عند إتمام دورة",
    "stu.emailCertIssued": "إصدار شهادة",
    "stu.emailCertIssuedDesc": "عند صدور شهادة",
    "stu.emailPaymentReceipt": "إيصال الدفع",
    "stu.emailPaymentReceiptDesc": "بعد كل عملية دفع",
    "stu.emailSessionReminder": "تذكير بجلسة",
    "stu.emailSessionReminderDesc": "قبل 24 ساعة من الجلسة",
    "stu.emailInactivity": "تذكير الخمول",
    "stu.emailInactivityDesc": "عند عدم النشاط",
    "stu.emailWeeklyDigest": "الملخص الأسبوعي",
    "stu.emailWeeklyDigestDesc": "ملخص أسبوعي للتقدم",
    "stu.saveFail": "فشل الحفظ",

    // Notification history
    "stu.notifHistory": "سجل الإشعارات",
    "stu.markAllRead": "قراءة الكل",
    "stu.allTypes": "جميع الأنواع",
    "stu.noNotifications": "لا توجد إشعارات",
    "stu.now": "الآن",
    "stu.minutesAgo": "دقيقة",
    "stu.hoursAgo": "ساعة",
    "stu.daysAgo": "يوم",
    "stu.agoPrefix": "منذ",
    "stu.notifSessionReminder": "تذكير جلسة",
    "stu.notifNewMessage": "رسالة جديدة",
    "stu.notifEnrollment": "تسجيل",
    "stu.notifCertificate": "شهادة",
    "stu.notifPurchaseConfirmed": "تأكيد شراء",
    "stu.notifPaymentFailed": "فشل دفع",
    "stu.notifSessionBooked": "حجز جلسة",
    "stu.notifSessionCancelled": "إلغاء جلسة",
    "stu.notifCourseCompleted": "إكمال دورة",
    "stu.notifDripUnlocked": "محتوى جديد",
    "stu.notifProductGrant": "منحة",

    // Free access
    "stu.freeAccessTitle": "الوصول المجاني",
    "stu.noFreeAccess": "لا يوجد لديك وصول مجاني حالياً",
    "stu.browseProducts": "تصفح المنتجات",
    "stu.activeFreeAccess": "وصول مجاني نشط",
    "stu.limitedAccess": "وصول محدود",
    "stu.startedOn": "بدأ في",
    "stu.upgradedToPaid": "تم الترقية إلى مدفوع",
    "stu.freePartInProgress": "الجزء المجاني: قيد التقدّم",
    "stu.freePartCompleted": "أكملت الجزء المجاني — أكمل الشراء لمتابعة بقية المحتوى",
    "stu.payToContinue": "ادفع للمواصلة",

    // Invites & Grants
    "stu.grantsTitle": "المنح والدعوات",
    "stu.grantsSubtitle": "المنتجات الممنوحة لك ودعوات الشراء",
    "stu.freeGrants": "المنح المجانية",
    "stu.purchaseInvites": "دعوات الشراء",
    "stu.noGrants": "لا توجد منح حالياً",
    "stu.noInvites": "لا توجد دعوات حالياً",
    "stu.freeGrant": "هدية مجانية",
    "stu.viewProduct": "عرض المنتج",
    "stu.expired": "منتهية",
    "stu.until": "حتى",

    // Academy home / custom pages
    "stu.comingSoonHome": "مدربك يعمل على تجهيز تجربة مميزة لك 🎓 عُد قريباً لاكتشاف المحتوى!",
    "stu.pageNotFound": "الصفحة غير موجودة",
    "stu.comingSoonPage": "مدربك يعمل على تجهيز محتوى هذه الصفحة. عُد قريباً!",
    "stu.academyLabel": "الأكاديمية",

    // Student misc (remaining components)
    "stu.myCoursesPg": "دوراتي",
    "stu.notEnrolledCourse": "لم تسجّل في أي دورة بعد",
    "stu.browseCoursesCta": "تصفح الدورات",
    "stu.lessonUnit": "درس",
    "stu.certificatesPg": "شهاداتي",
    "stu.noCertificates": "لا توجد شهادات بعد",
    "stu.completeCourseForCert": "أكمل دورة للحصول على شهادتك",
    "stu.issuedOn": "صدرت في",
    "stu.certNumber": "رقم:",
    "stu.downloadCert": "تحميل الشهادة",
    "stu.viewCert": "عرض الشهادة",
    "stu.messagesPg": "الرسائل",
    "stu.completeRegistration": "يرجى إكمال تسجيلك أولاً",
    "stu.startConversation": "ابدأ محادثة مع المدرب",
    "stu.writeMessage": "اكتب رسالتك...",
    "stu.productNotFound": "المنتج غير موجود",
    "stu.backToCourse": "العودة للدورة",
    "stu.reviewQuiz": "استعراض الاختبار",
    "stu.previewQuiz": "استعراض الاختبار",
    "stu.quizNotFound": "الاختبار غير موجود",
    "stu.reviewAnswers": "مراجعة الإجابات",
    "stu.questionUnit": "سؤال",
    "stu.passingGrade": "درجة النجاح:",
    "stu.attemptsUsed": "المحاولات:",
    "stu.startQuiz": "ابدأ الاختبار",
    "stu.retryQuiz": "إعادة المحاولة",
    "stu.skipOptionalQuiz": "تخطّي الاختبار",
    "stu.optionalQuizSuggestionTitle": "اختبار اختياري متاح",
    "stu.optionalQuizSuggestionDesc": "هذا الاختبار غير إلزامي. يمكنك إجراؤه أو تخطيه والمتابعة.",
    "stu.takeQuizNow": "ابدأ الاختبار",
    "stu.skipAndContinue": "تخطّي والمتابعة",
    "stu.answerRequiredFirst": "أجب على السؤال أولاً",
    "stu.requiredQuizMissingDesc": "هذا الاختبار إلزامي ويجب الإجابة على جميع الأسئلة قبل التسليم.",
    "stu.previewNoSubmit": "وضع المعاينة — لا يمكن إرسال الإجابات",
    "stu.previewBrowseOnly": "وضع المحاكاة — يمكنك تجربة الاختبار كاملاً، النتائج لا تُحفظ",
    "stu.previewSubmitOff": "وضع المحاكاة — يمكن الإرسال، النتيجة محلية ولا تُحفظ",
    "stu.previewSubmit": "تسليم تجريبي",
    "stu.previewResultBanner": "نتيجة معاينة — لن يتم حفظها",
    "stu.previewBrowse": "وضع المحاكاة — جرّب الاختبار بالكامل، النتائج لا تُحفظ في قاعدة البيانات",
    "stu.questionXofY": "السؤال {x} من {y}",
    "stu.answeredCount": "{x} من {y} مجاب",
    "stu.questionsList": "قائمة الأسئلة",
    "stu.previousQuestion": "السابق",
    "stu.nextQuestion": "التالي",
    "stu.unansweredWarning": "لديك {n} سؤالاً لم تُجَب. هل تريد التسليم؟",
    "stu.requiredQuiz": "إلزامي",
    "stu.answerRequiredFirst": "أجب على السؤال أولاً",
    "stu.requiredQuizMissingDesc": "هذا الاختبار إلزامي ويجب الإجابة على جميع الأسئلة قبل التسليم.",
    "stu.finalExamLabel": "اختبار نهائي",
    "stu.questionStatusAnswered": "مجاب",
    "stu.questionStatusEmpty": "فارغ",
    "stu.questionStatusCurrent": "حالي",
    "stu.actionNextLesson": "الدرس التالي",
    "stu.actionNextModule": "الوحدة التالية",
    "stu.actionBackToCourseShort": "العودة للدورة",
    "stu.actionEndCourse": "إنهاء الدورة",
    "stu.actionContinueAnyway": "متابعة الدورة",
    "stu.closePreview": "إغلاق المعاينة",
    "stu.submitQuiz": "تسليم الاختبار",
    "stu.quizPassed": "أحسنت! اجتزت الاختبار",
    "stu.quizFailed": "لم تجتز الاختبار",
    "stu.scoreLabel": "الدرجة",
    "stu.pointsLabel": "النقاط",
    "stu.exhaustedBlock": "استنفدت جميع المحاولات. تواصل مع المدرب.",
    "stu.exhaustedRequest": "تم إرسال طلب للمدرب لمنحك محاولة إضافية.",
    "stu.exhaustedContinue": "يمكنك المتابعة رغم عدم اجتياز الاختبار.",
    "stu.continueBtn": "متابعة",
    "stu.writeAnswer": "اكتب الإجابة هنا...",
    "stu.correctAnswer": "الإجابة الصحيحة:",
    "stu.loginToJoin": "سجّل دخولك للانضمام لهذه الأكاديمية",
    "stu.loginBtn": "تسجيل الدخول",
    "stu.registrationClosed": "التسجيل مغلق حالياً",
    "stu.registrationClosedDesc": "هذه الأكاديمية لا تقبل تسجيلات جديدة في الوقت الحالي.",
    "stu.joinDesc": "انضم لهذه الأكاديمية للوصول إلى المحتوى والدورات",
    "stu.joinBtn": "انضم لهذه الأكاديمية",
    "stu.maintenanceTitle": "الأكاديمية متوقفة مؤقتاً",
    "stu.maintenanceDesc": "هذه الأكاديمية غير متاحة حالياً. يرجى التواصل مع المدرب للمزيد من المعلومات.",
    "stu.checkoutSuccess": "تمت عملية الشراء بنجاح! 🎉",
    "stu.checkoutSuccessDesc": "تم تسجيلك في المنتج وأصبح بإمكانك الوصول للمحتوى الآن.",
    "stu.checkoutCancelled": "تم إلغاء عملية الدفع",
    "stu.checkoutCancelledDesc": "يمكنك المحاولة مرة أخرى في أي وقت.",
    "stu.comingSoonContent": "المحتوى قيد التحضير ✨",
    "stu.comingSoonDefault": "مدربك يعمل على تجهيز محتوى رائع لك. عُد قريباً!",
    "stu.loginForFree": "سجّل دخولك لتبدأ النسخة المجانية",
    "stu.startFreeVersion": "ابدأ النسخة المجانية",
    "stu.alreadyHaveFree": "لديك وصول مجاني لهذا المنتج بالفعل",
    "stu.freeAccessActivated": "تم تفعيل الوصول المجاني بنجاح! 🎉",
    "stu.freeAccessError": "حدث خطأ أثناء تفعيل الوصول المجاني",
    "stu.contentLocked": "هذا المحتوى غير متاح حالياً",
    "stu.contentRequiresSub": "هذا المحتوى يتطلب اشتراك",
    "stu.viewProductBtn": "عرض المنتج",
    "stu.orChooseAlt": "أو اختر من الخيارات الأخرى",
    "stu.loginFirst": "سجّل دخولك أولاً للمتابعة",
    "stu.productContents": "محتويات المنتج",
    "stu.freeAccessAvail": "وصول مجاني متاح",
    "stu.liveSessions": "الجلسات المباشرة",
    "stu.includedSessions": "الجلسات المباشرة المشمولة",
    "stu.noLinkedSessions": "لا توجد جلسات مباشرة مرتبطة بهذه الدورة حالياً",
    "stu.noIncludedSessions": "لا توجد جلسات مباشرة مشمولة حالياً",
    "stu.joinNow": "انضم الآن",
    "stu.joinLive": "انضم",
    "stu.recordingLabel": "التسجيل",
    "stu.recordingTitle": "تسجيل:",
    "stu.noRecordingAccess": "ليس لديك صلاحية مشاهدة هذا التسجيل",
    "stu.noRecordingUploaded": "لم يتم رفع التسجيل بعد",
    "stu.aboutCourse": "عن الدورة",
    "stu.courseContentLabel": "محتوى الدورة",
    "stu.startCourseBtn": "ابدأ الدورة",
    "stu.moduleUnit": "وحدة",
    "stu.hourUnit": "ساعة",
    "stu.andUnit": "و",
    "stu.contentTab": "المحتوى",
    "stu.discussionTab": "النقاش",
    "stu.lessonsCompleted": "درس مكتمل",
    "stu.freeLabel": "مجاني",
    "stu.instructorBadge": "مدرّب",
    "stu.replyBtn": "رد",
    "stu.pinBtn": "تثبيت",
    "stu.unpinBtn": "إلغاء التثبيت",
    "stu.showBtn": "إظهار",
    "stu.hideBtn": "إخفاء",
    "stu.commentsTitle": "التعليقات والأسئلة",
    "stu.replyTo": "رد على",
    "stu.writeComment": "اكتب تعليقك أو سؤالك...",
    "stu.noComments": "لا توجد تعليقات بعد. كن أول من يعلق!",
    "stu.unknownAuthor": "مجهول",
    "stu.startDiscussion": "ابدأ النقاش مع زملائك",
    "stu.myAnalytics": "تحليلاتي",
    "stu.preparingCharts": "جاري تجهيز الرسوم البيانية...",
    "stu.chartsAppearAfter": "ستظهر الرسوم البيانية هنا تلقائياً بعد بدء الدراسة أو حل الاختبارات أو حجز الجلسات.",
    "stu.courseProgress": "تقدم الدورات",
    "stu.weeklyActivity": "نشاط التعلم الأسبوعي",
    "stu.lessonsTab": "دروس",
    "stu.timeTab": "وقت (دقيقة)",
    "stu.quizPerformance": "أداء الكويزات",
    "stu.sessionAttendance": "حضور الجلسات",
    "stu.passRateStat": "نسبة النجاح",
    "stu.attemptUnit": "محاولة",
    "stu.avgTimePerQuiz": "متوسط الوقت لكل اختبار",
    "stu.allCoursesFilter": "كل الدورات",
    "stu.noCoursesStat": "بلا دورات",
    "stu.noQuizzesSolved": "لم يتم حل أي اختبار بعد.",
    "stu.noSessionBooked": "لم يتم حجز أي جلسة حتى الآن.",
    "stu.courseProgressHint": "ستظهر نسب التقدم هنا بعد التسجيل في أول دورة.",
    "stu.weeklyActivityHint": "سيظهر نشاطك اليومي هنا بعد إكمال الدروس.",
    "stu.milestoneTimeline": "المسار الزمني",
    "stu.courseEnrollment": "تسجيل في دورة",
    "stu.courseCompletion": "إكمال دورة",
    "stu.certIssuedMilestone": "شهادة صادرة",
    "stu.statusScheduled": "مجدولة",
    "stu.statusLive": "🔴 مباشرة الآن",
    "stu.statusCompleted": "منتهية",
    "stu.statusCancelled": "ملغاة",
    "stu.sessionPrivate": "خاصة",
    "stu.sessionGroup": "جماعية",
    "stu.notifTitle": "الإشعارات",
    "stu.unreadCount": "غير مقروء",
    "stu.viewAllNotifs": "عرض جميع الإشعارات",
    "stu.noNotifsBell": "لا توجد إشعارات",
    "stu.unsubProcessing": "جارٍ المعالجة...",
    "stu.unsubTitle": "إلغاء الاشتراك في الإيميلات",
    "stu.unsubConfirmQ": "هل تريد إلغاء استلام الإيميلات التلقائية؟",
    "stu.unsubConfirmBtn": "تأكيد إلغاء الاشتراك",
    "stu.unsubDone": "تم إلغاء الاشتراك",
    "stu.unsubDoneMsg": "لن تتلقى إيميلات تلقائية بعد الآن.",
    "stu.unsubAlready": "تم الإلغاء مسبقاً",
    "stu.unsubAlreadyMsg": "بريدك الإلكتروني غير مشترك بالفعل.",
    "stu.unsubError": "حدث خطأ",
    "stu.unsubErrorMsg": "الرابط غير صالح أو منتهي الصلاحية.",
    "stu.unsubProcessing2": "جارٍ إلغاء الاشتراك...",
    "stu.unsubDone2": "تم إلغاء الاشتراك",
    "stu.unsubDoneMsg2": "لن تتلقى إيميلات تلقائية بعد الآن. يمكنك إعادة الاشتراك من إعدادات حسابك.",
    "stu.unsubFail": "حدث خطأ",
    "stu.unsubFailMsg": "لم نتمكن من إلغاء الاشتراك. يرجى المحاولة مرة أخرى.",
    "stu.productFree": "مجاني",
    "stu.discountLabel": "خصم",
    "stu.couponCode": "كود الخصم",
    "stu.hasProductAccess": "لديك وصول لهذا المنتج",
    "stu.loginForPurchase": "سجّل دخولك",
    "stu.loginForFreeProduct": "للحصول عليه مجاناً",
    "stu.loginForBuy": "للشراء",
    "stu.getItFree": "احصل عليه مجاناً",
    "stu.subscribeNow": "اشترك الآن",
    "stu.refundPolicy": "سياسة الاسترجاع:",
    "stu.refund7d": "7 أيام",
    "stu.refund14d": "14 يوم",
    "stu.refund30d": "30 يوم",
    "stu.productCourse": "دورة",
    "stu.productBundle": "باقة",
    "stu.productMembership": "عضوية",
    "stu.productSessionPkg": "حزمة جلسات",
    "stu.productGeneric": "منتج",
    "stu.statusAttended": "حضر",
    "stu.statusBooked": "محجوز",
    "stu.statusNoShow": "لم يحضر",
    "stu.noSessions": "لا توجد جلسات",
    "stu.lessonsCompletedChartLabel": "دروس مكتملة",
    "stu.minutesLabel": "دقائق",
    "stu.progressLabel": "التقدم",
    "stu.averageLabel": "المتوسط",
    "stu.milestone25": "أحسنت! أنجزت ربع الدورة",
    "stu.milestone50": "رائع! أنجزت نصف الدورة",
    "stu.milestone75": "ممتاز! بقي القليل لإتمام الدورة",
    "stu.milestone100": "مبروك! أنهيت الدورة بالكامل",
    "stu.milestoneComplete": "أنهيت",
    "stu.milestoneCheckCert": "تحقق من شهادتك!",

    // Academy-branded auth + admin-blocked screen
    "academyLogin.subtitle": "تسجيل الدخول إلى الأكاديمية",
    "academyLogin.createAccount": "أنشئ حساباً جديداً",
    "academySignup.subtitle": "إنشاء حساب جديد في الأكاديمية",
    "academyAuth.emailTab": "البريد الإلكتروني",
    "academyAuth.googleTab": "Google",
    "academyAuth.signupCta": "إنشاء الحساب",
    "academyAuth.loginCta": "تسجيل الدخول",
    "academyAuth.phoneOptional": "رقم الجوال (اختياري)",
    "academyAuth.existingUserHint": "إذا كان لديك حساب سابقاً، سجّل الدخول بنفس البريد للانضمام لهذه الأكاديمية.",
    "academyAuth.goToLogin": "الذهاب لتسجيل الدخول",
    "academyAuth.loginGenericError": "بيانات الدخول غير صحيحة",
    "academyAuth.welcomeBack": "أهلاً بعودتك",
    "academyAuth.existingAccountTitle": "لديك حساب سابق في هذه الأكاديمية",
    "academyAuth.existingAccountBody": "سجّل الدخول بنفس البريد وكلمة المرور.",
    "academyAuth.signupGenericError": "تعذّر إنشاء الحساب الآن. حاول مرة أخرى لاحقاً.",
    "academyAuth.forgotPasswordLink": "نسيت كلمة المرور؟",
    "academyAuth.recoveryTitle": "استعادة كلمة المرور",
    "academyAuth.recoveryEmailHint": "أدخل بريدك المسجّل في هذه الأكاديمية وسنرسل لك رمز تحقق من 8 خانات.",
    "academyAuth.recoverySendOtp": "إرسال رمز التحقق",
    "academyAuth.recoveryBackToLogin": "العودة لتسجيل الدخول",
    "academyAuth.recoveryChangeEmail": "تغيير البريد الإلكتروني",
    "academyAuth.recoveryNewPasswordTitle": "كلمة مرور جديدة",
    "academyAuth.recoveryNewPasswordHint": "اختر كلمة مرور جديدة لاستخدامها في هذه الأكاديمية.",
    "academyAuth.recoveryDoneTitle": "تم تحديث كلمة المرور",
    "academyAuth.recoveryDoneBody": "يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.",
    "adminBlocked.title": "حسابك مدير منصة",
    "adminBlocked.body": "هذه البوابة مخصصة للمدربين. الرجاء استخدام بوابة الإدارة للدخول.",
    "adminBlocked.goAdmin": "اذهب إلى بوابة الإدارة",
    "adminBlocked.tryAgain": "تسجيل دخول بحساب آخر",
    "adminHandoff.noticeTitle": "تم تحويلك إلى بوابة الإدارة",
    "adminHandoff.noticeBody": "حسابك مدير منصة — يتم تحويلك تلقائياً.",

    // Student components
    "stu.loginToJoin": "سجّل دخولك للانضمام لهذه الأكاديمية",
    "stu.registrationClosed": "التسجيل مغلق حالياً",
    "stu.registrationClosedDesc": "هذه الأكاديمية لا تقبل تسجيلات جديدة في الوقت الحالي.",
    "stu.joinAcademyAccess": "انضم لهذه الأكاديمية للوصول إلى المحتوى والدورات",
    "stu.joinThisAcademy": "انضم لهذه الأكاديمية",
    "stu.notMemberHere": "حسابك الحالي ليس عضواً في هذه الأكاديمية",
    "stu.useDifferentAccount": "استخدم حساباً آخر",
    "stu.createSeparateAccount": "أنشئ حساب طالب مستقل لهذه الأكاديمية",
    "stu.blockedOwner": "أنت مالك هذه الأكاديمية",
    "stu.blockedStaff": "أنت من فريق هذه الأكاديمية",
    "stu.blockedDesc": "لا يمكنك التسجيل كطالب في أكاديمية تملكها أو تعمل فيها بنفس الحساب. استخدم وضع «افتح كطالب» للاختبار.",
    "stu.ownerSelfTitle": "أنت مالك هذه الأكاديمية",
    "stu.staffSelfTitle": "أنت عضو فريق في هذه الأكاديمية",
    "stu.ownerSelfDesc": "لا يمكنك الانضمام كطالب داخل أكاديميتك بنفس الحساب. استخدم «افتح كطالب» لمعاينة تجربة الطالب، أو ارجع للوحة المدرّب.",
    "stu.openAsStudent": "افتح كطالب (محاكاة)",
    "stu.goToInstructor": "اذهب للوحة المدرّب",
   "stu.accountSuspended": "تم تعليق حسابك",
   "stu.accountSuspendedDesc": "تم تعليق وصولك إلى هذه الأكاديمية مؤقتاً. سجلاتك (الدورات، المدفوعات، الشهادات) محفوظة.",
   "stu.accountBanned": "تم حظر حسابك",
   "stu.accountBannedDesc": "تم حظر حسابك من هذه الأكاديمية. سجلاتك السابقة محفوظة لكنك لا تستطيع الوصول إلى المحتوى.",
   "stu.contactToReinstate": "للاستفسار أو طلب إعادة التفعيل، تواصل عبر:",
   "stu.orPlatformSupport": "أو دعم المنصة:",
   "stu.featureUnavailable": "هذه الميزة غير متاحة حالياً",
   "stu.featureUnavailableDesc": "هذه الميزة غير مفعّلة في هذه الأكاديمية حالياً. للاستفسار، تواصل مع المدرب.",
   "stu.backToHome": "العودة للرئيسية",
    "stu.myCoursesPgDesc": "تابع تقدّمك في الدورات المسجّل فيها",
    "stu.ent.lifetime": "وصول دائم",
    "stu.ent.oneTime": "شراء مرة واحدة",
    "stu.ent.subscription": "اشتراك",
    "stu.ent.monthly": "اشتراك شهري",
    "stu.ent.annual": "اشتراك سنوي",
    "stu.ent.renews": "يتجدد في",
    "stu.ent.until": "حتى",
    "stu.ent.expired": "منتهي",
    "stu.ent.free": "مجاني",
    "stu.ent.trial": "وصول مؤقت",
    "stu.ent.renewNow": "جدّد الاشتراك",
    "stu.ent.buyAgain": "اشترِ مرة أخرى",
    "stu.ent.expiredOn": "انتهى في",
    "stu.ent.scheduledCancel": "مجدول للإلغاء — يستمر حتى",
    "stu.ent.pastDue": "تأخر السداد — حدّث طريقة الدفع",
    "stu.ent.activeSection": "المشتريات النشطة",
    "stu.ent.expiredSection": "مشتريات منتهية",
    "stu.ent.expiredAccessTooltip": "انتهت مدة وصولك لهذا المحتوى",
    "stu.ent.lifetimeBig": "♾ وصول دائم",
    "stu.ent.billingMonthly": "فوترة شهرية",
    "stu.ent.billingAnnual": "فوترة سنوية",
    "stu.academyPaused": "الأكاديمية متوقفة مؤقتاً",
    "stu.academyPausedDesc": "هذه الأكاديمية غير متاحة حالياً. يرجى التواصل مع المدرب للمزيد من المعلومات.",
    "stu.purchaseSuccess": "تمت عملية الشراء بنجاح! 🎉",
    "stu.purchaseSuccessDesc": "تم تسجيلك في المنتج وأصبح بإمكانك الوصول للمحتوى الآن.",
    "stu.paymentCancelled": "تم إلغاء عملية الدفع",
    "stu.paymentCancelledDesc": "يمكنك المحاولة مرة أخرى في أي وقت.",
    "stu.contentPreparing": "المحتوى قيد التحضير ✨",
    "stu.contentPreparingDesc": "مدربك يعمل على تجهيز محتوى رائع لك. عُد قريباً!",
    "stu.academyComingSoonTitle": "أكاديمية رهيبة قيد التجهيز ✨",
    "stu.academyComingSoonOwnerDesc": "توجّه لإعداد صفحة الهبوط لعرض إبداعاتك للعالم.",
    "stu.academyComingSoonGuestDesc": "مدرّبك يضع اللمسات الأخيرة على تجربة مميزة. عُد قريباً!",
    "stu.setupLandingPageBtn": "إعداد صفحة الهبوط",
    "stu.backToDashboardBtn": "العودة إلى لوحة التحكم",
    "stu.backToDwrrahBtn": "العودة إلى دورة",
    "stu.startConversation": "ابدأ محادثة مع المدرب",
    "stu.typeMessage": "اكتب رسالتك...",
    "stu.newMessage": "رسالة جديدة",
    "stu.startDiscussion": "ابدأ النقاش مع زملائك",
    "stu.instructor": "مدرّب",
    "stu.reply": "رد",
    "stu.pin": "تثبيت",
    "stu.unpin": "إلغاء التثبيت",
    "stu.hide": "إخفاء",
    "stu.show": "إظهار",
    "stu.replyTo": "رد على:",
    "stu.aboutCourse": "عن الدورة",
    "stu.courseContent": "محتوى الدورة",
    "stu.startCourse": "ابدأ الدورة",
    "stu.lesson": "درس",
    "stu.lessonsCount": "درس",
    "stu.module": "وحدة",
    "stu.hour": "ساعة",
    "stu.and": "و",
    "stu.minute": "دقيقة",
    "stu.question": "سؤال",
    "stu.free": "مجاني",
    "stu.content": "المحتوى",
    "stu.discussion": "النقاش",
    "stu.lessonCompleted": "درس مكتمل",
    "stu.of": "من",
    "stu.noLiveSessions": "لا توجد جلسات مباشرة مرتبطة بهذه الدورة حالياً",
    "stu.liveSessions": "الجلسات المباشرة",
    "stu.scheduled": "مجدولة",
    "stu.liveNow": "🔴 مباشرة الآن",
    "stu.ended": "منتهية",
    "stu.cancelled": "ملغاة",
    "stu.join": "انضم",
    "stu.joinNow": "انضم الآن",
    "stu.details": "التفاصيل",
    "stu.recording": "التسجيل",
    "stu.private": "خاصة",
    "stu.group": "جماعية",
    "stu.loginFreeAccess": "سجّل دخولك لتبدأ النسخة المجانية",
    "stu.startFreeAccess": "ابدأ النسخة المجانية",
    "stu.alreadyHaveFreeAccess": "لديك وصول مجاني لهذا المنتج بالفعل",
    "stu.freeAccessActivated": "تم تفعيل الوصول المجاني بنجاح! 🎉",
    "stu.freeAccessError": "حدث خطأ أثناء تفعيل الوصول المجاني",
    "stu.commentsAndQuestions": "التعليقات والأسئلة",
    "stu.replyToAuthor": "رد على",
    "stu.writeComment": "اكتب تعليقك أو سؤالك...",
    "stu.noComments": "لا توجد تعليقات بعد. كن أول من يعلق!",
    "stu.unknown": "مجهول",
    "stu.noLessonContent": "لا يوجد محتوى في هذا الدرس",
    "stu.notesTitle": "ملاحظاتي 📝",
    "stu.notesPlaceholder": "اكتب ملاحظاتك الشخصية هنا...",
    "stu.notesSaving": "جارٍ الحفظ...",
    "stu.notesSaved": "تم الحفظ",
    "stu.notesChars": "حرف",
    "stu.contentLocked": "هذا المحتوى غير متاح حالياً",
    "stu.requiresSubscription": "هذا المحتوى يتطلب اشتراك",
    "stu.viewProduct": "عرض المنتج",
    "stu.orChooseOther": "أو اختر من الخيارات الأخرى",
    "stu.loginFirst": "سجّل دخولك أولاً للمتابعة",
    "stu.course": "دورة",
    "stu.bundle": "باقة",
    "stu.membership": "عضوية",
    "stu.sessionPackage": "حزمة جلسات",
    "stu.product": "منتج",
    "stu.productContents": "محتويات المنتج",
    "stu.freeAccessAvailable": "وصول مجاني متاح",
    "stu.notifications": "الإشعارات",
    "stu.unread": "غير مقروء",
    "stu.noNotifications": "لا توجد إشعارات",
    "stu.viewAllNotifications": "عرض جميع الإشعارات",
    "support.title": "الدعم والبلاغات",
    "support.type": "نوع البلاغ",
    "support.description": "الوصف",
    "support.placeholder": "اكتب تفاصيل البلاغ هنا...",
    "support.submit": "إرسال البلاغ",
    "support.success": "تم إرسال البلاغ بنجاح",
    "support.error": "تعذّر الإرسال",
    "support.descRequired": "الوصف مطلوب",
    "support.descTooLong": "الوصف طويل جداً (أقصى 2000 حرف)",
    "support.studentHint": "أرسل بلاغك لإدارة الأكاديمية",
    "support.platformHint": "أرسل بلاغك لفريق المنصة",
    "support.types.bug": "مشكلة تقنية",
    "support.types.feature_request": "طلب ميزة",
    "support.types.account_issue": "مشكلة حساب",
    "support.types.payment_issue": "مشكلة دفع",
    "support.types.other": "أخرى",
    "support.tab": "الدعم والبلاغات",
    "support.history": "سجل بلاغاتي",
    "support.empty": "لا توجد بلاغات بعد",
    "support.loadError": "تعذّر تحميل البلاغات",
    "stu.now": "الآن",
    "stu.minsAgo": "د",
    "stu.hoursAgo": "س",
    "stu.daysAgo": "ي",
    "stu.couponCode": "كود الخصم",
    "stu.discount": "خصم",
    "stu.hasAccess": "لديك وصول لهذا المنتج",
    "stu.loginToGetFree": "للحصول عليه مجاناً",
    "stu.loginToPurchase": "للشراء",
    "stu.getItFree": "احصل عليه مجاناً",
    "stu.subscribeNow": "اشترك الآن",
    "stu.buyNow": "اشترِ الآن",
    "stu.refundPolicy": "سياسة الاسترجاع:",
    "stu.refund7": "7 أيام",
    "stu.refund14": "14 يوم",
    "stu.refund30": "30 يوم",
    "stu.paymentNotConfigured": "هذا المنتج غير متاح للشراء حالياً. يرجى التواصل مع الأكاديمية.",
    "stu.checkoutError": "حدث خطأ أثناء إنشاء جلسة الدفع",
    "stu.noPaymentUrl": "لم يتم إرجاع رابط الدفع",
    "stu.purchaseError": "حدث خطأ أثناء الشراء",
    "stu.noIncludedSessions": "لا توجد جلسات مباشرة مشمولة حالياً",
    "stu.includedSessions": "الجلسات المباشرة المشمولة",
    "stu.writeAnswer": "اكتب الإجابة هنا...",
    "stu.correctAnswer": "الإجابة الصحيحة:",
    "stu.passedQuiz": "أحسنت! اجتزت الاختبار",
    "stu.failedQuiz": "لم تجتز الاختبار",
    "stu.score": "الدرجة",
    "stu.points": "النقاط",
    "stu.passingGrade": "درجة النجاح",
    "stu.allAttemptsUsed": "استنفدت جميع المحاولات. تواصل مع المدرب.",
    "stu.requestSentToInstructor": "تم إرسال طلب للمدرب لمنحك محاولة إضافية.",
    "stu.canContinue": "يمكنك المتابعة رغم عدم اجتياز الاختبار.",
    "stu.cannotRetry": "لا يمكن إعادة المحاولة.",
    "stu.timeExceededTitle": "انتهى الوقت",
    "stu.timeExceededDesc": "تم تسليم الاختبار تلقائياً لانتهاء الوقت المحدد.",
    "stu.submitError": "تعذر تسليم الاختبار",
    "stu.retryAttempt": "إعادة المحاولة",
    "stu.continue": "متابعة",
    "stu.reviewAnswers": "مراجعة الإجابات",
    "stu.previewSubmitDisabled": "وضع المحاكاة — يمكن الإرسال محلياً، النتيجة لا تُحفظ",
    "stu.attempts": "المحاولات:",
    "stu.attemptsExhausted": "استنفدت المحاولات. يمكنك المتابعة.",
    "stu.myAnalytics": "تحليلاتي",
    "stu.preparingCharts": "جاري تجهيز الرسوم البيانية...",
    "stu.allCourses": "كل الدورات",
    "stu.chartsAppearAfter": "ستظهر الرسوم البيانية هنا تلقائياً بعد بدء الدراسة أو حل الاختبارات أو حجز الجلسات.",
    "stu.courseProgress": "تقدم الدورات",
    "stu.noCourses": "بلا دورات",
    "stu.progressAfterEnroll": "ستظهر نسب التقدم هنا بعد التسجيل في أول دورة.",
    "stu.weeklyActivity": "نشاط التعلم الأسبوعي",
    "stu.lessonsTab": "دروس",
    "stu.timeTab": "وقت (دقيقة)",
    "stu.completedLessons": "دروس مكتملة",
    "stu.activityAfterLessons": "سيظهر نشاطك اليومي هنا بعد إكمال الدروس.",
    "stu.quizPerformance": "أداء الكويزات",
    "stu.passRate": "نسبة النجاح",
    "stu.attempt": "محاولة",
    "stu.avgTimePerQuiz": "متوسط الوقت لكل اختبار",
    "stu.noQuizYet": "لم يتم حل أي اختبار بعد.",
    "stu.sessionAttendance": "حضور الجلسات",
    "stu.attended": "حضر",
    "stu.booked": "محجوز",
    "stu.noShow": "لم يحضر",
    "stu.cancelledStatus": "ملغي",
    "stu.noSessionsBooked": "لم يتم حجز أي جلسة حتى الآن.",
    "stu.milestoneTimeline": "مراحل التعلم",
    "stu.courseEnrollment": "تسجيل في دورة",
    "stu.courseCompletion": "إكمال دورة",
    "stu.certIssued": "شهادة صادرة",
    "stu.certFor": "شهادة:",
    "stu.monthlyActive": "شهرياً",
    "stu.quarterlyActive": "كل 3 أشهر",
    "stu.annuallyActive": "سنوياً",

    // Discussion & Comments
    "stu.startDiscussion": "ابدأ النقاش مع زملائك",
    "stu.reply": "رد",
    "stu.unpin": "إلغاء التثبيت",
    "stu.pin": "تثبيت",
    "stu.show": "إظهار",
    "stu.hide": "إخفاء",
    "stu.replyTo": "رد على:",
    "stu.cancel": "إلغاء",
    "stu.writeMessage": "اكتب رسالتك...",
    "stu.instructor": "المدرب",
    "stu.student": "طالب",
    "stu.instructorBadge": "مدرّب",
    "stu.unknown": "مجهول",
    "stu.commentsTitle": "التعليقات والأسئلة",
    "stu.writeComment": "اكتب تعليقك أو سؤالك...",
    "stu.noCommentsYet": "لا توجد تعليقات بعد. كن أول من يعلق!",
    "stu.newCommentNotif": "تعليق جديد من",
    "stu.replyNotif": "رد على تعليقك",
    "stu.instructorReply": "رد المدرب:",

    // Sidebar
    "stu.contentTab": "المحتوى",
    "stu.discussionTab": "النقاش",
    "stu.lessonCompleted": "درس مكتمل",
    "stu.free": "مجاني",
    "stu.questionCount": "سؤال",

    // Quiz
    "stu.correctAnswer": "الإجابة الصحيحة:",
    "stu.typeAnswer": "اكتب الإجابة هنا...",
    "stu.retryQuiz": "إعادة المحاولة",
    "stu.passingGrade": "درجة النجاح:",
    "stu.questionsCount": "سؤال",
    "stu.minutesLabel": "دقيقة",
    "stu.exhaustedBlock": "استنفدت جميع المحاولات. تواصل مع المدرب.",
    "stu.exhaustedRequest": "تم إرسال طلب للمدرب لمنحك محاولة إضافية.",
    "stu.exhaustedContinue": "استنفدت المحاولات. يمكنك المتابعة.",

    // Live Session Block
    "stu.noSessionSelected": "لم يتم تحديد جلسة مباشرة",
    "stu.sessionNotFound": "الجلسة غير موجودة",
    "stu.sessionEnded": "انتهت الجلسة",
    "stu.sessionCancelled": "الجلسة ملغاة",
    "stu.liveNowLabel": "🔴 مباشرة الآن",
    "stu.aboutToStart": "الجلسة على وشك البدء",
    "stu.bookSeat": "احجز مكانك",
    "stu.retry": "إعادة المحاولة",
    "stu.startsIn": "ستبدأ بعد",
    "stu.bookedConfirm": "✓ تم الحجز — ستتمكن من الانضمام عند بدء الجلسة",
    "stu.dayUnit": "يوم",
    "stu.hourAndMin": "ساعة و",
    "stu.failedLoadRoom": "فشل تحميل غرفة الاجتماع",

    // File & Quiz inline blocks
    "stu.loadingFile": "جاري تحميل الملف...",
    "stu.viewOnly": "عرض فقط",
    "stu.attachedFile": "ملف مرفق",
    "stu.quizInlineLoading": "جارٍ التحميل...",
    "stu.quizPassingScore": "درجة النجاح",
    "stu.start": "ابدأ",

    // Time ago
    "stu.agoMin": "منذ {n} د",
    "stu.agoHour": "منذ {n} س",
    "stu.agoDay": "منذ {n} ي",

    // Milestones (analytics)
    "stu.milestonesAppear": "ستظهر هنا إنجازاتك مثل التسجيل في الدورات، الإكمال، وإصدار الشهادات.",
    "stu.progress": "التقدم",
    "stu.minutes": "دقائق",
    "stu.avg": "المتوسط",

    // Instructor page titles & descriptions
    "ins.pg.coursesTitle": "الدورات",
    "ins.pg.coursesDesc": "أنشئ وأدر دوراتك التعليمية",
    "ins.pg.productsTitle": "المنتجات",
    "ins.pg.productsDesc": "أنشئ منتجاتك وحدد أسعارها والمحتوى المشمول",
    "ins.pg.studentsTitle": "الطلاب",
    "ins.pg.studentsDesc": "تابع طلابك وتفاعلهم",
    "ins.pg.sessionsTitle": "الجلسات المجدولة",
    "ins.pg.sessionsDesc": "أنشئ جلسات مباشرة وتفاعل مع طلابك",
    "ins.pg.messagesTitle": "الرسائل",
    "ins.pg.messagesDesc": "تواصل مع طلابك",
    "ins.pg.commentsTitle": "التعليقات",
    "ins.pg.commentsDesc": "إدارة تعليقات الطلاب على الدروس",
    "ins.pg.walletTitle": "المحفظة",
    "ins.pg.walletDesc": "تابع رصيدك والمعاملات المالية لأكاديميتك",
    "ins.pg.couponsTitle": "الكوبونات",
    "ins.pg.couponsDesc": "أنشئ كوبونات خصم لمنتجاتك",
    "ins.pg.pagesTitle": "الصفحات العامة",
    "ins.pg.pagesDesc": "أنشئ وخصّص صفحات موقع أكاديميتك",
    "ins.pg.domainsTitle": "النطاقات",
    "ins.pg.domainsDesc": "إدارة عناوين URL لأكاديميتك",
    "ins.pg.teamTitle": "فريق العمل",
    "ins.pg.teamDesc": "إدارة أعضاء فريق أكاديميتك وأدوارهم",
    "ins.pg.exportTitle": "تصدير البيانات",
    "ins.pg.exportDesc": "صدّر بيانات أكاديميتك بصيغة CSV أو JSON",
    "ins.pg.emailTitle": "البريد التلقائي",
    "ins.pg.emailDesc": "أرسل إيميلات تلقائية لطلابك عند أحداث معينة",
    "ins.pg.certsTitle": "قوالب الشهادات",
    "ins.pg.certsDesc": "خصص تصميم الشهادات التي تُصدر للطلاب عند إتمام الدورات",
    "ins.pg.notifTitle": "سجل الإشعارات",
    "ins.pg.notifDesc": "جميع الإشعارات الواردة",
    "ins.pg.questionBankTitle": "بنك الأسئلة",
    "ins.pg.questionBankDesc": "أنشئ وأدر أسئلتك واستخدمها في اختباراتك",
    "ins.pg.onboardingTitle": "دليل البدء",
    "ins.pg.onboardingDesc": "خطوات إعداد أكاديميتك",
    "ins.pg.dashboardTitle": "لوحة التحكم",
    "ins.pg.dashboardDesc": "نظرة عامة على أداء أكاديميتك",
    "ins.pg.settingsTitle": "الإعدادات",
    "ins.pg.settingsDesc": "إعدادات أكاديميتك العامة",

    // Instructor page common UI
    "ins.pg.newCourse": "دورة جديدة",
    "ins.pg.newProduct": "منتج جديد",
    "ins.pg.newCoupon": "كوبون جديد",
    "ins.pg.newSession": "جلسة جديدة",
    "ins.pg.newPage": "صفحة جديدة",
    "ins.pg.inviteStudent": "دعوة طالب",
    "ins.pg.inviteMember": "دعوة عضو",
    "ins.pg.export": "تصدير",
    "ins.pg.sendEmail": "إرسال رسالة",
    "ins.pg.emailLog": "سجل الإرسال",
    "ins.pg.readAll": "قراءة الكل",

    // Instructor status tabs
    "ins.tab.all": "الكل",
    "ins.tab.draft": "مسودة",
    "ins.tab.published": "منشورة",
    "ins.tab.paused": "متوقفة",
    "ins.tab.archived": "مؤرشفة",
    "ins.tab.scheduled": "مجدولة",
    "ins.tab.live": "مباشرة الآن",
    "ins.tab.completed": "منتهية",
    "ins.tab.cancelled": "ملغاة",
    "ins.tab.pending": "بانتظار الرد",
    "ins.tab.individual": "محادثات فردية",
    "ins.tab.groups": "مجموعات",

    // Instructor empty states
    "ins.empty.courses": "لا توجد دورات بعد. اضغط «دورة جديدة» للبدء.",
    "ins.empty.coursesFiltered": "لا توجد نتائج مطابقة.",
    "ins.empty.products": "لا توجد منتجات بعد. أنشئ أول منتج لبدء البيع.",
    "ins.empty.students": "لا يوجد طلاب بعد. ادعُ طلابك للبدء.",
    "ins.empty.studentsFiltered": "لا توجد نتائج مطابقة.",
    "ins.empty.coupons": "لا توجد كوبونات بعد. أنشئ أول كوبون خصم.",
    "ins.empty.couponsSearch": "لا توجد نتائج للبحث",
    "ins.empty.pages": "لا توجد صفحات بعد. اضغط \"صفحة جديدة\" للبدء.",
    "ins.empty.team": "لا يوجد أعضاء بعد. ادعُ أول عضو للفريق.",
    "ins.empty.comments": "لا توجد تعليقات بانتظار الرد 🎉",
    "ins.empty.commentsAll": "لا توجد تعليقات",
    "ins.empty.messages": "اختر محادثة للبدء",
    "ins.empty.notif": "لا توجد إشعارات",
    "ins.empty.sessions": "أنشئ جلسات مباشرة وتفاعل مع طلابك",

    // Instructor toast messages
    "ins.toast.deleted": "تم الحذف",
    "ins.toast.saved": "تم الحفظ",
    "ins.toast.activated": "تم التفعيل",
    "ins.toast.deactivated": "تم التعطيل",
    "ins.toast.updated": "تم التحديث",
    "ins.toast.sent": "تم الإرسال",
    "ins.toast.created": "تم الإنشاء",
    "ins.toast.error": "خطأ",
    "ins.toast.replySent": "تم إرسال الرد",
    "ins.toast.pinned": "تم التثبيت",
    "ins.toast.unpinned": "تم إلغاء التثبيت",
    "ins.toast.shown": "تم إظهار التعليق",
    "ins.toast.hidden": "تم إخفاء التعليق",
    "ins.toast.roleChanged": "تم تغيير دور العضو.",
    "ins.toast.revoked": "تم إلغاء عضوية هذا المستخدم.",
    "ins.toast.inviteSent": "تم إرسال دعوة الانضمام.",
    "ins.toast.exportSuccess": "تم التصدير بنجاح.",
    "ins.toast.pageCreated": "تم إنشاء صفحة جديدة.",
    "ins.toast.pageDeleted": "تم حذف الصفحة بنجاح.",
    "ins.toast.homepageSet": "أصبحت الصفحة الرئيسية.",
    "ins.toast.cantDeleteHome": "لا يمكن حذف الصفحة الرئيسية. عيّن صفحة أخرى كرئيسية أولاً.",
    "ins.toast.certSaved": "تم حفظ إعدادات قالب الشهادة بنجاح",
    "ins.toast.signatureUploaded": "تم رفع التوقيع",
    "ins.toast.sessionCreated": "تم إنشاء الجلسة بنجاح",
    "ins.toast.sessionCancelled": "تم إلغاء الجلسة",
    "ins.toast.streamStarted": "تم بدء البث — يتم فتح الجلسة...",
    "ins.toast.slugSaved": "تم تحديث رابط الأكاديمية.",
    "ins.toast.domainSaved": "تم حفظ النطاق. تأكد من إعدادات DNS ثم اضغط تحقق.",
    "ins.toast.domainVerified": "النطاق المخصص مفعّل بنجاح.",
    "ins.toast.domainNotVerified": "تأكد من إعدادات DNS وحاول مرة أخرى.",
    "ins.toast.domainRemoved": "تم إزالة النطاق المخصص.",
    "ins.toast.productDeleted": "تم حذف المنتج بنجاح.",
    "ins.toast.courseDeleted": "تم حذف الدورة.",
    "ins.toast.couponDeleted": "تم حذف الكوبون بنجاح",
    "ins.toast.cantPublish": "لا يمكن النشر حالياً",
    "ins.toast.cantPublishDesc": "وثّق حسابك البنكي عبر Stripe أولاً لتتمكن من نشر المنتجات المدفوعة للطلاب.",
    "ins.toast.sessionError": "حدث خطأ أثناء إنشاء الجلسة",
    "ins.toast.enterTitle": "يرجى إدخال عنوان الجلسة",
    "ins.toast.selectDate": "حدد التاريخ والوقت",

    // Instructor permissions
    "ins.noAccess": "ليس لديك صلاحية الوصول لهذه الصفحة.",

    // Instructor Stripe gate
    "ins.stripe.linkAccount": "وثّق حسابك لاستلام المدفوعات",
    "ins.stripe.freePublishNote": "يمكنك نشر المنتجات المجانية مباشرة، لكن المنتجات المدفوعة ستبقى كمسودة حتى يتم ربط حساب استلام الأرباح.",
    "ins.stripe.linkBtn": "وثّق حسابك",

    // Instructor wallet
    "ins.wallet.monthRevenue": "إيرادات الشهر الحالي:",
    "ins.wallet.payoutsHeld": "التحويلات معلّقة",

    // Instructor comments
    "ins.comments.pendingReply": "بانتظار الرد",
    "ins.comments.allCourses": "كل الدورات",
    "ins.comments.replyPlaceholder": "اكتب ردك...",
    "ins.comments.replyBtn": "رد",
    "ins.comments.replyLink": "الرد ↩",

    // Instructor sessions form
    "ins.session.createTitle": "إنشاء جلسة جديدة",
    "ins.session.titleLabel": "عنوان الجلسة *",
    "ins.session.titlePlaceholder": "مثال: جلسة مراجعة أسبوعية...",
    "ins.session.descLabel": "الوصف (اختياري)",
    "ins.session.descPlaceholder": "وصف مختصر...",
    "ins.session.typeLabel": "نوع الجلسة",
    "ins.session.groupType": "جماعية",
    "ins.session.privateType": "خاصة (1-1)",
    "ins.session.durationLabel": "المدة (دقائق)",
    "ins.session.dateLabel": "التاريخ والوقت *",
    "ins.session.capacityLabel": "السعة",
    "ins.session.once": "مرة واحدة",
    "ins.session.weekly": "أسبوعياً",
    "ins.session.biweekly": "كل أسبوعين",
    "ins.session.calendarBanner": "💡 اربط تقويم Google لمزامنة مواعيد جلساتك تلقائياً",
    "ins.session.linkCalendar": "ربط التقويم",
    "ins.session.sessionCount": "جلسة",
    "ins.session.duplicated": "تم نسخ بيانات الجلسة — حدد الموعد واحفظ",

    // Instructor domains
    "ins.domain.academyLink": "رابط الأكاديمية",
    "ins.domain.linkLocked": "الرابط مقفل لأن النطاق المخصص مفعّل. طلابك يصلون عبر النطاق المخصص.",
    "ins.domain.linkLabel": "رابط أكاديميتك على المنصة",
    "ins.domain.linkHint": "أحرف إنجليزية صغيرة، أرقام، وشرطات فقط. 3 أحرف كحد أدنى.",
    "ins.domain.slugMinError": "الرابط يجب أن يكون 3 أحرف على الأقل",
    "ins.domain.slugTaken": "هذا الرابط محجوز. جرب اسماً آخر.",
    "ins.domain.slugReserved": "هذا الرابط محجوز للنظام. جرب اسماً آخر.",
    "ins.domain.customDomain": "نطاق مخصص",
    "ins.domain.proRequired": "الخطة الاحترافية",
    "ins.domain.proUpgrade": "النطاق المخصص متاح في الخطة الاحترافية فقط.",
    "ins.domain.upgradePlan": "ترقية الخطة",
    "ins.domain.dnsSettings": "إعدادات DNS المطلوبة:",
    "ins.domain.verifyNow": "تحقق الآن",
    "ins.domain.remove": "إزالة",
    "ins.domain.activeDomain": "النطاق مفعّل. طلابك يصلون عبر",
    "ins.domain.disabledDomain": "النطاق المخصص معطّل بسبب تخفيض الخطة. قم بالترقية للخطة الاحترافية لإعادة التفعيل.",
    "ins.domain.comingSoonBadge": "قريباً ✨",
    "ins.domain.comingSoonMsg": "نجهّز لك تجربة نطاق خاص مميزة — ترقّبها!",
    "ins.domain.statusSuspended": "معلَّق",
    "ins.domain.statusRemovalPending": "قيد الإزالة",
    "ins.domain.suspendedBanner": "النطاق المخصص معلَّق حالياً بسبب الخطة أو حالة الاشتراك. يتم تحويل طلابك تلقائياً إلى رابط الأكاديمية على المنصة دون أي خطأ. قم بترقية خطتك أو تجديد الاشتراك لإعادة تفعيل النطاق.",
    "ins.domain.removalPendingBanner": "تم طلب فصل النطاق. سيظل النطاق نشطاً ويحوّل طلابك إلى رابط المنصة لمدة 7 أيام، ثم يُحذف نهائياً. يمكنك إلغاء الطلب أو الفصل الفوري الآن.",
    "ins.domain.removalPendingSince": "تم طلب الفصل في",
    "ins.domain.removalPurgeOn": "الحذف النهائي في",
    "ins.domain.requestRemoval": "طلب فصل النطاق",
    "ins.domain.cancelRemoval": "إلغاء طلب الفصل",
    "ins.domain.removeNow": "فصل فوري",
    "ins.domain.confirmRequestRemoval": "سيتم بدء فصل النطاق خلال 7 أيام، ويتحول الطلاب تلقائياً إلى رابط المنصة.\n\n⚠️ مهم جداً: لضمان وصول الطلاب لصفحة التحويل اللطيفة بدلاً من صفحة خطأ DNS من المتصفح، لا تُزل سجل CNAME من إعدادات الدومين لدى مسجِّل الدومين قبل مرور 7 أيام كاملة. إن أزلته الآن، سيرى الطلاب رسالة خطأ تقنية (DNS resolution error) ولن نستطيع تحويلهم.\n\nهل تريد المتابعة؟",
    "ins.domain.confirmCancelRemoval": "هل تريد إلغاء طلب فصل النطاق؟",
    "ins.domain.confirmRemoveNow": "سيتم فصل النطاق فوراً ولن يعمل بعد ذلك. هل تريد المتابعة؟",
    "ins.domain.removalRequested": "تم تسجيل طلب الفصل",
    "ins.domain.removalCancelled": "تم إلغاء طلب الفصل",
    "ins.domain.removedNow": "تم فصل النطاق",
    "ins.domain.removeNowWarning": "تنبيه: بعد الفصل الفوري لن يستطيع الطلاب الوصول إلى أكاديميتك عبر هذا النطاق. ننصحك بإشعارهم مسبقاً عبر البنر الإعلاني قبل المتابعة.",
    "ins.domain.openAnnouncementBanner": "فتح البنر الإعلاني",
    "ins.domain.providerAuthError": "تعذر الاتصال بمزود النطاقات. يرجى مراجعة إعدادات Cloudflare API في المنصة.",

    // Instructor pages
    "ins.pages.published": "منشورة",
    "ins.pages.draft": "مسودة",
    "ins.pages.setHomepage": "تعيين كصفحة رئيسية",
    "ins.pages.preview": "معاينة",

    // Instructor email templates
    "ins.email.proOnly": "متاح في الخطة الاحترافية فقط. قم بالترقية لتفعيل الإيميلات التلقائية.",

    // Instructor certificates
    "ins.cert.selectCourse": "اختر الدورة",
    "ins.cert.defaultAll": "القالب الافتراضي (جميع الدورات)",
    "ins.cert.perCourseHint": "يمكنك تخصيص قالب لكل دورة، أو استخدام القالب الافتراضي لجميع الدورات",
    "ins.cert.templateStyle": "نمط القالب",
    "ins.cert.elegant": "أنيق",
    "ins.cert.elegantDesc": "تدرج لوني مع شريط علوي ملون",
    "ins.cert.minimal": "بسيط",
    "ins.cert.minimalDesc": "حدود بسيطة بتصميم نظيف",
    "ins.cert.formal": "رسمي",
    "ins.cert.formalDesc": "حدود مزدوجة مع زخارف الأركان",
    "ins.cert.accentColor": "لون التمييز",
    "ins.cert.settingsTitle": "إعدادات الشهادة",
    "ins.cert.customText": "نص مخصص (اختياري)",
    "ins.cert.customTextPlaceholder": "يُضاف أسفل اسم الدورة في الشهادة...",
    "ins.cert.showLogo": "عرض شعار الأكاديمية",
    "ins.cert.showLogoDesc": "يظهر الشعار أعلى الشهادة",
    "ins.cert.addSignature": "إضافة توقيع",
    // Issue Certificate (manual) — Phase 3
    "ins.issueCert.navLabel": "إصدار شهادة",
    "ins.issueCert.title": "إصدار شهادة يدوياً",
    "ins.issueCert.subtitle": "أصدر شهادة لأي طالب في أي دورة دون اشتراط الإكمال.",
    "ins.issueCert.openDialog": "ابدأ الإصدار اليدوي",
    "ins.issueCert.backToTemplates": "العودة إلى قوالب الشهادات",
    "ins.issueCert.bypassNote": "الإصدار اليدوي يتجاوز شرط إكمال الدورة. الشهادات المكررة لنفس (طالب + دورة) تُمنع تلقائياً.",
    "ins.issueCert.f1Title": "اختيار القالب",
    "ins.issueCert.f1Desc": "يستخدم القالب المحفوظ للدورة أو الافتراضي (يشمل HTML المخصص).",
    "ins.issueCert.f2Title": "إشعار داخل النظام",
    "ins.issueCert.f2Desc": "يصل الطالب إشعار فوري برابط شهاداته.",
    "ins.issueCert.f3Title": "بريد إلكتروني",
    "ins.issueCert.f3Desc": "يُرسل قالب البريد certificate-issued تلقائياً.",
    "ins.issueCert.lastIssued": "آخر شهادة صادرة",
    "ins.issueCert.noPermissionTitle": "لا تملك صلاحية الإصدار اليدوي",
    "ins.issueCert.noPermissionDesc": "الإصدار اليدوي متاح لمالك الأكاديمية والمدير فقط.",
    "ins.cert.addSignatureDesc": "يظهر التوقيع أسفل الشهادة",
    "ins.cert.signatureImage": "صورة التوقيع",
    "ins.cert.changeSignature": "تغيير التوقيع",
    "ins.cert.uploadSignature": "رفع صورة التوقيع",
    "ins.cert.saveTemplate": "حفظ القالب",
    "ins.cert.previewTemplate": "معاينة",
    "ins.cert.previewNote": "سيتم تطبيق القالب تلقائياً على الشهادات الجديدة",
    "ins.cert.livePreview": "معاينة مباشرة",
    "ins.cert.pdfFinalNote": "الناتج النهائي للطالب: PDF",
    "ins.cert.pdfNote": "هذا الـ HTML يُستخدم كمصدر تصميم فقط — الشهادة النهائية للطالب تُسلَّم كـ PDF.",
    "ins.cert.customHtml": "HTML/CSS مخصص (متقدم)",
    "ins.cert.customHtmlDesc": "اكتب قالبك بنفسك — للمستخدمين المتقدمين فقط",
    "ins.cert.customHtmlLabel": "قالب HTML/CSS مخصص",
    "ins.cert.customHtmlHint": "استخدم المتغيرات التالية في القالب:",
    "ins.cert.tabTemplates": "القوالب",
    "ins.cert.tabAuto": "الإصدار التلقائي",
    "ins.cert.tabIssued": "الشهادات الصادرة",
    "ins.cert.autoExplain": "فعّل الإصدار التلقائي للشهادات لأي دورة لتُصدر الشهادة فور إتمام الطالب لها. الإصدار التلقائي يستخدم القالب المربوط بالدورة (أو القالب الافتراضي).",
    "ins.cert.autoOnDesc": "سيتم إصدار شهادة تلقائياً عند إكمال الدورة",
    "ins.cert.autoOffDesc": "لن تُصدر شهادة تلقائياً — يمكنك إصدارها يدوياً من تبويب الشهادات الصادرة",
    "ins.cert.autoSaved": "تم تحديث إعداد الشهادة للدورة",
    "ins.cert.draft": "مسودة",
    "ins.cert.noCourses": "لا توجد دورات في الأكاديمية بعد",
    "ins.cert.totalIssued": "إجمالي الشهادات: {count}",
    "ins.cert.refresh": "تحديث",
    "ins.cert.issueManual": "إصدار شهادة يدوياً",
    "ins.cert.fStudent": "الطالب",
    "ins.cert.fStudentPh": "ابحث باسم الطالب...",
    "ins.cert.fCourse": "الدورة",
    "ins.cert.fStatus": "الحالة",
    "ins.cert.fMode": "نوع الإصدار",
    "ins.cert.fFrom": "من تاريخ",
    "ins.cert.fTo": "إلى تاريخ",
    "ins.cert.all": "الكل",
    "ins.cert.clearFilters": "مسح الفلاتر",
    "ins.cert.colStudent": "الطالب",
    "ins.cert.colCourse": "الدورة",
    "ins.cert.colMode": "النوع",
    "ins.cert.colDate": "تاريخ الإصدار",
    "ins.cert.colStatus": "الحالة",
    "ins.cert.colActions": "إجراءات",
    "ins.cert.modeAuto": "تلقائي",
    "ins.cert.modeManual": "يدوي",
    "ins.cert.statusIssued": "صادرة",
    "ins.cert.statusRevoked": "ملغاة",
    "ins.cert.download": "تحميل PDF",
    "ins.cert.view": "عرض",
    "ins.cert.landscape": "أفقي",
    "ins.cert.portrait": "عمودي",
    "ins.cert.previewMatchesView": "هذه المعاينة مطابقة تماماً لما يراه الطالب عند فتح الشهادة.",
    "cert.view.title": "عرض الشهادة",
    "cert.view.print": "طباعة / حفظ PDF",
    "cert.view.notFound": "الشهادة غير موجودة",
    "cert.view.loadError": "تعذر تحميل الشهادة",
    "cert.view.noAccess": "قد لا يكون لديك صلاحية الوصول لهذه الشهادة.",
    "ins.cert.noResults": "لا توجد شهادات مطابقة للفلاتر الحالية",
    "ins.cert.regenFailed": "تعذّر إنشاء PDF — حاول مرة أخرى",

    // Instructor data export
    "ins.export.settingsTitle": "إعدادات التصدير",
    "ins.export.dataType": "نوع البيانات",
    "ins.export.fileFormat": "صيغة الملف",
    "ins.export.exportBtn": "تصدير",
    "ins.export.backupTitle": "نسخة احتياطية كاملة",
    "ins.export.backupDesc": "تصدير كل بيانات الأكاديمية في ملف Excel واحد متعدد الأوراق.",
    "ins.export.backupNote": "للأكاديميات الكبيرة جدًا قد يستغرق التصدير وقتًا أطول. إن فشل، صدّر الجداول واحدًا تلو الآخر.",
    "ins.export.backupBtn": "تصدير نسخة احتياطية كاملة (Excel)",
    "ins.export.group.people": "الطلاب والتسجيلات",
    "ins.export.group.content": "المحتوى التعليمي",
    "ins.export.group.finance": "المالية",
    "ins.export.group.sessionsCerts": "الجلسات والشهادات",
    "ins.export.group.support": "الدعم",
    "ins.export.ent.students": "الطلاب",
    "ins.export.ent.enrollments": "التسجيلات",
    "ins.export.ent.product_accesses": "صلاحيات الوصول للمنتجات",
    "ins.export.ent.courses": "الدورات",
    "ins.export.ent.products": "المنتجات",
    "ins.export.ent.quizzes": "الاختبارات",
    "ins.export.ent.quiz_attempts": "محاولات الاختبارات",
    "ins.export.ent.course_results": "نتائج الدورات",
    "ins.export.ent.payments": "المدفوعات",
    "ins.export.ent.wallet_transactions": "معاملات المحفظة",
    "ins.export.ent.bank_transfer_requests": "طلبات التحويل البنكي",
    "ins.export.ent.memberships": "اشتراكات العضوية",
    "ins.export.ent.sessions": "الجلسات",
    "ins.export.ent.session_bookings": "حجوزات الجلسات",
    "ins.export.ent.certificates": "الشهادات",
    "ins.export.ent.support_reports": "بلاغات الدعم",
    // Admin data export
    "adm.export.sidebar": "تصدير البيانات",
    "adm.export.title": "تصدير بيانات المنصة",
    "adm.export.desc": "تصدير بيانات أي أكاديمية أو بيانات المنصة بالكامل بصيغ Excel و CSV و JSON.",
    "adm.export.scope": "النطاق",
    "adm.export.allAcademies": "كل الأكاديميات",
    "adm.export.group.platform": "بيانات المنصة (للمشرف فقط)",
    "adm.export.ent.academies": "الأكاديميات",
    "adm.export.ent.instructor_subscriptions": "اشتراكات المدربين",
    "adm.export.ent.platform_admins": "مشرفو المنصة",
    "adm.export.ent.bank_transfer_escalations": "تصعيدات التحويل البنكي",
    "adm.export.ent.admin_audit_logs": "سجل تدقيق المشرفين",
    "adm.export.ent.pro_feature_requests": "طلبات ميزات Pro",
    "adm.export.ent.ops_health_snapshots": "لقطات صحة النظام",
    "adm.export.ent.email_logs": "سجل البريد",
    "adm.export.academyBackupDesc": "نسخة احتياطية لأكاديمية واحدة (أو لكل الأكاديميات حسب النطاق) في ملف Excel واحد.",
    "adm.export.platformBackupTitle": "نسخة احتياطية كاملة للمنصة",
    "adm.export.platformBackupDesc": "كل بيانات المنصة + كل بيانات الأكاديميات في ملف Excel واحد ضخم. للنسخ الاحتياطي الشامل فقط.",
    "adm.export.platformBackupBtn": "تصدير نسخة المنصة الكاملة",

    // Instructor notification types
    "ins.notif.newComment": "تعليق جديد",
    "ins.notif.newMessage": "رسالة جديدة",
    "ins.notif.enrollment": "تسجيل",
    "ins.notif.discussion": "نقاش",
    "ins.notif.grant": "منحة",
    "ins.notif.purchase": "شراء جديد",
    "ins.notif.newStudent": "طالب جديد",
    "ins.notif.courseCompleted": "إكمال دورة",
    "ins.notif.sessionCancelled": "إلغاء جلسة",
    "ins.notif.certificate": "شهادة",
    "ins.notif.allTypes": "جميع الأنواع",
    "ins.notif.searchPlaceholder": "بحث في الإشعارات...",
    "ins.notif.loading": "جارٍ التحميل...",
    "ins.notif.page": "صفحة",
    "ins.notif.prev": "السابق",
    "ins.notif.next": "التالي",
    "ins.notif.proFeatureRequest": "طلب إتاحة ميزة",
    "ins.notif.newCommentTitle": "تعليق جديد على درس",
    "ins.notif.newDiscussionTitle": "رسالة جديدة في نقاش الدورة",
    "ins.notif.inviteConverted": "تحويل دعوة شراء",
    "ins.sentInvites": "الدعوات المرسلة",

    // Feature requests page (instructor)
    "ins.featureRequests.nav": "طلبات الميزات",
    "ins.featureRequests.title": "طلبات إتاحة الميزات",
    "ins.featureRequests.subtitle": "ميزات Pro طلب طلابك إتاحتها — استخدمها كمؤشر على ما يحتاجه طلابك",
    "ins.featureRequests.totalRequests": "إجمالي الطلبات",
    "ins.featureRequests.empty": "لا توجد طلبات حتى الآن",
    "ins.featureRequests.unknownStudent": "طالب",

    // Instructor email logs
    "ins.emailLog.title": "سجل الإرسال",
    "ins.emailLog.desc": "تتبع حالة الإيميلات المرسلة",
    "ins.emailLog.total": "الإجمالي",
    "ins.emailLog.sentLabel": "تم الإرسال",
    "ins.emailLog.failedLabel": "فشل",
    "ins.emailLog.suppressedLabel": "محظور",
    "ins.emailLog.noEmails": "لا يوجد إيميلات في هذه الفترة",
    "ins.emailLog.recipient": "المستلم",
    "ins.emailLog.type": "النوع",
    "ins.emailLog.status": "الحالة",
    "ins.emailLog.error": "الخطأ",
    "ins.emailLog.date": "التاريخ",
    "ins.emailLog.searchPlaceholder": "بحث بالإيميل أو النوع...",
    "ins.emailLog.sent": "مرسل",
    "ins.emailLog.failed": "فاشل",
    "ins.emailLog.suppressed": "محظور",

    // Instructor email campaign
    "ins.campaign.title": "إرسال رسالة",
    "ins.campaign.desc": "أرسل إيميل مخصص لطلابك",
    "ins.campaign.subject": "العنوان",
    "ins.campaign.subjectPlaceholder": "عنوان الإيميل...",
    "ins.campaign.content": "المحتوى",
    "ins.campaign.contentPlaceholder": "اكتب محتوى الرسالة...",
    "ins.campaign.sendBtn": "إرسال",
    "ins.campaign.sentTitle": "تم الإرسال!",
    "ins.campaign.sentDesc": "تم إرسال الرسائل بنجاح",
    "ins.campaign.viewLog": "سجل الإرسال",
    "ins.campaign.newMessage": "إرسال رسالة جديدة",
    "ins.campaign.recipients": "المستلمين",
    "ins.campaign.selectAll": "تحديد الكل",
    "ins.campaign.deselectAll": "إلغاء الكل",
    "ins.campaign.noStudents": "لا يوجد طلاب",
    "ins.campaign.subjectRequired": "العنوان مطلوب",
    "ins.campaign.selectOne": "اختر طالب واحد على الأقل",
    "ins.campaign.student": "طالب",

    // Instructor session form extras
    "ins.session.linkCourse": "ربط بدورة (اختياري)",
    "ins.session.noCourseLink": "بدون ربط — جلسة مستقلة",
    "ins.session.free": "مجانية",
    "ins.session.allowBooking": "السماح بالحجز",
    "ins.session.enableRecording": "تفعيل التسجيل",
    "ins.session.studentAccess": "صلاحية الطالب",
    "ins.session.recurrence": "التكرار",
    "ins.session.recurrenceEnd": "تاريخ انتهاء التكرار",
    "ins.session.addContent": "إضافة محتوى للجلسة (اختياري)",
    "ins.session.freeText": "نص حر",
    "ins.session.image": "صورة",
    "ins.session.file": "ملف",
    "ins.session.cta": "دعوة للعمل",
    "ins.session.createBtn": "إنشاء الجلسة",
    "ins.session.noSessions": "لا توجد جلسات",
    "ins.session.noSessionsStatus": "لا توجد جلسات بهذه الحالة",
    "ins.session.firstSession": "أنشئ جلستك الأولى بالضغط على \"جلسة جديدة\" أعلاه",
    "ins.session.searchPlaceholder": "بحث في الجلسات...",
    "ins.session.invitesSent": "تم إرسال {n} دعوة",

    // Sessions page
    "ins.sessions.title": "الجلسات المجدولة",
    "ins.sessions.newBtn": "جلسة جديدة",
    "ins.sessions.createTitle": "إنشاء جلسة جديدة",
    "ins.sessions.sessionTitle": "عنوان الجلسة *",
    "ins.sessions.sessionTitlePlaceholder": "مثال: جلسة مراجعة أسبوعية...",
    "ins.sessions.descOptional": "الوصف (اختياري)",
    "ins.sessions.descPlaceholder": "وصف مختصر...",
    "ins.sessions.sessionType": "نوع الجلسة",
    "ins.sessions.group": "جماعية",
    "ins.sessions.private": "خاصة (1-1)",
    "ins.sessions.duration": "المدة (دقائق)",
    "ins.sessions.dateTime": "التاريخ والوقت *",
    "ins.sessions.maxCapacity": "السعة القصوى (0 = بلا حد)",
    "ins.sessions.calendarBanner": "💡 اربط تقويم Google لمزامنة مواعيد جلساتك تلقائياً",
    "ins.sessions.linkCalendar": "ربط التقويم",
    "ins.sessions.sessionCount": "{n} جلسة",
    "ins.sessions.enterTitle": "يرجى إدخال عنوان الجلسة",
    "ins.sessions.selectDateTime": "حدد التاريخ والوقت",
    "ins.sessions.createdMulti": "تم إنشاء {n} جلسة",
    "ins.sessions.createdSingle": "تم إنشاء الجلسة بنجاح",
    "ins.sessions.createError": "حدث خطأ أثناء إنشاء الجلسة",
    "ins.sessions.streamStarted": "تم بدء البث — يتم فتح الجلسة...",
    "ins.sessions.cancelled": "تم إلغاء الجلسة",
    "ins.sessions.deleted": "تم الحذف",
    "ins.sessions.duplicated": "تم نسخ بيانات الجلسة — حدد الموعد واحفظ",
    "ins.sessions.noAccess": "ليس لديك صلاحية الوصول.",
    "ins.sessions.liveSessions": "الجلسات المباشرة",
    "ins.sessions.createLiveSessions": "أنشئ جلسات مباشرة وتفاعل مع طلابك",
    "ins.sessions.statusAll": "الكل",
    "ins.sessions.statusScheduled": "مجدولة",
    "ins.sessions.statusLive": "مباشرة الآن",
    "ins.sessions.statusCompleted": "منتهية",
    "ins.sessions.statusCancelled": "ملغاة",
    "ins.sessions.statusOpenForBooking": "متاحة للحجز",
    "ins.sessions.sortLabel": "الترتيب",
    "ins.sessions.sortNearest": "الأقرب موعدًا",
    "ins.sessions.sortFurthest": "الأبعد موعدًا",
    "ins.sessions.sortNewest": "الأحدث إنشاء",
    "ins.sessions.bookablePreset": "جلسة بالحجز",
    "ins.sessions.bookablePresetHint": "الطالب يحجز موعدًا من الأوقات التي تتيحها أنت",
    "ins.sessions.bookableBadge": "نمط: جلسة بالحجز",
    "ins.sessions.convertToRegular": "تحويل إلى جلسة مجدوَلة",
    "ins.sessions.dialog.title": "نوع الجلسة الجديدة",
    "ins.sessions.dialog.subtitle": "اختر آلية تحديد الموعد. يمكنك تعديل كل التفاصيل بعد ذلك.",
    "ins.sessions.dialog.scheduledTitle": "جلسة مجدوَلة",
    "ins.sessions.dialog.scheduledDesc": "أنت تحدد التاريخ والوقت، والطلاب ينضمون في الموعد المحدد.",
    "ins.sessions.dialog.scheduledExamples": "محاضرات، ورش عمل، دورات مباشرة",
    "ins.sessions.dialog.bookableTitle": "جلسة جماعية بالحجز",
    "ins.sessions.dialog.bookableDesc": "جلسة جماعية بسعة محدودة، والطالب يختار من المواعيد التي تتيحها.",
    "ins.sessions.dialog.bookableExamples": "ورش جماعية صغيرة، حلقات نقاش، تدريب جماعي مرن",
    "ins.sessions.dialog.consultTitle": "جلسة استشارية فردية",
    "ins.sessions.dialog.consultDesc": "جلسة 1:1 مع طالب واحد فقط، يحجز موعدًا من الأوقات التي تتيحها.",
    "ins.sessions.dialog.consultExamples": "استشارة شخصية، كوتشينج فردي، مراجعة مشروع، مقابلة",
    "ins.sessions.dialog.suitableFor": "مناسبة لـ",
    "ins.sessions.dialog.choose": "اختيار",
    "ins.sessions.dialog.creatingPreset": "تنشئ الآن",
    "ins.sessions.dialog.changeType": "تغيير النوع",
    "ins.sessions.contentSectionAlwaysVisible": "محتوى الجلسة (يظهر للطالب داخل غرفة الجلسة)",
    "ins.sessions.coverHint": "أضف غلافًا لتظهر الجلسة كبطاقة احترافية للطلاب.",
    "ins.sessions.salesViaProductsInfo": "تُباع هذه الجلسة عبر المنتجات. أنشئ منتجًا واربطه بها لبدء البيع.",
    "ins.sessions.openLandingFromForm": "تخصيص صفحة هبوط الجلسة",
    "ins.sessions.openLandingEditor": "تصميم صفحة هبوط الجلسة",
    "ins.sessions.openLandingEditorAfterSave": "احفظ أولًا ثم صمّم صفحة الهبوط",
    "ins.sessions.salesAndAccess": "البيع والوصول",
    "ins.sessions.salesAndAccessHint": "الجلسات تُباع عبر المنتجات. أنشئ منتجًا واربطه بهذه الجلسة.",
    "ins.sessions.linkedProducts": "المنتجات المرتبطة",
    "ins.sessions.noLinkedProducts": "لا توجد منتجات مرتبطة بهذه الجلسة بعد.",
    "ins.sessions.linkNewProduct": "ربط بمنتج جديد",
    "ins.sessions.contentSectionTitle": "محتوى الجلسة (اختياري)",
    "ins.sessions.contentSectionHint": "يظهر للطالب بعد الحجز — فيديو ترحيبي، ملفات، شرح…",
    "ins.sessions.openForBookingHint": "متاحة للحجز — لا تاريخ ثابت",
    "ins.sessions.bookingQuantity": "عدد الجلسات لكل عملية شراء",
    "ins.sessions.once": "مرة واحدة",
    "ins.sessions.weekly": "أسبوعياً",
    "ins.sessions.biweekly": "كل أسبوعين",
    "ins.sessions.recurrenceHint": "سيتم إنشاء جلسة {interval} حتى {date}",
    "ins.sessions.everyWeek": "كل أسبوع",
    "ins.sessions.everyTwoWeeks": "كل أسبوعين",

    // Course lifecycle (archive / hard delete)
    "ins.courses.statusArchived": "مؤرشفة",
    "ins.courses.archive": "أرشفة",
    "ins.courses.unarchive": "إعادة نشر",
    "ins.courses.hardDelete": "حذف نهائي",
    "ins.courses.deleteOrArchive": "حذف / أرشفة",
    "ins.courses.confirmArchiveTitle": "أرشفة الدورة؟",
    "ins.courses.confirmArchiveDesc": "ستُخفى الدورة من البيع والكتالوج العام، لكن وصول الطلاب الحاليين يبقى محفوظاً.",
    "ins.courses.confirmHardDeleteTitle": "حذف نهائي؟",
    "ins.courses.confirmHardDeleteDesc": "سيتم حذف الدورة وكل محتواها بشكل نهائي. هذا الإجراء لا يمكن التراجع عنه.",
    "ins.courses.cannotDeleteHasDeps": "لا يمكن الحذف النهائي — لديك بيانات مرتبطة. استخدم الأرشفة للحفاظ على حقوق الطلاب.",
    "ins.courses.depEnrollments": "تسجيلات نشطة",
    "ins.courses.depCertificates": "شهادات صادرة",
    "ins.courses.depPayments": "دفعات",
    "ins.courses.depResults": "تقدّم طلاب",
    "ins.courses.depComments": "تعليقات/نقاشات",
    "ins.courses.depProducts": "منتجات مرتبطة",
    "ins.courses.archiveInsteadCta": "أرشفة بدلاً من ذلك",
    "ins.courses.autoArchivedProducts": "تم أيضاً أرشفة المنتجات التالية:",
    "ins.courses.archived": "تمت الأرشفة",
    "ins.courses.unarchived": "تمت إعادة النشر",
    "ins.courses.deleted": "تم الحذف نهائياً",
    "ins.courses.checking": "جاري الفحص...",
    "stu.courseArchivedBadge": "مؤرشفة",
    "stu.courseArchivedHint": "لم تعد متاحة للبيع، لكن وصولك محفوظ.",

    // CourseForm
    "ins.courseForm.settingsTitle": "إعدادات الدورة",
    "ins.courseForm.newCourse": "دورة جديدة",
    "ins.courseForm.previewPage": "معاينة صفحة البيع",
    "ins.courseForm.simulateStudent": "تجربة كطالب",
    "ins.courseForm.customizePage": "تخصيص الصفحة",
    "ins.courseForm.saveChanges": "حفظ التغييرات",
    "ins.courseForm.createCourse": "إنشاء الدورة",
    "ins.courseForm.basicInfo": "المعلومات الأساسية",
    "ins.courseForm.courseTitleLabel": "عنوان الدورة *",
    "ins.courseForm.courseTitlePlaceholder": "مثال: أساسيات التسويق الرقمي",
    "ins.courseForm.courseDesc": "وصف الدورة",
    "ins.courseForm.courseDescPlaceholder": "اكتب وصفاً تفصيلياً...",
    "ins.courseForm.category": "التصنيف",
    "ins.courseForm.categoryPlaceholder": "مثال: تسويق",
    "ins.courseForm.language": "اللغة",
    "ins.courseForm.courseImage": "صورة الدورة",
    "ins.courseForm.clickToUpload": "اضغط لرفع صورة",
    "ins.courseForm.titleRequired": "عنوان الدورة مطلوب.",
    "ins.courseForm.saved": "تم تحديث إعدادات الدورة.",
    "ins.courseForm.created": "تم إنشاء الدورة بنجاح.",
    "ins.courseForm.notFound": "لم يتم العثور على الدورة أو ليست ضمن نطاق الأكاديمية.",
    "ins.courseForm.loadError": "فشل تحميل إعدادات الدورة. يرجى المحاولة مرة أخرى.",
    "ins.courseForm.loadErrorTitle": "تعذّر تحميل الدورة",
    "ins.courseForm.uploadError": "خطأ في الرفع",
    "ins.courseForm.uploadFailed": "فشل رفع الصورة",

    // CourseContent
    "ins.courseContent.flatStructure": "هيكل مسطح",
    "ins.courseContent.modulesAndLessons": "وحدات ودروس",
    "ins.courseContent.lessonsCount": "{n} درس",
    "ins.courseContent.preview": "معاينة صفحة البيع",
    "ins.courseContent.simulate": "تجربة كطالب",
    "ins.courseContent.finalExam": "اختبار نهائي",
    "ins.courseContent.settings": "الإعدادات",
    "ins.courseContent.newLesson": "درس جديد",
    "ins.courseContent.newModule": "وحدة جديدة",
    "ins.courseContent.endOfCourseQuizzes": "اختبارات نهاية الدورة",
    "ins.courseContent.quizStats": "إحصائيات الاختبارات",
    "ins.courseContent.questionsCount": "{n} أسئلة",
    "ins.courseContent.notFound": "لم يتم العثور على الدورة أو ليست ضمن نطاق الأكاديمية.",
    "ins.courseContent.loadError": "فشل تحميل محتوى الدورة. يرجى المحاولة مرة أخرى.",
    "ins.courseContent.loadErrorTitle": "تعذّر تحميل محتوى الدورة",
    "ins.courseContent.structureLabel": "هيكل الدورة",
    "ins.courseContent.courseLevelLabel": "على مستوى الدورة",
    "ins.courseContent.addFinalExam": "إضافة اختبار نهائي",
    "common.moveUp": "نقل لأعلى",
    "common.moveDown": "نقل لأسفل",
    "common.collapse": "طيّ",
    "common.expand": "توسيع",
    "stu.openSidebar": "فتح القائمة",

    // LessonEditor
    "ins.lessonEditor.lessonTitlePlaceholder": "عنوان الدرس",
    "ins.lessonEditor.hidePreview": "إخفاء المعاينة",
    "ins.lessonEditor.showPreview": "إظهار المعاينة",
    "ins.lessonEditor.save": "حفظ",
    "ins.lessonEditor.lessonSettings": "إعدادات الدرس",
    "ins.lessonEditor.published": "منشور",
    "ins.lessonEditor.freePreview": "معاينة مجانية",
    "ins.lessonEditor.durationMinutes": "المدة (دقائق)",
    "ins.lessonEditor.comments": "التعليقات",
    "ins.lessonEditor.inheritCourse": "وراثة من الدورة",
    "ins.lessonEditor.enabled": "مفعّلة",
    "ins.lessonEditor.disabled": "معطّلة",
    "ins.lessonEditor.notFound": "لم يتم العثور على الدرس أو ليس ضمن نطاق الأكاديمية.",
    "ins.lessonEditor.loadError": "فشل تحميل الدرس. يرجى المحاولة مرة أخرى.",
    "ins.lessonEditor.loadErrorTitle": "تعذّر تحميل الدرس",

    // Course Builder — shared
    "ins.builder.lessonsLabel": "درس",
    "ins.builder.preview": "معاينة",
    "ins.builder.addLesson": "إضافة درس",
    "ins.builder.addQuiz": "اختبار",
    "ins.builder.noLessonsYet": "لا توجد دروس بعد",
    "ins.builder.emptyModulesHint": "لا توجد وحدات بعد. اضغط «وحدة جديدة» للبدء.",
    "ins.builder.emptyFlatHint": "لا توجد دروس بعد. اضغط «درس جديد» للبدء.",
    "ins.builder.quizQuestions": "{n} أسئلة",
    "ins.builder.emptyLesson": "الدرس فارغ. أضف محتوى لبناء الدرس.",
    "ins.builder.addContent": "إضافة محتوى",
    "ins.builder.addQuizAfter": "إضافة اختبار بعد هذا الدرس",
    "ins.builder.confirmDeleteLessonTitle": "حذف الدرس؟",
    "ins.builder.confirmDeleteLessonBody": "سيتم حذف الدرس \"{title}\" مع كل تعليقاته وتقدّم الطلاب فيه والاختبارات المرتبطة به نهائياً. لا يمكن التراجع.",
    "ins.builder.confirmDeleteQuizTitle": "حذف الاختبار؟",
    "ins.builder.confirmDeleteQuizBody": "سيتم حذف الاختبار \"{title}\" مع كل أسئلته ومحاولات الطلاب نهائياً. لا يمكن التراجع.",
    "ins.builder.confirmDeleteModuleTitle": "حذف الوحدة؟",
    "ins.builder.confirmDeleteModuleBody": "سيتم حذف الوحدة \"{title}\" مع كل دروسها واختباراتها نهائياً. لا يمكن التراجع.",
    "ins.builder.insertHere": "إضافة عنصر هنا",
    "ins.blockTransfer.moveTitle": "نقل العناصر إلى درس آخر",
    "ins.blockTransfer.copyTitle": "نسخ العناصر إلى درس آخر",
    "ins.blockTransfer.moveDesc": "اختر الدرس الذي تريد نقل العناصر المحددة إليه. سيتم إزالتها من الدرس الحالي.",
    "ins.blockTransfer.copyDesc": "اختر الدرس الذي تريد نسخ العناصر المحددة إليه. ستبقى نسخة في الدرس الحالي.",
    "ins.blockTransfer.confirmMove": "نقل العناصر",
    "ins.blockTransfer.confirmCopy": "نسخ العناصر",
    "ins.blockTransfer.selectedCount": "عدد العناصر المحددة:",
    "ins.blockTransfer.selectedN": "تم تحديد {n} عنصر",
    "ins.blockTransfer.noOtherLessons": "لا توجد دروس أخرى في هذه الدورة. أنشئ درسًا آخر أولًا.",
    "ins.blockTransfer.selectThisBlock": "تحديد هذا العنصر",
    "ins.blockTransfer.moveBtn": "نقل إلى درس آخر",
    "ins.blockTransfer.copyBtn": "نسخ إلى درس آخر",
    "ins.blockTransfer.clear": "إلغاء التحديد",
    "ins.blockTransfer.successTitle": "تم بنجاح",
    "ins.blockTransfer.movedDesc": "تم نقل العناصر إلى الدرس المختار",
    "ins.blockTransfer.copiedDesc": "تم نسخ العناصر إلى الدرس المختار",
    "ins.blockTransfer.targetMissing": "تعذّر إيجاد الدرس المستهدف",

    // Block picker
    "ins.blockPicker.title": "إضافة محتوى",
    "ins.blockPicker.video": "فيديو",
    "ins.blockPicker.videoDesc": "فيديو مرفوع أو رابط خارجي",
    "ins.blockPicker.text": "نص",
    "ins.blockPicker.textDesc": "محتوى نصي منسّق",
    "ins.blockPicker.image": "صورة",
    "ins.blockPicker.imageDesc": "صورة توضيحية",
    "ins.blockPicker.file": "ملف PDF",
    "ins.blockPicker.fileDesc": "ملف للعرض فقط",
    "ins.blockPicker.quiz": "اختبار مضمّن",
    "ins.blockPicker.quizDesc": "كويز داخل الدرس",
    "ins.blockPicker.liveSession": "جلسة مباشرة",
    "ins.blockPicker.liveSessionDesc": "تضمين جلسة مباشرة مجدولة",
    "ins.blockPicker.divider": "فاصل",
    "ins.blockPicker.dividerDesc": "فاصل بصري بين المحتوى",
    "ins.blockPicker.callout": "ملاحظة",
    "ins.blockPicker.calloutDesc": "تنبيه أو نصيحة",
    "ins.blockPicker.externalLink": "رابط خارجي",
    "ins.blockPicker.externalLinkDesc": "رابط لصفحة خارجية كمصدر للدرس",
    "ins.blockPicker.audio": "صوت",
    "ins.blockPicker.audioDesc": "ملف صوتي مرفوع أو رابط خارجي",

    // Block editors
    "ins.blockItem.unknownType": "نوع غير معروف",
    "ins.blockItem.video.youtube": "YouTube",
    "ins.blockItem.video.drive": "Drive",
    "ins.blockItem.video.vimeo": "Vimeo",
    "ins.blockItem.video.upload": "رفع مباشر",
    "ins.blockItem.video.uploaded": "✓ تم رفع الفيديو",
    "ins.blockItem.video.uploadHint": "اضغط لرفع فيديو (حد 200 MB)",
    "ins.blockItem.video.storageTip": "💡 لتوفير مساحة التخزين، ننصح برفع الفيديوهات الكبيرة على YouTube أو Google Drive ولصق الرابط بدلاً من الرفع المباشر.",
    "ins.blockItem.video.urlLabel": "رابط الفيديو",
    "ins.blockItem.video.durationLabel": "المدة (دقائق)",
    "ins.blockItem.video.autoExtracted": "— مستخرجة تلقائياً",
    "ins.blockItem.video.tooBig": "حجم الملف يتجاوز 200 ميجابايت. يرجى رفعه على YouTube أو Google Drive ولصق الرابط.",
    "ins.blockItem.video.linkUnsupported": "الرابط غير مكتمل أو غير صالح. تأكد من نسخ الرابط الكامل (يبدأ بـ https://) من شريط عنوان المتصفح أو من زر «نسخ الرابط» في YouTube/SharePoint/OneDrive/Drive…",
    "ins.blockItem.video.linkTip": "للملفات على SharePoint/OneDrive، استخدم زر «Copy link» أو «نسخ الرابط» وتأكد أن صلاحية الوصول تسمح بمشاهدته من خارج المؤسسة. وإذا لم يعمل الرابط المنسوخ من مزود الخدمة أو الـ Drive نفسه، جرّب نسخ رابط الـ URL الذي يظهر في شريط العنوان أثناء عرض الفيديو.",
    "ins.blockItem.image.uploaded": "✓ تم رفع الصورة",
    "ins.blockItem.image.uploadHint": "اضغط لرفع صورة",
    "ins.blockItem.image.captionPlaceholder": "وصف الصورة (اختياري)",
    "ins.blockItem.file.tooBig": "حجم الملف يتجاوز 50 ميجابايت. يرجى رفعه على Google Drive ولصق الرابط.",
    "ins.blockItem.file.uploadHint": "اضغط لرفع ملف PDF (حد 50 MB)",
    "ins.blockItem.file.fallbackName": "ملف",
    "ins.blockItem.file.remove": "إزالة",
    "ins.blockItem.file.instructorOnly": "يظهر لك فقط",
    "ins.blockItem.file.titlePlaceholder": "عنوان الملف (يظهر للطالب)",
    "ins.blockItem.file.summaryPlaceholder": "نبذة مختصرة عن الملف...",
    "ins.blockItem.callout.info": "معلومة",
    "ins.blockItem.callout.warning": "تحذير",
    "ins.blockItem.callout.tip": "نصيحة",
    "ins.blockItem.callout.placeholder": "اكتب الملاحظة...",
    "ins.blockItem.live.selectSession": "اختر جلسة مجدولة *",
    "ins.blockItem.live.loading": "جاري التحميل...",
    "ins.blockItem.live.noSessions": "لا توجد جلسات مجدولة. أنشئ جلسة أولاً من صفحة الجلسات.",
    "ins.blockItem.live.selected": "محددة",
    "ins.blockItem.live.completionRule": "قاعدة إكمال الدرس",
    "ins.blockItem.live.rule.session_ended": "بعد انتهاء الموعد",
    "ins.blockItem.live.rule.attended": "عند حضور الطالب",
    "ins.blockItem.live.rule.skippable": "قابل للتخطي",
    "ins.blockItem.quiz.linked": "اختبار مربوط",
    "ins.blockItem.quiz.embedHint": "اختبار مضمّن — احفظ الدرس ثم أنشئ اختبار",
    "ins.blockItem.externalLink.urlLabel": "رابط الصفحة الخارجية *",
    "ins.blockItem.externalLink.titleLabel": "عنوان (اختياري)",
    "ins.blockItem.externalLink.titlePlaceholder": "مثال: مقال مرجعي",
    "ins.blockItem.externalLink.descLabel": "وصف قصير (اختياري)",
    "ins.blockItem.externalLink.descPlaceholder": "اشرح للطالب ما الذي سيجده في هذا الرابط",

    // Block labels (chips on each block in the editor)
    "ins.blockLabel.video": "فيديو",
    "ins.blockLabel.text": "نص",
    "ins.blockLabel.image": "صورة",
    "ins.blockLabel.file": "ملف PDF",
    "ins.blockLabel.divider": "فاصل",
    "ins.blockLabel.callout": "ملاحظة",
    "ins.blockLabel.quiz": "اختبار مضمّن",
    "ins.blockLabel.live_session": "جلسة مباشرة",
    "ins.blockLabel.external_link": "رابط خارجي",
    "ins.blockLabel.audio": "صوت",

    // Audio block editor (instructor)
    "ins.blockItem.audio.linkTab": "رابط صوتي",
    "ins.blockItem.audio.uploadTab": "رفع من الجهاز",
    "ins.blockItem.audio.uploadHint": "اضغط لرفع ملف صوتي (MP3/M4A/WAV/OGG — حد 50 MB)",
    "ins.blockItem.audio.uploadingHint": "جارٍ رفع الصوت...",
    "ins.blockItem.audio.removeAudio": "إزالة الملف الصوتي",
    "ins.blockItem.audio.urlLabel": "رابط الملف الصوتي",
    "ins.blockItem.audio.linkPlaceholder": "ألصق رابط ملف MP3 مباشر أو SoundCloud…",
    "ins.blockItem.audio.titleLabel": "عنوان المقطع",
    "ins.blockItem.audio.titlePlaceholder": "عنوان قصير يظهر فوق المشغل",
    "ins.blockItem.audio.summaryLabel": "وصف مختصر",
    "ins.blockItem.audio.summaryPlaceholder": "وصف مختصر يساعد الطالب على فهم محتوى المقطع",
    "ins.blockItem.audio.tooBig": "الملف يتجاوز الحد الأقصى 50 ميجابايت",
    "ins.blockItem.audio.uploadFailed": "فشل رفع الملف الصوتي، حاول مرة أخرى",

    // Quiz question media (instructor)
    "ins.quizForm.media.section": "وسائط السؤال (اختياري)",
    "ins.quizForm.media.hint": "أضف صوتاً لأسئلة الاستماع، فيديو لأسئلة المشاهدة، أو صورة. يظهر فوق نص السؤال للطالب.",
    "ins.quizForm.media.audio": "صوت",
    "ins.quizForm.media.video": "فيديو",
    "ins.quizForm.media.image": "صورة",
    "ins.quizForm.media.remove": "إزالة الوسائط",
    "ins.quizForm.media.attached": "وسائط مرفقة",
    "ins.quizForm.option.image": "صورة الخيار",
    "ins.quizForm.option.imageRemove": "إزالة الصورة",
    "ins.quizForm.option.imageHint": "اضغط لرفع صورة (اختياري)",
    "ins.quizForm.option.mediaSection": "وسائط الخيار (اختياري)",
    "ins.quizForm.media.upload": "رفع",

    // QuizEditor
    "ins.quizEditor.titlePlaceholder": "عنوان الاختبار",
    "ins.quizEditor.aiGenerate": "توليد بالذكاء",
    "ins.quizEditor.save": "حفظ",
    "ins.quizEditor.saved": "تم الحفظ",
    "ins.quizEditor.saveSummary": "تم حفظ {n} سؤال (مضاف: {added}، محذوف: {deleted}).",
    "ins.quizEditor.saveSummaryNew": "تم إنشاء الاختبار وحفظ {n} سؤال.",
    "ins.quizEditor.saveFailed": "تعذر حفظ الاختبار. حاول مرة أخرى.",
    "ins.quizEditor.titleRequired": "عنوان الاختبار مطلوب.",
    "ins.quizEditor.created": "تم إنشاء الاختبار بنجاح.",
    "ins.quizEditor.proOnly": "الاختبارات متاحة في الخطة المتقدمة فقط.",
    "ins.quizEditor.aiAddedTitle": "تمت الإضافة كمسودة",
    "ins.quizEditor.aiAddedDesc": "تم إضافة {n} أسئلة للمراجعة. اضغط \"حفظ\" بعد المراجعة.",
    "ins.quizEditor.newQuiz": "اختبار جديد",

    // ProductForm
    "ins.productForm.editTitle": "تعديل المنتج",
    "ins.productForm.newTitle": "منتج جديد",
    "ins.productForm.subtitle": "حدد نوع المنتج وتسعيره والمحتوى المشمول",
    "ins.productForm.save": "حفظ",
    "ins.productForm.basicInfo": "المعلومات الأساسية",
    "ins.productForm.productTitle": "عنوان المنتج",
    "ins.productForm.titlePlaceholder": "مثال: دورة تصميم الجرافيك",
    "ins.productForm.desc": "الوصف",
    "ins.productForm.descPlaceholder": "وصف مختصر للمنتج...",
    "ins.productForm.productImage": "صورة المنتج",
    "ins.productForm.clickToUpload": "اضغط لرفع صورة",
    "ins.productForm.remove": "إزالة",
    "ins.productForm.pricing": "التسعير",
    "ins.productForm.productPage": "صفحة المنتج التسويقية",
    "ins.productForm.productPageDesc": "صمم صفحة هبوط مخصصة لمنتجك باستخدام محرر السحب والإفلات",
    "ins.productForm.designPage": "تصميم صفحة المنتج",
    "ins.productForm.refundPolicy": "سياسة الاسترجاع",
    "ins.productForm.noRefund": "بدون استرجاع",
    "ins.productForm.7days": "7 أيام",
    "ins.productForm.14days": "14 يوم",
    "ins.productForm.30days": "30 يوم",
    "ins.productForm.titleRequired": "يرجى إدخال عنوان المنتج.",
    "ins.productForm.savedEdit": "تم تحديث المنتج بنجاح.",
    "ins.productForm.savedNew": "تم إنشاء المنتج بنجاح.",
    "ins.productForm.stripeBanner": "منتجك سيُحفظ كمسودة",
    "ins.productForm.stripeBannerDesc": "لنشر المنتج وإتاحته للطلاب، وثّق حسابك لاستلام المدفوعات أولاً.",
    "ins.productForm.verifyAccount": "وثّق حسابك",

    // SessionDetail
    "ins.sessionDetail.startStream": "ابدأ البث",
    "ins.sessionDetail.endSession": "إنهاء الجلسة",
    "ins.sessionDetail.edit": "تعديل",
    "ins.sessionDetail.editLandingPage": "تحرير صفحة الهبوط",
    "ins.sessionDetail.notFound": "لم يتم العثور على الجلسة",
    "ins.sessionDetail.backToSessions": "العودة للجلسات",
    "ins.sessionDetail.copyLink": "نسخ رابط الجلسة",
    "ins.sessionDetail.copied": "تم النسخ",
    "ins.sessionDetail.meetingRoom": "غرفة الاجتماع",
    "ins.sessionDetail.roomId": "معرف الغرفة:",
    "ins.sessionDetail.roomSecurity": "الغرفة مؤمنة بـ JWT — المدرب moderator والطالب participant",
    "ins.sessionDetail.inviteSubscribers": "دعوة المشتركين",
    "ins.sessionDetail.inviteToBuy": "دعوة للشراء",
    "ins.sessionDetail.grantFreeAccess": "منح وصول مجاني",
    "ins.sessionDetail.stats": "الإحصائيات",
    "ins.sessionDetail.bookings": "الحجوزات (مؤكدة)",
    "ins.sessionDetail.actualAttendance": "الحضور الفعلي",
    "ins.sessionDetail.type": "النوع",
    "ins.sessionDetail.status": "الحالة",
    "ins.sessionDetail.privateType": "خاصة",
    "ins.sessionDetail.groupType": "جماعية",
    "ins.sessionDetail.statusScheduled": "مجدولة",
    "ins.sessionDetail.statusLive": "مباشرة",
    "ins.sessionDetail.statusCompleted": "منتهية",
    "ins.sessionDetail.statusCancelled": "ملغاة",
    "ins.sessionDetail.statusChanged": "تم تغيير الحالة إلى {status}",
    "ins.sessionDetail.recordingUploaded": "تم رفع التسجيل",
    "ins.sessionDetail.linkCopied": "تم نسخ الرابط",

    // StudentDetail
    "ins.studentDetail.chat": "محادثة",
    "ins.studentDetail.enrollInCourse": "تسجيل في دورة",
    "ins.studentDetail.suspend": "تعليق",
    "ins.studentDetail.activate": "تفعيل",
    "ins.studentDetail.ban": "حظر",
    "ins.studentDetail.unban": "إلغاء الحظر",
    "ins.studentDetail.activated": "تم التفعيل",
    "ins.studentDetail.suspended": "تم التعليق",
    "ins.studentDetail.banned": "تم الحظر",
    "ins.studentDetail.tabOverview": "نظرة عامة",
    "ins.studentDetail.tabCourses": "الدورات",
    "ins.studentDetail.tabQuizzes": "الاختبارات",
    "ins.studentDetail.tabPayments": "المدفوعات",
    "ins.studentDetail.tabCertificates": "الشهادات",
    "ins.studentDetail.tabComments": "التعليقات",
    "ins.studentDetail.issueManually": "إصدار شهادة يدوياً",
    "ins.studentDetail.noCertificates": "لا يوجد شهادات صادرة",
    "ins.studentDetail.issued": "صدرت",
    "ins.studentDetail.download": "تحميل",
    "ins.studentDetail.certNumber": "رقم:",
    "ins.studentDetail.certError": "فشل إصدار الشهادة",
    "ins.studentDetail.certIssued": "تم إصدار الشهادة",
    "ins.studentDetail.certIssuedDesc": "تم توليد الشهادة بنجاح.",
    "ins.studentDetail.certExists": "الشهادة موجودة مسبقاً",
    "ins.studentDetail.certExistsDesc": "تم إصدار هذه الشهادة من قبل.",

    // Onboarding
    "ins.onboarding.setupTitle": "إعداد أكاديميتك",
    "ins.onboarding.guideTitle": "دليل إعداد أكاديميتك",
    "ins.onboarding.dashboard": "لوحة التحكم",
    "ins.onboarding.stepOf": "الخطوة {step} من {total}",
    "ins.onboarding.guideHint": "يمكنك مراجعة خطوات الإعداد في أي وقت من هنا",
    "ins.onboarding.loadError": "خطأ في تحميل البيانات",
    "ins.onboarding.completeError": "تعذر إكمال الإعداد. حاول مرة أخرى.",

    // Branding
    "ins.branding.title": "الهوية البصرية",
    "ins.branding.desc": "خصّص مظهر أكاديميتك العام",
    "ins.branding.saveChanges": "حفظ التغييرات",
    "ins.branding.saved": "تم تحديث الهوية البصرية بنجاح.",

    // TemplateSelection
    "ins.templates.title": "القوالب",
    "ins.templates.desc": "اختر القالب المناسب لأكاديميتك — يمكنك تغييره في أي وقت",
    "ins.templates.updated": "تم تغيير قالب الأكاديمية بنجاح.",

    // QuestionBank
    "ins.questionBank.title": "بنك الأسئلة",
    "ins.questionBank.desc": "أنشئ أسئلة واستخدمها في اختبارات دوراتك — {n} سؤال",
    "ins.questionBank.proOnly": "بنك الأسئلة متاح في الخطة المتقدمة فقط.",
    "ins.questionBank.searchPlaceholder": "بحث في الأسئلة...",
    "ins.questionBank.allTypes": "كل الأنواع",
    "ins.questionBank.allCourses": "كل الدورات",
    "ins.questionBank.noMatch": "لا توجد أسئلة مطابقة",
    "ins.questionBank.copy": "نسخ",
    "ins.questionBank.copied": "تم النسخ",
    "ins.questionBank.copiedDesc": "تم نسخ السؤال إلى \"{quiz}\"",
    "ins.questionBank.point": "نقطة",
    "ins.questionBank.multipleChoice": "اختيار من متعدد",
    "ins.questionBank.multiSelect": "اختيار متعدد",
    "ins.questionBank.trueFalse": "صح وخطأ",
    "ins.questionBank.matching": "توصيل",
    "ins.questionBank.fillBlank": "ملء الفراغ",
    "ins.questionBank.ordering": "ترتيب",

    // EmailTemplateEditor
    "ins.emailEditor.active": "مفعّل",
    "ins.emailEditor.save": "حفظ",
    "ins.emailEditor.emailSubject": "عنوان الإيميل",
    "ins.emailEditor.emailSubjectPlaceholder": "عنوان الإيميل...",
    "ins.emailEditor.content": "المحتوى",
    "ins.emailEditor.recipientScope": "نطاق المستلمين",
    "ins.emailEditor.triggerTargetOnly": "الطالب المعني فقط",
    "ins.emailEditor.courseStudents": "طلاب دورة محددة",
    "ins.emailEditor.allStudents": "جميع الطلاب",
    "ins.emailEditor.targetCourse": "الدورة المستهدفة",
    "ins.emailEditor.welcome": "ترحيب بطالب جديد",
    "ins.emailEditor.enrollment": "تسجيل في دورة",
    "ins.emailEditor.courseCompleted": "إكمال دورة",
    "ins.emailEditor.quizPassed": "نجاح في اختبار",
    "ins.emailEditor.quizFailed": "رسوب في اختبار",
    "ins.emailEditor.certificateIssued": "صدور شهادة",
    "ins.emailEditor.paymentReceipt": "إيصال دفع",
    "ins.emailEditor.paymentFailed": "فشل دفع",
    "ins.emailEditor.sessionReminder": "تذكير بجلسة",
    "ins.emailEditor.refundProcessed": "استرجاع",
    "ins.emailEditor.inactivityReminder": "تذكير خمول",

    // Common page messages
    // Language
    "lang.switch": "EN",
    "lang.current": "العربية",

    // ===== Learning Center (instructor + admin) =====
    "learning.center.title": "مركز التعلم",
    "learning.center.subtitle": "أدلة ومقالات تعليمية لإتقان منصة دورة",
    "learning.center.searchPlaceholder": "ابحث في المركز...",
    "learning.center.pinned": "مقالات مثبتة",
    "learning.center.categories": "التصنيفات",
    "learning.center.empty": "لا يوجد محتوى تعليمي بعد",
    "learning.center.emptyCat": "لا توجد مقالات في هذا التصنيف بعد",
    "learning.center.notFound": "التصنيف غير موجود",
    "learning.center.articleNotFound": "المقال غير موجود",
    "learning.center.backToHome": "مركز التعلم",
    "learning.center.related": "مقالات ذات صلة",
    "learning.center.article": "مقال",
    "learning.center.articles": "مقالات",
    // Admin · articles
    "learning.art.search": "ابحث في المقالات...",
    "learning.art.allCats": "كل التصنيفات",
    "learning.art.allStatuses": "كل الحالات",
    "learning.art.new": "مقال جديد",
    "learning.art.newTitle": "مقال جديد",
    "learning.art.empty": "لا توجد مقالات تعليمية بعد",
    "learning.art.noCategory": "بلا تصنيف",
    "learning.art.deleteTitle": "حذف المقال؟",
    "learning.art.deleteDesc": "لا يمكن التراجع عن هذا الإجراء.",
    // Admin · editor
    "learning.editor.needsArabic": "اكتب المحتوى العربي أولاً",
    "learning.editor.translated": "تمت الترجمة — لا تنسَ الحفظ",
    "learning.editor.translateFailed": "فشلت الترجمة",
    "learning.editor.confirmRemoveEn": "إزالة النسخة الإنجليزية؟",
    "learning.editor.enRemoved": "أُزيلت — احفظ لتأكيد التغيير",
    "learning.editor.titleRequired": "العنوان العربي مطلوب",
    "learning.editor.source": "المصدر",
    "learning.editor.removeEn": "إزالة الإنجليزية",
    "learning.editor.translateEn": "ترجم للإنجليزية",
    "learning.editor.title": "العنوان",
    "learning.editor.excerpt": "المقتطف",
    "learning.editor.body": "محتوى المقال",
    "learning.editor.aiNotice": "هذه نسخة مترجمة بالذكاء الاصطناعي — راجعها وعدّلها قبل النشر.",
    "learning.editor.video": "الفيديو (اختياري)",
    "learning.editor.embedUrl": "رابط فيديو خارجي (YouTube/Vimeo/Loom)",
    "learning.editor.or": "أو",
    "learning.editor.uploadVideo": "ارفع فيديو (mp4/webm، حتى 500MB)",
    "learning.editor.chooseVideo": "اختر فيديو",
    "learning.editor.preview": "معاينة",
    "learning.editor.publish": "النشر",
    "learning.editor.category": "التصنيف",
    "learning.editor.noCategory": "بلا تصنيف",
    "learning.editor.status": "الحالة",
    "learning.editor.pinned": "تثبيت",
    "learning.editor.slug": "الرابط (slug)",
    "learning.editor.sortOrder": "الترتيب",
    "learning.editor.cover": "صورة الغلاف",
    "learning.editor.uploadCover": "ارفع صورة",
    "learning.editor.tags": "الوسوم",
    "learning.editor.noTags": "لا توجد وسوم بعد",
    "learning.editor.imageTooLarge": "الصورة أكبر من 5MB",
    "learning.editor.videoBadType": "نوع الفيديو غير مدعوم (mp4/webm فقط)",
    "learning.editor.videoTooLarge": "حجم الفيديو أكبر من 500MB",
    // Admin · categories
    "learning.cat.confirmDelete": "حذف التصنيف؟ سيتم فك ربط المقالات.",
    "learning.cat.nameAr": "الاسم (عربي)",
    "learning.cat.nameEn": "Name (EN)",
    "learning.cat.placeholderAr": "مثلاً: الدورات",
    "learning.cat.icon": "أيقونة (lucide)",
    "learning.cat.add": "أضف تصنيف",
    // Admin · tags
    "learning.tag.confirmDelete": "حذف الوسم؟",
    "learning.tag.nameAr": "الاسم (عربي)",
    "learning.tag.nameEn": "Name (EN)",
    "learning.tag.placeholderAr": "مثلاً: مبتدئ",
    "learning.tag.add": "أضف وسم",
    // Admin · settings
    "learning.settings.title": "إعدادات مركز التعلم",
    "learning.settings.toggleLabel": "إظهار مركز التعلم للمدربين",
    "learning.settings.toggleDesc": "عند التفعيل، يظهر رابط مركز التعلم في القائمة الجانبية لجميع المدربين. عند التعطيل، يُخفى تماماً.",
    "learning.settings.enabled": "تم التفعيل",
    "learning.settings.disabled": "تم التعطيل",
  },

  en: {
    // Common
    "common.loading": "Loading...",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.search": "Search",
    "common.searchPlaceholder": "Search...",
    "common.filter": "Filter",
    "common.actions": "Actions",
    "common.back": "Back",
    "common.next": "Next",
    "common.prev": "Previous",
    "common.retry": "Retry",
    "common.close": "Close",
    "common.confirm": "Confirm",
    "common.yes": "Yes",
    "common.no": "No",
    "common.or": "or",
    "common.email": "Email",
    "common.password": "Password",
    "common.name": "Name",
    "common.submit": "Submit",
    "common.logout": "Logout",
    "common.login": "Login",
    "common.signup": "Sign Up",
    "common.menu": "Menu",
    "common.noData": "No data",
    "common.viewAll": "View All",
    "common.view": "View",
    "common.share": "Share",
    "common.shareCopied": "Share link copied",
    "common.shareCopyFailed": "Couldn't copy. Please copy the link manually.",
    "ins.seo.shareLinkHint": "Optional quick-share link you can copy and paste anywhere. Once the Cloudflare Worker is enabled on dwrrah.com (see docs/CLOUDFLARE_SOCIAL_PROXY_SETUP.md), every direct academy URL will automatically show your logo and details in social previews.",
    "common.status": "Status",
    "common.date": "Date",
    "common.type": "Type",
    "common.amount": "Amount",
    "common.all": "All",
    "common.active": "Active",
    "common.free": "Free",
    "common.error": "An error occurred",
    "common.saved": "Saved",
    "common.saveError": "Error saving",
    "common.show": "Show",
    "common.hide": "Hide",
    "common.settings": "Settings",
    "common.notifications": "Notifications",
    "common.send": "Send",
    "common.publish": "Publish",
    "common.unpublish": "Unpublish",
    "common.create": "Create",
    "common.update": "Update",
    "common.currency": "Currency",
    "common.description": "Description",
    "common.title": "Title",
    "common.image": "Image",
    "common.days": "days",
    "common.daily": "Daily",
    "common.weekly": "Weekly",
    "common.monthly": "Monthly",
    "common.quarterly": "Quarterly",
    "common.annual": "Annual",
    "common.lessons": "lessons",
    "common.minutes": "min",
    "common.owner": "Owner",
    "common.manager": "Manager",
    "common.enterArrow": "Enter →",
    "common.enter": "Enter",
    "common.buyNow": "Buy Now",
    "common.getFree": "Get Free",
    "common.owned": "Owned",
    "common.paid": "Paid",
    "common.refunded": "Refunded",
    "common.expires": "Expires",
    "common.course": "Course",
    "common.courses": "Courses",
    "common.product": "Product",
    "common.products": "Products",
    "common.session": "Session",
    "common.sessions": "Sessions",
    "common.certificate": "Certificate",
    "common.certificates": "Certificates",
    "common.student": "Student",
    "common.students": "Students",
    "common.payments": "Payments",
    "common.allRightsReserved": "All rights reserved",
    "common.termsOfService": "Terms of Service",
    "common.privacyPolicy": "Privacy Policy",
    "common.subscribeNow": "Subscribe Now",
    "common.startNow": "Start Now",
    "common.bookNow": "Book Now",
    "common.joinAcademy": "Join Academy",
    "common.registration": "Registration",

    // Auth
    "auth.loginTitle": "Log In",
    "auth.loginSubtitle": "Log in to access your dashboard",
    "auth.signupTitle": "Create Your Account",
    "auth.signupSubtitle": "Sign up and start your free trial",
    "auth.fullName": "Full Name",
    "auth.forgotPassword": "Forgot password?",
    "auth.forgotHint": "Enter your email above first",
    "auth.noAccount": "Don't have an account?",
    "auth.haveAccount": "Already have an account?",
    "auth.signupNow": "Sign up now",
    "auth.loginNow": "Log in",
    "auth.orEmail": "or with email",
    "auth.googleLogin": "Log in with Google",
    "auth.appleLogin": "Log in with Apple",
    "auth.googleSignup": "Sign up with Google",
    "auth.appleSignup": "Sign up with Apple",
    "auth.loggingIn": "Logging in...",
    "auth.signingUp": "Creating account...",
    "auth.signingIn": "Signing in...",
    "auth.createAccount": "Create Account",
    "auth.loginError": "Login Error",
    "auth.signupError": "Signup Error",
    "auth.invalidCredentials": "Invalid email or password",
    "auth.error": "Error",
    "auth.googleFail": "Google login failed",
    "auth.appleFail": "Apple login failed",
    "auth.googleSignupFail": "Google signup failed",
    "auth.appleSignupFail": "Apple signup failed",
    "auth.passwordMin": "Password must be at least 6 characters",
    "auth.resetSent": "Password reset link sent to your email",
    "auth.enterEmailFirst": "Enter your email first",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.confirmPassword": "Confirm password",
    "auth.or": "or",
    "auth.loginButton": "Log in",
    "auth.signupButton": "Create account",
    "auth.passwordMismatch": "Passwords do not match",
    "auth.passwordTooShort": "Password must be at least 6 characters",
    "auth.signupCheckEmail": "Check your inbox",
    "auth.signupCheckEmailDesc": "We sent you a confirmation link. Open it to finish creating your account.",
    "auth.emailAlreadyRegistered": "This email is already registered. Please log in instead.",
    "auth.signupFailed": "Could not create account. Check your details and try again.",
    "auth.forgotTitle": "Reset your password",
    "auth.forgotSubtitle": "Enter your email and we'll send you a verification code",
    "auth.sendResetLink": "Send verification code",
    "auth.resetSentGeneric": "If this email is registered, a verification code has been sent.",
    "auth.otp.enterCode": "Enter the verification code sent to your email",
    "auth.otp.verifyButton": "Verify code",
    "auth.otp.resend": "Resend code",
    "auth.otp.resendIn": "You can resend in {s}s",
    "auth.otp.checkSpam": "Check your spam folder. Only the latest code is valid.",
    "auth.otp.invalid": "Invalid or expired code. Please try again.",
   "auth.otp.latestIsValid": "Only the most recently sent code is valid.",
   "auth.admin.loginAfterReset": "Password updated. Please sign in again.",
   "auth.admin.forgotTitle": "Admin password recovery",
    "auth.newPassword": "New password",
    "auth.savePassword": "Save password",
    "auth.passwordChanged": "Password changed successfully. Please sign in.",
    "auth.backToLogin": "Back to login",
    "auth.emailNotRegistered": "This email is not registered, please check the email address",
    "auth.checkEmail": "Check Your Email",
    "auth.confirmSent": "We sent a confirmation link to",
    "auth.confirmAction": "Please open your email and click the link to activate your account.",
    "auth.backToLogin": "Back to Login",
    "auth.resendVerification": "Resend verification link",
    "auth.resendIn": "You can resend in {s}s",
    "auth.resendHint": "Check your Spam folder if you don't see the email.",
    "auth.resendSuccess": "A new verification link was sent to your email",
    "auth.resendError": "Could not resend verification link",
    "auth.resendRateLimit": "Too many requests, try again shortly",
    "auth.emailNotConfirmed": "Your email isn't verified yet — we sent you a new verification link",
    "auth.err.weakPassword": "This password is too weak and easy to guess. Pick a stronger one (e.g. mix upper/lowercase, numbers, symbols, 8+ characters).",
    "auth.err.passwordShort": "Password is too short. Use at least 6 characters.",
    "auth.err.emailNotConfirmed": "Your email isn't verified yet. Please confirm your email first.",
    "auth.err.userExists": "This email is already registered. Try signing in or resetting your password.",
    "auth.alreadyRegistered.title": "This email is already registered",
    "auth.alreadyRegistered.body": "Please sign in to your existing account, or reset your password if you forgot it.",
    "auth.err.rateLimit": "Too many attempts. Please wait a moment and try again.",
    "auth.err.invalidEmail": "The email address format is invalid.",
    "auth.err.signupDisabled": "Signups are currently closed. Please try again later.",
    "auth.err.network": "Couldn't reach the server. Check your connection and try again.",
    "auth.err.generic": "Something went wrong. Please try again.",
    "auth.otp.sentTo": "We sent a 6-digit verification code to",
    "auth.otp.enterBelow": "Enter the code below to activate your account.",
    "auth.otp.verify": "Verify and activate account",
    "auth.otp.invalidLength": "Enter the 6-digit verification code.",
    "auth.otp.success": "Your account has been activated",
    "auth.otp.error": "Invalid or expired verification code",
    "auth.terms": "Terms of Service",
    "auth.privacy": "Privacy Policy",
    "auth.agreeTerms": "By signing up you agree to the",
    "auth.and": "and",

    // Phone & profile completion
    "auth.phoneLabel": "Phone Number",
    "auth.phoneRequired": "Please enter your phone number",
    "auth.phoneInvalid": "Invalid phone number",
    "auth.phonePlaceholder": "5XXXXXXXX",
    "auth.completeProfile.title": "Welcome, {name} 👋",
    "auth.completeProfile.subtitle": "One last step! We need your phone number to contact you about your orders.",
    "auth.completeProfile.cta": "Continue",
    "auth.completeProfile.saving": "Saving...",
    "auth.completeProfile.skip": "Skip for now",
    "auth.completeProfile.confirmExisting": "Looks good, use this number",
    "auth.completeProfile.editPhone": "Edit number",
    "auth.completeProfile.savedToast": "Profile saved",

    // Signup benefits
    "signup.benefit1": "14-day free trial",
    "signup.benefit2": "No credit card required",
    "signup.benefit3": "Setup in 5 minutes",
    "signup.benefit4": "Full Arabic support",
    "signup.passwordPlaceholder": "At least 6 characters",
    "signup.namePlaceholder": "John Doe",

    // Landing / Public
    "nav.home": "Home",
    "nav.features": "Features",
    "nav.howItWorks": "How It Works",
    "nav.pricing": "Pricing",
    "nav.contact": "Contact Us",
    "nav.startFree": "Start Free",

    // Footer
    "footer.quickLinks": "Quick Links",
    "footer.pricing": "Pricing",
    "footer.createAccount": "Create Account",
    "footer.contactTitle": "Contact",
    "footer.legal": "Legal",
    "footer.product": "Product",
    "footer.support": "Support",
    "footer.startFree": "Start Free",
    "footer.faq": "FAQ",
    "footer.loginLink": "Login",
    "footer.madeWith": "Made with 💜 for Arab instructors",
    "footer.developedByCenter": "Developed by Saasmain ©",
    "footer.description": "An all-in-one platform that empowers instructors to build their digital academies and sell courses professionally.",
    "footer.rights": "All rights reserved.",
    "footer.madeByDwrrah": "Made with love by Dwrrah 💜",
    "footer.trust.companyTitle": "Company Information",
    "footer.trust.developedBy": "Saasmain LLC",
    "footer.trust.licensedIn": "Registered and licensed in the United Arab Emirates",
    "footer.trust.licenseNo": "Commercial License No.: 2646101.01",
    "footer.trust.address": "United Arab Emirates - Sharjah - Sharjah Media City",
    "footer.trust.regionTitle": "Our Regional Presence",
    "footer.trust.regionLine": "An Arabic platform serving instructors and content creators across Saudi Arabia, the Gulf and the Arab world",
    "footer.trust.securePayments": "Secure payments via a trusted provider",
    "footer.trust.verifyTitle": "License Verification",
    "footer.trust.licensedBy": "Licensed by",
    "footer.trust.scanToVerify": "Scan to verify",
    "settings.attribution.title": "Dwrrah Branding",
    "settings.attribution.toggle": "Show \"Made with love by Dwrrah\" in footer",
    "settings.attribution.lockedHint": "Upgrade to Pro to remove the \"Made with love by Dwrrah\" attribution",
    "settings.attribution.upgrade": "Upgrade now",

    // Instructor sidebar & general
    "ins.dashboard": "Dashboard",
    "ins.courses": "Courses",
    "ins.questionBank": "Question Bank",
    "ins.sessions": "Sessions",
    "ins.students": "Students",
    "ins.messages": "Messages",
    "ins.comments": "Comments",
    "ins.autoEmail": "Auto Email",
    "ins.payments": "Payments",
    "ins.wallet": "Wallet",
    "ins.branding": "Branding",
    "ins.pages": "Public Pages",
    "ins.products": "Products",
    "ins.coupons": "Coupons",
    "ins.certificates": "Certificates",
    "ins.settings": "Settings",
    "ins.plan": "Plan",
    "ins.planPro": "Pro",
    "ins.planBasic": "Basic",
    "ins.profile": "Profile",
    "ins.switchAcademies": "Switch Academy",
    "ins.guide": "Guide",
    "ins.myAcademy": "My Academy",
    "ins.platformLink": "Dwrrah Platform",
    "ins.overview": "Overview",
    "ins.quizzes": "Quizzes",
    "ins.createCourse": "Create Course",
    "ins.createSession": "Create Session",
    "ins.createProduct": "Add Product",
    "ins.createCoupon": "Create Coupon",
    "ins.searchStudents": "Search students...",
    "ins.searchCoupons": "Search by code or title...",
    "ins.enrollments": "Enrollments",
    "ins.revenue": "Revenue",
    "ins.content": "Content",
    "ins.engagement": "Engagement",
    "ins.academyName": "Academy Name",
    "ins.contactEmail": "Contact Email",
    "ins.primaryColor": "Primary Color",
    "ins.secondaryColor": "Secondary Color",
    "ins.currencyTax": "Currency & Tax",
    "ins.billing": "Billing",
    "ins.integrations": "Integrations",
    "ins.socialFooter": "Social & Footer",
    "ins.notificationsSettings": "Notifications",
    "ins.subscription": "Subscription",
    "ins.upgradeBanner.title": "💡 Smart, flexible upgrades",
    "ins.upgradeBanner.line1": "Monthly → annual: you only pay the price difference, prorated by remaining days.",
    "ins.upgradeBanner.line2": "Basic → Pro: we credit what you've already paid and deduct it automatically.",
    "ins.upgradeBanner.line3": "Upgrade applies instantly on payment — your renewal date stays the same.",
    "ins.manualPlanNotice.title": "Your plan was activated manually by admin",
    "ins.manualPlanNotice.desc": "There's no active paid subscription linked to your account, so upgrades will be charged at the full price of the new plan with no proration.",
    "ins.upgradeToast.fullPrice.title": "Full-price checkout",
    "ins.upgradeToast.fullPrice.desc": "No active paid subscription found, so you'll be redirected to pay the full price for the new plan.",
    "ins.dataExport": "Data Export",
    "ins.domains": "Custom Domain",
    "ins.teamManagement": "Team Management",
    "ins.emailLogs": "Email Logs",
    "ins.emailCampaign": "Email Campaign",
    "ins.notificationHistory": "Notification History",
    "ins.upcomingSessions": "Upcoming Sessions",
    "ins.weeklyDigest": "Weekly Digest",

    // Instructor pages
    "ins.pages.landing": "Landing",
    "ins.pages.about": "About Us",
    "ins.pages.terms": "Terms & Conditions",
    "ins.pages.privacy": "Privacy Policy",
    "ins.pages.courses": "Courses",
    "ins.pages.sessions": "Sessions",
    "ins.pages.custom": "Custom",
    "ins.pages.homepage": "Homepage",

    // Instructor course
    "ins.course.settings": "Course Settings",
    "ins.course.create": "Create Course",
    "ins.course.published": "Published",
    "ins.course.draft": "Draft",
    "ins.course.completionOnly": "Completion only",
    "ins.course.freeMovement": "Free navigation",
    "ins.course.quizNoBlock": "Quiz does not block progress",
    "ins.course.dripContent": "Drip Content",
    "ins.course.sequential": "Sequential",
    "ins.course.certEnabled": "Completion Certificate",
    "ins.course.discussion": "Discussions",
    "ins.course.comments": "Comments",
    "ins.course.chooseYourCourse": "Choose the right course and start your learning journey",
    "ins.course.availableCourses": "Available Courses",
    "ins.course.productsGrantAccess": "Products that grant access to this course",
    "ins.course.contentOnly": "Courses, content, and quizzes only",

    // Instructor product
    "ins.product.bundle": "Bundle",
    "ins.product.membership": "Membership",
    "ins.product.sessionPackage": "Session Package",
    "ins.product.singleSession": "Private Session",
    "ins.product.subscription": "Recurring Subscription",
    "ins.product.subscriptionDesc": "Recurring subscription for accessing specific content",
    "ins.product.pricing": "Pricing",
    "ins.product.access": "Courses & sessions included in this product",
    "ins.product.specificTypes": "Specific types",
    "ins.product.allCourses": "All Courses",
    "ins.product.allSessions": "All Sessions",
    "ins.product.bundles": "Bundles",
    "ins.product.memberships": "Memberships",
    "ins.product.sessionPackages": "Session Packages",
    "ins.product.privateSession": "Private Session",

    // Instructor sessions
    "ins.session.group": "Group",
    "ins.session.private": "Private",
    "ins.session.liveNow": "Session is live now",
    "ins.session.liveSessions": "Live Sessions",
    "ins.session.liveAndRecording": "Live + Recording",
    "ins.session.liveOnly": "Live viewing only",
    "ins.session.bookings": "Bookings",
    "ins.session.cancelSession": "Cancel Session",
    "ins.session.bookSession": "Book Session",
    "ins.session.sendInvites": "Send invites to all enrolled students",
    "ins.session.createFirst": "Create your first online session",
    "ins.session.createWithoutInvites": "Create session without invites",

    // Instructor quiz
    "ins.quiz.title": "Quiz",
    "ins.quiz.finalExam": "Final Exam",
    "ins.quiz.newQuiz": "New Quiz",
    "ins.quiz.multipleChoice": "Multiple Choice",
    "ins.quiz.singleChoice": "Single Choice",
    "ins.quiz.multiSelect": "Multi-Select",
    "ins.quiz.matching": "Matching",
    "ins.quiz.ordering": "Ordering",
    "ins.quiz.fillBlank": "Fill in the Blank",
    "ins.quiz.question": "Question",
    "ins.quiz.answer": "Answer",
    "ins.quiz.options": "Options",
    "ins.quiz.acceptedAnswers": "Accepted Answers",
    "ins.quiz.addOption": "Add Option",
    "ins.quiz.addAltAnswer": "Add Alternative Answer",
    "ins.quiz.grade": "Grade",
    "ins.quiz.items": "Items (in correct order)",
    "ins.quiz.writeQuestion": "Write the question here...",
    "ins.quiz.linkedQuiz": "Linked Quiz",
    "ins.quiz.inlineQuiz": "Inline Quiz",
    "ins.quiz.inlineHint": "Inline quiz — save the lesson then create a quiz",
    "ins.quiz.quickQuiz": "Quick quiz with two default questions",
    "ins.quiz.proOnly": "Quizzes are available in the Pro plan only.",

    // Instructor wallet
    "ins.wallet.deposit": "Deposit",
    "ins.wallet.adminDeposit": "Manual deposit (admin)",
    "ins.wallet.refund": "Refund",
    "ins.wallet.studentPurchase": "Student Purchase",
    "ins.wallet.paymentFailed": "Payment Failed",
    "ins.wallet.paymentReceipt": "Payment Receipt",

    // Instructor onboarding
    "ins.onboard.setupAcademy": "Set up your academy",
    "ins.onboard.createFirstCourse": "Create your first course",
    "ins.onboard.createFirstSession": "Create your first online session",
    "ins.onboard.createLanding": "Create landing page",
    "ins.onboard.createFirstProduct": "Create your first product",
    "ins.onboard.chooseSlug": "Choose a unique URL for your academy",
    "ins.onboard.launch": "Launch!",
    "ins.onboard.firstCourse": "First Course",
    "ins.onboard.firstSession": "First Session",
    "ins.onboard.firstProduct": "First Product",
    "ins.onboard.slugUsed": "URL already taken",
    "ins.onboard.slugMinLength": "URL must be at least 3 characters",
    "ins.onboard.chooseAnotherSlug": "Choose another URL",

    // Instructor dashboard
    "ins.dash.dashboardTitle": "Dashboard",
    "ins.dash.healthScore": "Health Score",
    "ins.dash.quickActions": "Quick Actions",
    "ins.dash.coursePerformance": "Course Performance",
    "ins.dash.enrollmentRevenue": "Enrollments & Revenue",
    "ins.dash.operational": "Live Sessions & Operations",
    "ins.dash.walletSummary": "Wallet Summary",
    "ins.dash.viewAll": "View All",
    "ins.dash.wallet": "Wallet",
    "ins.dash.topCourses": "Top Courses",
    "ins.dash.noCourses": "No courses yet",
    "ins.dash.course": "Course",
    "ins.dash.enrollments": "Enrollments",
    "ins.dash.completion": "Completion",
    "ins.dash.revenue": "Revenue",
    "ins.dash.revenueWith": "Revenue ({currency})",
    "ins.dash.academyHealth": "Academy Health",
    "ins.dash.outOf100": "out of 100",
    "ins.dash.content": "Content",
    "ins.dash.students": "Students",
    "ins.dash.engagement": "Engagement",
    "ins.dash.recentEnrollments": "Recent Enrollments",
    "ins.dash.recentPayments": "Recent Payments",
    "ins.dash.noRecentEnrollments": "No recent enrollments",
    "ins.dash.noRecentPayments": "No recent payments",

    // Instructor branding
    "ins.brand.coverImage": "The cover that sells your courses",
    "ins.brand.imageUpdated": "Image updated",
    "ins.brand.showPreview": "Show Preview",
    "ins.brand.hidePreview": "Hide Preview",

    // Instructor email triggers
    "ins.email.enrollment": "Enrollment Confirmation",
    "ins.email.purchaseConfirm": "Purchase Confirmation",
    "ins.email.courseComplete": "Course Completion",
    "ins.email.certificateIssued": "Certificate Issued",
    "ins.email.sessionReminder": "Session Reminder",
    "ins.email.sessionCancel": "Session Cancellation",
    "ins.email.paymentReceipt": "Payment Receipt",
    "ins.email.welcomeStudent": "Welcome New Student",
    "ins.email.inactivityReminder": "Inactivity Reminder",
    "ins.email.weeklyDigest": "Weekly Digest",
    "ins.email.onEnroll": "On course enrollment",
    "ins.email.onComplete": "On course completion",
    "ins.email.afterPayment": "After every payment",
    "ins.email.writeContent": "Write email content...",
    "ins.email.studentEmail": "Student Email",
    "ins.email.courseStudents": "Students enrolled in linked course",
    "ins.email.specificStudent": "Specific student only",
    "ins.email.productStudents": "Students who purchased a specific product",

    // Instructor messaging
    "ins.msg.newMessage": "New Message",
    "ins.msg.writeReply": "Write your reply...",
    "ins.msg.writeGroupMsg": "Write a message to the group...",
    "ins.msg.writeMessageContent": "Write message content...",
    "ins.msg.selectStudents": "Select specific students from the list",
    "ins.msg.selectOneStudent": "Select at least one student",
    "ins.msg.manualSelect": "Manual Selection",

    // Instructor students
    "ins.stu.overview": "Overview",
    "ins.stu.lastLogin": "Last Login",
    "ins.stu.enrolledInCourse": "Student is already enrolled in this course.",
    "ins.stu.writeNote": "Write a note...",
    "ins.stu.aboutYou": "Tell us about yourself...",
    "ins.stu.validEmail": "Enter a valid email",

    // Instructor settings
    "ins.set.general": "Settings",
    "ins.set.resubscribe": "Resubscribe",
    "ins.set.nextRenewal": "Next Renewal",
    "ins.set.inviteTeam": "Invite Member",
    "ins.set.position": "Position (optional)",
    "ins.set.googleCalConnected": "Google Calendar connected successfully ✅",
    "ins.set.googleCalDisconnected": "Google Calendar disconnected",
    "ins.set.domainSuccess": "Custom domain activated successfully.",
    "ins.set.titleRequired": "Title is required",

    // Instructor coupon
    "ins.coupon.searchPlaceholder": "Search by code or title...",

    // Instructor certificate
    "ins.cert.defaultTemplate": "Default Template",
    "ins.cert.signature": "Signature",
    "ins.cert.alreadyExists": "Certificate already exists",
    "ins.cert.issuedSuccess": "Completion certificate issued successfully.",
    "ins.cert.getCertForEachCourse": "Get a completion certificate for each course",

    // Instructor page editor
    "ins.editor.hero": "Hero",
    "ins.editor.mainTitle": "Main Title",
    "ins.editor.subtitle": "Subtitle",
    "ins.editor.text": "Text",
    "ins.editor.icon": "Icon",
    "ins.editor.term": "Term",
    "ins.editor.definition": "Definition",
    "ins.editor.features": "Features",
    "ins.editor.testimonials": "Testimonials",
    "ins.editor.faq": "FAQ (Collapsible Q&A)",
    "ins.editor.addSection": "Add Section",
    "ins.editor.addItem": "Add Item",
    "ins.editor.headerCourses": "Courses",
    "ins.editor.headerProducts": "Products",
    "ins.editor.headerSessions": "Sessions",

    // Instructor data export
    "ins.export.students": "Students",
    "ins.export.enrollments": "Enrollments",
    "ins.export.courses": "Courses",
    "ins.export.payments": "Payments",

    // Instructor lock screen
    "ins.lock.academyLocked": "Your Academy is Locked",

    // Student sidebar
    "stu.browse": "Browse",
    "stu.home": "Home",
    "stu.courses": "Courses",
    "stu.products": "Products",
    "stu.sessions": "Sessions",
    "stu.myAccount": "My Account",
    "stu.dashboard": "Dashboard",
    "stu.myCourses": "My Courses",
    "stu.mySessions": "My Sessions",
    "stu.mySubscriptions": "Subscriptions",
    "stu.explore": "Explore",
    "stu.myPurchases": "My Purchases",
    "stu.freeAccess": "Free Access",
    "stu.inbox": "Inbox",
    "stu.certificates": "Certificates",
    "stu.profile": "Profile",
    "stu.exit": "Logout",
    "stu.invites": "Invites & Grants",
    "stu.settings": "Settings",
    "stu.simNotAvailable": "Not available in simulation mode",
    "stu.academyPage": "Academy Page",
    "stu.academy": "Academy",
    "stu.joinAcademy": "Join Academy",
    "stu.enterAcademy": "Enter Academy",
    "stu.officialDomain": "Official domain",
    "stu.visitOnCustomDomain": "Visit on custom domain",
    "stu.platformLink": "Platform link",

    // Student welcome onboarding
    "stu.welcome.title": "Welcome",
    "stu.welcome.badge": "Welcome",
    "stu.welcome.subtitle": "We're delighted to have you",
    "stu.welcome.skip": "Skip",
    "stu.welcome.startTour": "Start quick tour",
    "stu.welcome.fallback": "Wishing you a great learning journey. Let's take a quick tour of the main sections.",

    // Student explore (product-only discovery)
    "stu.exploreTitle": "Explore Products",
    "stu.exploreSubtitle": "Products available in the academy — courses are granted via products only",
    "stu.noQuizQuestions": "This quiz has no questions yet. Please contact the instructor.",
    "stu.matchingHint": "Tap an item in the first column, then its match in the second column",
    "stu.clearSelection": "Clear selection",
    "stu.clearMatch": "Unlink",
    "stu.confirmEmptySubmit": "You haven't answered any question. Submit anyway?",
    "stu.discoverProducts": "Discover more products",
    "stu.noProducts": "No products available",
    "stu.noCourses": "No courses available",
    "stu.searchCourses": "Search courses...",
    "stu.searchProducts": "Search products...",
    "stu.browseCourses": "Browse available courses",
    "stu.startCourse": "Start Course",

    // Student purchases
    "stu.purchasesTitle": "My Purchases",
    "stu.purchasesSubtitle": "Products you have access to",
    "stu.noPurchases": "No purchases yet",
    "stu.exploreProducts": "Explore Products",
    "stu.sessionCredits": "Session credits:",
    "stu.productContent": "Product content",
    "stu.viewProductPage": "View product page",
    "stu.purchaseDate": "Purchase date:",
    "stu.ownedProductTitle": "Owned Product",
    "stu.notOwnedYet": "This product is not in your purchases",
    "stu.notOwnedYetHint": "You haven't purchased this product and it hasn't been granted to you. You can review its details on the discovery page.",
    "stu.goToDiscovery": "Go to product page",
    "stu.ownedSubscriptionManage": "Manage subscription",
    "stu.ownedContentSection": "Content available to you",
    "stu.lfaUpgradeHint": "You're using limited free access for this product.",
    "stu.lfaUpgradeCta": "Upgrade for full access",
    "stu.source.oneTime": "One-time purchase",
    "stu.source.oneTimeHint": "Lifetime access to the content bundled in this product.",
    "stu.source.recurring": "Recurring subscription",
    "stu.source.recurringHint": "Renews automatically per your plan.",
    "stu.source.renewsOn": "Renews on",
    "stu.source.grant": "Free grant",
    "stu.source.grantHint": "This product was granted to you by the instructor — auto-activated.",
    "stu.source.limited": "Limited free access",
    "stu.source.limitedHint": "Temporary partial access — upgrade for full access.",
    "stu.source.timed": "Time-limited access",

    // Student subscriptions
    "stu.subscriptionsTitle": "My Subscriptions",
    "stu.subscriptionsSubtitle": "Invoices, payments & recurring subscriptions",
    "stu.noSubscriptions": "No subscriptions",
    "stu.recurringSubscriptions": "Recurring Subscriptions",
    "stu.nextRenewal": "Next renewal:",
    "stu.oneTimePayments": "One-time Payments",
    "stu.paymentHistory": "Payment History",
    "stu.activeSubscriptions": "Active subscriptions",

    // Student sessions
    "stu.bookAndJoin": "Book & Join",
    "stu.upcomingSessions": "Upcoming Sessions",
    "stu.needSubscription": "Subscription required",
    "stu.cancelSession": "Cancel Session",

    // Student quiz
    "stu.issueCertificate": "Issue Certificate",

    // Student email prefs
    "stu.emailPrefs.confirmEnroll": "Enrollment Confirmation",
    "stu.emailPrefs.purchaseConfirm": "Purchase Confirmation",
    "stu.emailPrefs.courseComplete": "Course Completion",
    "stu.emailPrefs.certIssued": "Certificate Issued",
    "stu.emailPrefs.sessionReminder": "Session Reminder",
    "stu.emailPrefs.cancelSession": "Session Cancellation",
    "stu.emailPrefs.paymentReceipt": "Payment Receipt",
    "stu.emailPrefs.weeklyDigest": "Weekly Digest",
    "stu.emailPrefs.activated": "Activated",
    "stu.emailPrefs.cancelled": "Cancelled",

    // Admin sidebar
    "admin.dashboard": "Dashboard",
    "admin.academies": "Academies",
    "admin.subscriptions": "Subscriptions",
    "admin.sales": "Sales",
    "admin.students": "Students",
    "admin.notifications": "Notifications",
    "admin.auditLog": "Audit Log",
    "admin.platformAdmin": "Platform Admin",
    "admin.dwrrah": "Dwrrah",
    "admin.logout": "Logout",
    "admin.team": "Team Management",
    "admin.teamDesc": "Manage platform admins and invite new members",
    "admin.inviteAdmin": "Invite Admin",
    "admin.inviteNew": "Invite New Admin",
    "admin.sendInvite": "Send Invite",
    "admin.currentAdmins": "Current Admins",
    "admin.revokeAccess": "Revoke Access",
    "admin.noAccess": "You do not have permission to access this page",
    "admin.roleOwner": "Platform Owner",
    "admin.roleAdmin": "Full Admin",
    "admin.roleStaff": "Staff (View Only)",
    "admin.roleAdminDesc": "Full Admin — full access without admin management",
    "admin.roleStaffDesc": "Staff — view only",
    "admin.statusActive": "Active",
    "admin.statusPending": "Pending",
    "admin.statusRevoked": "Revoked",
    "admin.inviteSuccess": "Invitation sent successfully",
    "admin.revokeSuccess": "Access revoked",
    "admin.enterEmail": "Enter email address",
    "admin.role": "Role",
    "admin.dateAdded": "Date Added",
    "admin.status": "Status",

    // Admin dashboard
    "admin.dashboardTitle": "Dashboard",
    "admin.totalAcademies": "Total Academies",
    "admin.activeSubscriptions": "Active Subscriptions",
    "admin.totalStudents": "Total Students",
    "admin.totalRevenue": "Total Revenue",
    "admin.trialCount": "Trial",
    "admin.pastDueCount": "Past Due",
    "admin.totalCourses": "Courses",
    "admin.totalProducts": "Products",
    "admin.walletBalance": "Wallet Balance",
    "admin.planDistribution": "Plan Distribution",
    "admin.statusDistribution": "Academy Status",
    "admin.monthlyGrowth": "Monthly Growth",
    "admin.monthlyRevenue": "Monthly Revenue",
    "admin.topAcademies": "Top Academies by Revenue",
    "admin.academyName": "Academy",
    "admin.revenue": "Revenue",
    "admin.academiesLabel": "Academies",
    "admin.studentsLabel": "Students",
    "admin.revenueLabel": "Revenue",
    "admin.planPro": "Pro",
    "admin.planBasic": "Basic",
    "admin.planUnknown": "Unknown",
    "admin.period7d": "Last 7 days",
    "admin.period30d": "Last 30 days",
    "admin.period90d": "Last 3 months",
    "admin.period6m": "Last 6 months",
    "admin.period1y": "Last year",
    "admin.periodAll": "All time",
    "admin.sar": "SAR",

    // Variant × Status (4 plans × 6 statuses + Legacy)
    "admin.variantBasicMonthly": "Basic Monthly",
    "admin.variantBasicAnnual": "Basic Annual",
    "admin.variantProMonthly": "Pro Monthly",
    "admin.variantProAnnual": "Pro Annual",
    "admin.variantLegacy": "Legacy Trials",
    "admin.variantLegacyDesc": "Pre-cycle-selection — not counted in variants",
    "admin.statusTrialActive": "Trial Active",
    "admin.statusTrialCancelled": "Trial Cancelled",
    "admin.statusTrialExpired": "Trial Expired",
    "admin.statusPaidActive": "Paid Active",
    "admin.statusPaidCancelScheduled": "Cancel Scheduled",
    "admin.statusPaidExpired": "Paid Expired",
    "admin.willNotRenew": "Will Not Renew",
    "admin.cycleMigrations": "Cycle Migrations",
    "admin.tierMigrations": "Tier Migrations",
    "admin.variantStatusMatrix": "Variant × Status Matrix",
    "admin.desyncedAcademies": "Desynced Academies",
    "admin.desyncedDesc": "Academy plan differs from subscription plan",
    "admin.sourceTrialInternal": "Trial (internal)",
    "admin.sourceStripeSynced": "Stripe synced",
    "admin.sourceDesynced": "⚠️ Desynced",
    "admin.sourceLegacyTrial": "Legacy Trial",

    // Admin settings
    "admin.settings": "Settings",
    "admin.settingsDesc": "Platform settings, pricing, and system status",
    "admin.settingsPricing": "Pricing",
    "admin.settingsConfig": "Configuration",
    "admin.settingsSystem": "System Status",
    "admin.settingsSaved": "Settings saved successfully",
    "admin.settingsError": "Error saving settings",
    "admin.basicPlanDesc": "Basic Plan — everything an instructor needs to start",
    "admin.proPlanDesc": "Pro Plan — advanced tools and full experience",
    "admin.monthlyPrice": "Monthly Price",
    "admin.annualPrice": "Annual Price",
    "admin.annualSavings": "Annual Savings",
    "admin.savePrices": "Save Prices",
    "admin.saveConfig": "Save Configuration",
    "admin.commissionRefund": "Commission & Refund",
    "admin.commissionRate": "Commission Rate",
    "admin.commissionNote": "Percentage deducted from each sale (0 = no commission)",
    "admin.refundDays": "Refund Window (days)",
    "admin.trialDays": "Free Trial Duration (days)",
    "admin.storageLimits": "Storage Limits",
    "admin.storageBasic": "Basic Plan Storage",
    "admin.storagePro": "Pro Plan Storage",
    "admin.maxFileSize": "Max File Size",
    "admin.scheduledJobs": "Scheduled Jobs",
    "admin.cronTrialExpiry": "Check trial expiry daily",
    "admin.cronSessionReminder": "Session reminders hourly",
    "admin.cronWeeklySettlement": "Weekly wallet settlement",
    "admin.cronReconcileStorage": "Weekly storage reconciliation",
    "admin.cronEmailQueue": "Process email queue every 5 seconds",
    "admin.active": "Active",
    "admin.systemInfo": "System Info",
    "admin.emailSystem": "Email System",
    "admin.paymentProcessor": "Payment Processor",
    "admin.liveProvider": "Live Sessions Provider",
    "admin.contentProtection": "Content Protection",
    "admin.aiProvider": "AI Provider",
    "admin.customDomain": "Custom Domain",
    "admin.notImplemented": "Not implemented yet",

    // Phase 23 — Custom Domain Safe Fallback
    "customDomain.fallbackToast": "The academy was opened via the main Dwrrah link because the custom domain is currently unavailable.",
    "customDomain.statusSuspended": "Suspended (plan)",
    "customDomain.statusRemovalPending": "Removal pending",
    "customDomain.suspendedBanner": "Your custom domain is suspended because your current plan does not include this feature. Students are automatically redirected to the main academy link. Upgrade to Pro to re-enable it.",
    "customDomain.removalPendingBanner": "This domain is scheduled for removal in {days} day(s). You can cancel the request or remove it immediately.",
    "customDomain.cancelRemoval": "Cancel removal request",
    "customDomain.forceRemoveNow": "Remove immediately",
    "customDomain.confirmForceRemove": "Confirm immediate and permanent removal of this domain? All circulating links will break instantly.",
    "customDomain.notMapped.title": "This domain is no longer active",
    "customDomain.notMapped.body": "The domain {host} is not connected to any academy on Dwrrah. You will be redirected to the main page in a few seconds.",
    "customDomain.notMapped.cta": "Go to dwrrah.com now",
    "ins.toast.cancel": "Cancel",
    "customDomain.fallbackToast.domain_removed": "The academy no longer uses this custom domain. You were redirected to the Dwrrah main page.",
    "customDomain.fallbackToast.domain_unknown": "The domain you visited is no longer active. You were redirected to the Dwrrah main page.",

    // Admin tables & actions
    "admin.searchAcademies": "Search by name or URL...",
    "admin.searchStudents": "Search by name or email...",
    "admin.searchPayments": "Search by student or product...",
    "admin.searchAudit": "Search by actor or reason...",
    "admin.newAcademy": "New Academy",
    "admin.plan": "Plan",
    "admin.billingCycle": "Billing Cycle",
    "admin.action": "Action",
    "admin.enterReason": "Please enter a reason",
    "admin.writeReason": "Write the reason for this action...",
    "admin.actionSuccess": "Action completed successfully",
    "admin.unauthorized": "Unauthorized",
    "admin.noAccessMsg": "This account does not have admin access",

    // Admin subscription actions
    "admin.changePlan": "Change Plan",
    "admin.activateSubscription": "Activate Subscription",
    "admin.suspendSubscription": "Suspend Subscription",
    "admin.cancelSubscription": "Cancel Subscription",
    "admin.cancelEndPeriod": "Cancel at end of period",
    "admin.extendTrial": "Extend / Set Trial Date",
    "admin.setTrialDate": "Set Trial Date",
    "admin.scheduledPlan": "Scheduled Plan",
    "admin.trial": "Trial",
    "admin.trialExpiring": "Trial expiring soon",
    "admin.trialExpiry": "Trial Expiry",
    "admin.trialStart": "Trial Start",
    "admin.expired": "Expired",
    "admin.suspended": "Suspended",
    "admin.cancelled": "Cancelled",
    "admin.pastDue": "Past Due",
    "admin.start": "Start",
    "admin.end": "End",
    "admin.daysRemaining": "days remaining",
    "admin.annualRevenue": "Projected Annual Revenue",
    "admin.retentionRate": "Retention Rate",
    "admin.conversionRate": "Conversion Rate",
    "admin.churnRate": "Churn Rate",
    "admin.retention": "Retention",
    "admin.churn": "Churn",

    // Admin plan labels
    "admin.basicMonthly": "Basic Monthly",
    "admin.basicAnnual": "Basic Annual",
    "admin.proMonthly": "Pro Monthly",
    "admin.proAnnual": "Pro Annual",
    "admin.trialPeriod": "Trial Period",

    // Admin payment types
    "admin.paymentType.purchase": "Student Purchase",
    "admin.paymentType.subscription": "Subscription",
    "admin.paymentType.deposit": "Deposit",
    "admin.paymentType.refund": "Refund",
    "admin.paymentType.payment": "Payment",
    "admin.paymentType.paidStatus": "Paid",

    // Admin notifications
    "admin.notif.trialExpiry": "Trial Expiry",
    "admin.notif.newRegistration": "Registration",
    "admin.notif.planChange": "Plan Change",
    "admin.notif.trialExtend": "Trial Extension",
    "admin.notif.activate": "Activation",
    "admin.notif.suspend": "Suspension",

    // KPI
    "kpi.students": "Students",
    "kpi.enrollments": "Enrollments",
    "kpi.revenue": "Revenue",
    "kpi.completionRate": "Completion Rate",
    "kpi.certificates": "Certificates",
    "kpi.sessions": "Sessions",
    "kpi.quizAttempts": "Quiz Attempts",
    "kpi.passRate": "Pass Rate",

    // Lock screen
    "lock.expired.title": "Your Academy is Locked",
    "lock.expired.message": "Your subscription has expired. Renew to continue.",
    "lock.expired.detail": "Your landing page is currently inactive and in maintenance mode until you renew.",
    "lock.expired.button": "Renew Subscription",
    "lock.pastDue.message": "Payment failed and was not renewed during the grace period. Renew to continue.",
    "lock.cancelled.message": "Your subscription was cancelled and the service period ended. Renew to continue.",
    "lock.suspended.title": "Your Academy is Suspended",
    "lock.suspended.message": "Your academy was suspended by the platform administration.",
    "lock.suspended.detail": "Contact support to learn why and reactivate your academy.",
    "lock.suspended.button": "Contact Support",
    "lock.academyLabel": "Academy",

    // Student dashboard
    "stu.welcome": "Welcome",
    "stu.myCoursesStat": "My Courses",
    "stu.activeSubscriptionsStat": "Active Subscriptions",
    "stu.upcomingSessionsStat": "Upcoming Sessions",
    "stu.certificatesStat": "Certificates",
    "stu.nextRenewalLabel": "Next subscription renewal",
    "stu.details": "Details",
    "stu.continueWhere": "Continue where you left off",
    "stu.courseLabel": "Course",
    "stu.lessonsOf": "of",
    "stu.lesson": "lesson",
    "stu.notEnrolledYet": "You haven't enrolled in any course yet",
    "stu.exploreCourses": "Explore Courses & Products",
    "stu.discoverMore": "Discover more from the academy",
    "stu.quizLabel": "quiz",
    // Dashboard v2 — Next Action Hero, Streak, Multi-course
    "stu.nextAction.title": "What's next?",
    "stu.nextAction.liveNow": "Live session now — join",
    "stu.nextAction.upcomingSession": "Upcoming session",
    "stu.nextAction.joinSession": "Join session",
    "stu.nextAction.viewSession": "View session",
    "stu.nextAction.continueLesson": "Continue where you left off",
    "stu.nextAction.continueBtn": "Resume learning",
    "stu.nextAction.startCourse": "Start your first course",
    "stu.nextAction.exploreBtn": "Explore courses",
    "stu.nextAction.almostDone": "You're almost done!",
    "stu.nextAction.lessonsLeft": "lessons left",
    "stu.streak.title": "Your activity this week",
    "stu.streak.daysActive": "active days",
    "stu.streak.lessonsThisWeek": "lessons this week",
    "stu.streak.noActivity": "No learning yet this week — start today!",
    "stu.streak.dayShort.0": "Sun",
    "stu.streak.dayShort.1": "Mon",
    "stu.streak.dayShort.2": "Tue",
    "stu.streak.dayShort.3": "Wed",
    "stu.streak.dayShort.4": "Thu",
    "stu.streak.dayShort.5": "Fri",
    "stu.streak.dayShort.6": "Sat",
    "stu.continueAll": "All my courses",
    // Progress ring + nudge + info-strip
    "stu.progressRing.title": "Overall progress",
    "stu.progressRing.subtitle": "across all your courses",
    "stu.progressRing.empty": "Enroll to see your progress here",
    "stu.nudge.almostCert": "You're close to a new certificate!",
    "stu.nudge.almostCertDesc": "Only {n} lesson(s) left in {course}",
    "stu.nudge.continueNow": "Continue now",
    "stu.info.courses": "Courses",
    "stu.info.subs": "Subscriptions",
    "stu.info.sessions": "Sessions",
    "stu.info.certs": "Certificates",
    "stu.analytics.toggle": "Detailed stats & analytics",

    // Student profile
    "stu.profileTitle": "Profile",
    "stu.displayName": "Display Name",
    "stu.phone": "Phone Number",
    "stu.bioLabel": "About You",
    "stu.bioPlaceholder": "Tell us about yourself...",
    "stu.imageUpdated": "Image updated",
    "stu.imageUploadFail": "Image upload failed",
    "stu.saveChanges": "Save Changes",

    // Student settings
    "stu.settingsTitle": "Settings",
    "stu.settingsSubtitle": "Your account settings and integrations",
    "stu.personalInfoTitle": "Personal Information",
    "stu.notificationsTitle": "Notification Preferences",
    "stu.notificationsSubtitle": "Control system and email notifications by type",
    "stu.googleCalSync": "Automatically sync your booked sessions with your Google Calendar",
    "stu.featureNotAvailable": "This feature is not available in this academy",
    "stu.connected": "Connected",
    "stu.disconnectGoogle": "Disconnect",
    "stu.reconnectGoogle": "Reconnect",
    "stu.connectGoogle": "Connect Google Calendar",
    "stu.expiredConnection": "Connection expired. Please reconnect.",
    "stu.calendarSyncTitle": "How does sync work?",
    "stu.calendarSyncItem1": "When you book a session, an event is automatically added to your calendar",
    "stu.calendarSyncItem2": "Automatic reminder 30 minutes before the session",
    "stu.calendarSyncItem3": "Join link is included in the event",
    "stu.googleCalConnected": "Google Calendar connected successfully ✅",
    "stu.googleCalFail": "Failed to connect Google Calendar",
    "stu.googleCalDisconnected": "Google Calendar disconnected",

    // Pro feature gating (student side)
    "stu.proFeature.locked": "This feature requires your instructor to enable it",
    "stu.proFeature.requestAccess": "Request access from instructor",
    "stu.proFeature.requestSent": "Request sent",
    "stu.proFeature.requestSuccess": "Your request was sent to the instructor",
    "stu.proFeature.requestAlready": "You already sent this request",
    "stu.proFeature.requestError": "Could not send request, try again later",

    // Student sessions
    "stu.mySessionsTitle": "My Sessions",
    "stu.completeRegFirst": "Please complete your registration first",
    "stu.linkCalendarBanner": "📅 Link your calendar to sync session times automatically",
    "stu.linkCalendar": "Link Calendar",
    "stu.tabUpcoming": "Upcoming",
    "stu.tabPast": "Past",
    "stu.tabInvited": "Invites",
    "stu.noUpcomingSessions": "No upcoming sessions",
    "stu.noPastSessions": "No past sessions",
    "stu.noInvitesNow": "No invites at the moment",
    "stu.browseSessionsHint": "Browse available sessions and book your spot",
    "stu.invitedSessionsHint": "These are sessions you've been invited to — click for details and booking",
    // Unified sessions list (v1.07.14)
    "stu.sessions.searchPlaceholder": "Search sessions",
    "stu.sessions.filterAll": "All",
    "stu.sessions.viewActive": "Active",
    "stu.sessions.filterUpcoming": "Upcoming",
    "stu.sessions.filterLive": "Live now",
    "stu.sessions.filterAwaiting": "Awaiting scheduling",
    "stu.sessions.filterPast": "Ended",
    "stu.sessions.filterCancelled": "Cancelled",
    "stu.sessions.filterInvites": "Invites",
    "stu.sessions.sortLabel": "Sort",
    "stu.sessions.sortNearest": "Nearest first",
    "stu.sessions.sortFurthest": "Furthest first",
    "stu.sessions.sortNewest": "Newest created",
    "stu.sessions.allEmpty": "No sessions match the current filters",
    "stu.sessions.awaitingTag": "Awaiting scheduling",
    "stu.sessions.scheduleNow": "Schedule now",
    "stu.sessions.invited": "Invited",

    // Course viewer
    "stu.completeLesson": "Complete Lesson",
    "stu.completed": "Completed",
    "stu.lessonCompletedToast": "Lesson completed ✓",
    "stu.prevLesson": "Previous",
    "stu.nextLesson": "Next",
    "stu.noLessons": "No lessons in this course",
    "stu.previewMode": "Simulation — quiz is fully testable, results are not saved",
    "stu.previewLabel": "Preview Mode",
    "stu.previewOwner": "Owner",
    "stu.previewManager": "Manager",
    "stu.previewEditor": "Content Editor",
    "stu.limitedFreeAccess": "Limited Free Access",
    "stu.buyFull": "Buy Full",
    "stu.buyFullAccess": "Buy for full access",
    "stu.lessonNotFree": "This lesson is not available in the free version",
    "stu.lessonNeedsSub": "This lesson requires a subscription",
    "stu.lessonNotAvailable": "This lesson is not available yet",
    "stu.goToQuiz": "Go to Quiz",
    "stu.cannotCompleteYet": "Cannot complete yet",
    "stu.waitSessionEnd": "Wait for the live session to end",
    "stu.mustAttendSession": "You must attend the live session first",
    "stu.congrats": "🎉 Congratulations!",
    "stu.lessonDoneTitle": "Lesson completed",
    "stu.nextUp": "Next:",
    "stu.continueToNext": "Continue to next lesson",
    "stu.moduleDoneTitle": "Module completed",
    "stu.continueToNextModule": "Continue to next module",
    "stu.contentDoneTitle": "You finished the course content",
    "stu.finalExamRequired": "The final exam is still required to complete the course",
    "stu.startFinalExam": "Start final exam",
    "stu.retryFinalExam": "Retake final exam",
    "stu.courseCompletedTitle": "🎉 Course completed!",
    "stu.courseCompletedDesc": "You've met all the course requirements. Keep learning!",
    "stu.viewCertificate": "View certificate",
    "stu.replayCourse": "Replay course",
    "stu.backToMyCourses": "Back to my courses",
    "stu.finishCourse": "Finish course",
    "stu.simCertNotAvailable": "Certificate is not available in simulation mode",
    "stu.simCertPreviewOnly": "This is a preview only — no certificate will be saved in simulation mode.",
    "stu.simCertPreviewNote": "Mock preview without saving — this is not a shareable certificate.",
    "stu.previewCertificate": "Preview Certificate",
    "stu.startQuizCta": "Start quiz",
    "stu.finalExamSection": "Final exam",
    "stu.moduleQuizLabel": "Module quiz",
    "stu.continueToFinish": "Continue to finish",
    "stu.backToCourse": "Back to course",
    "stu.completionNotYetTitle": "Course is not yet complete",
    "stu.completionScoreLabel": "Score",
    "stu.completionPassingLabel": "Passing grade",
    "stu.completionFinalExamScore": "Final exam score",
    "stu.completionWeightedScore": "Weighted score",
    "stu.completionUnmetLessons": "Some required lessons are still incomplete.",
    "stu.completionUnmetFinalExamPending": "All content done — the final exam is still required to complete the course.",
    "stu.completionUnmetFinalExamFailed": "Your final exam score is below the passing grade — a retake is required.",
    "stu.completionUnmetWeighted": "Your weighted score (quizzes + final exam) is below the passing grade.",
    "stu.completionUnmetFinalPlusQuizzes": "The final exam and quizzes must be completed to compute the required passing score.",
    "stu.completionUnmetUnknown": "The instructor-configured completion conditions are not yet satisfied.",

    // Course detail
    "stu.courseNotFound": "Course not found",
    "stu.courseContent": "Course Content",
    "stu.previewCourse": "Preview Course",
    "stu.internalPreview": "Internal Preview",
    "stu.previewAs": "Preview mode — you are viewing this page as",
    "stu.ownerRole": "Owner",
    "stu.managerRole": "Manager",
    "stu.editorRole": "Content Editor",
    "stu.freePreview": "Free Preview",
    "stu.editLabel": "Edit",

    // Session room
    "stu.backLabel": "Back",
    "stu.previewNoBooKing": "Preview mode — no booking or attendance will be recorded",
    "stu.sessionNotStarted": "Session hasn't started yet",
    "stu.sessionLiveNow": "Session is live now!",
    "stu.sessionFull": "Session is full",
    "stu.bookAndJoinBtn": "Book & Join",
    "stu.sessionEnded": "Session ended",
    "stu.watchRecording": "Watch Recording",
    "stu.sessionCancelled": "Session cancelled",
    "stu.joinLinkOpensIn": "Join link opens in",
    "stu.retryLabel": "Retry",
    "stu.bookingsCount": "bookings",
    "stu.internalPreviewBadge": "Internal Preview",
    "stu.freeSession": "Free",
    "stu.hasAccess": "You have access",
    "stu.addToCalendar": "Add to Calendar",
    "stu.calendarFileHint": "Download calendar file (ICS) to add to Google, Apple, or Outlook",
    "stu.registeredInSession": "You are registered in this session",
    "stu.cancelBooking": "Cancel Booking",
    "stu.sessionFullNoBook": "Session full — cannot book",
    "stu.bookYourSpot": "Book your spot",
    "stu.directBookNotAvail": "Direct booking is not available for this session",

    // Course catalog
    "stu.coursesTitle": "Courses",
    "stu.coursesAvailable": "courses available",
    "stu.searchCourse": "Search courses...",
    "stu.comingSoonCourses": "Your instructor is preparing amazing courses. Stay tuned!",

    // Products catalog
    "stu.productsTitle": "Products",
    "stu.searchProduct": "Search products...",
    "stu.comingSoonProducts": "Your instructor is preparing amazing products for you. Stay tuned!",
    "stu.featured": "Featured",
    "stu.perMonth": "/month",
    "stu.per3Months": "/3 months",
    "stu.perYear": "/year",

    // Sessions catalog
    "stu.availableSessions": "Available Sessions",
    "stu.noScheduledSessions": "No scheduled sessions. Your instructor will add sessions soon!",
    "stu.privateLabel": "Private",
    "stu.groupLabel": "Group",
    "stu.openLabel": "Open",
    "stu.fullLabel": "Full",
    "stu.detailsLabel": "Details",
    "stu.viewLabel": "View",

    // Email preferences
    "stu.emailPrefsTitle": "Email Preferences",
    "stu.emailPrefsSubtitle": "Control the automatic emails you receive",
    "stu.emailWelcome": "Welcome new student",
    "stu.emailWelcomeDesc": "When registering in the academy",
    "stu.emailEnrollConfirm": "Enrollment Confirmation",
    "stu.emailEnrollConfirmDesc": "When enrolling in a course",
    "stu.emailCourseComplete": "Course Completion",
    "stu.emailCourseCompleteDesc": "When completing a course",
    "stu.emailCertIssued": "Certificate Issued",
    "stu.emailCertIssuedDesc": "When a certificate is issued",
    "stu.emailPaymentReceipt": "Payment Receipt",
    "stu.emailPaymentReceiptDesc": "After every payment",
    "stu.emailSessionReminder": "Session Reminder",
    "stu.emailSessionReminderDesc": "24 hours before the session",
    "stu.emailInactivity": "Inactivity Reminder",
    "stu.emailInactivityDesc": "When inactive",
    "stu.emailWeeklyDigest": "Weekly Digest",
    "stu.emailWeeklyDigestDesc": "Weekly progress summary",
    "stu.saveFail": "Save failed",

    // Notification history
    "stu.notifHistory": "Notification History",
    "stu.markAllRead": "Mark all read",
    "stu.allTypes": "All types",
    "stu.noNotifications": "No notifications",
    "stu.now": "Now",
    "stu.minutesAgo": "min",
    "stu.hoursAgo": "hour",
    "stu.daysAgo": "day",
    "stu.agoPrefix": "ago",
    "stu.notifSessionReminder": "Session Reminder",
    "stu.notifNewMessage": "New Message",
    "stu.notifEnrollment": "Enrollment",
    "stu.notifCertificate": "Certificate",
    "stu.notifPurchaseConfirmed": "Purchase Confirmed",
    "stu.notifPaymentFailed": "Payment Failed",
    "stu.notifSessionBooked": "Session Booked",
    "stu.notifSessionCancelled": "Session Cancelled",
    "stu.notifCourseCompleted": "Course Completed",
    "stu.notifDripUnlocked": "New Content",
    "stu.notifProductGrant": "Grant",

    // Free access
    "stu.freeAccessTitle": "Free Access",
    "stu.noFreeAccess": "You don't have any free access currently",
    "stu.browseProducts": "Browse Products",
    "stu.activeFreeAccess": "Active Free Access",
    "stu.limitedAccess": "Limited Access",
    "stu.startedOn": "Started on",
    "stu.upgradedToPaid": "Upgraded to paid",
    "stu.freePartInProgress": "Free portion: in progress",
    "stu.freePartCompleted": "You've completed the free portion — purchase to continue the rest",
    "stu.payToContinue": "Pay to continue",

    // Invites & Grants
    "stu.grantsTitle": "Grants & Invites",
    "stu.grantsSubtitle": "Granted products and purchase invitations",
    "stu.freeGrants": "Free Grants",
    "stu.purchaseInvites": "Purchase Invites",
    "stu.noGrants": "No grants yet",
    "stu.noInvites": "No invites yet",
    "stu.freeGrant": "Free Grant",
    "stu.viewProduct": "View Product",
    "stu.expired": "Expired",
    "stu.until": "Until",

    // Academy home / custom pages
    "stu.comingSoonHome": "Your instructor is preparing an amazing experience for you 🎓 Come back soon!",
    "stu.pageNotFound": "Page not found",
    "stu.comingSoonPage": "Your instructor is preparing this page. Come back soon!",
    "stu.academyLabel": "Academy",

    // Student misc (remaining components)
    "stu.myCoursesPg": "My Courses",
    "stu.notEnrolledCourse": "You haven't enrolled in any course yet",
    "stu.browseCoursesCta": "Browse Courses",
    "stu.lessonUnit": "lesson",
    "stu.certificatesPg": "My Certificates",
    "stu.noCertificates": "No certificates yet",
    "stu.completeCourseForCert": "Complete a course to earn your certificate",
    "stu.issuedOn": "Issued on",
    "stu.certNumber": "No:",
    "stu.downloadCert": "Download Certificate",
    "stu.viewCert": "View certificate",
    "stu.messagesPg": "Messages",
    "stu.completeRegistration": "Please complete your registration first",
    "stu.startConversation": "Start a conversation with the instructor",
    "stu.writeMessage": "Write your message...",
    "stu.productNotFound": "Product not found",
    "stu.backToCourse": "Back to Course",
    "stu.reviewQuiz": "Review Quiz",
    "stu.previewQuiz": "Preview Quiz",
    "stu.quizNotFound": "Quiz not found",
    "stu.reviewAnswers": "Review Answers",
    "stu.questionUnit": "question",
    "stu.passingGrade": "Passing grade:",
    "stu.attemptsUsed": "Attempts:",
    "stu.startQuiz": "Start Quiz",
    "stu.retryQuiz": "Retry",
    "stu.skipOptionalQuiz": "Skip quiz",
    "stu.optionalQuizSuggestionTitle": "Optional quiz available",
    "stu.optionalQuizSuggestionDesc": "This quiz is optional. You can take it or skip and continue.",
    "stu.takeQuizNow": "Take quiz",
    "stu.skipAndContinue": "Skip and continue",
    "stu.answerRequiredFirst": "Answer the question first",
    "stu.requiredQuizMissingDesc": "This quiz is required — please answer every question before submitting.",
    "stu.previewNoSubmit": "Preview mode — answers cannot be submitted",
    "stu.previewBrowseOnly": "Simulation — try the full quiz, results are not saved",
    "stu.previewSubmitOff": "Simulation — submit works locally, result is not saved",
    "stu.previewSubmit": "Submit (preview)",
    "stu.previewResultBanner": "Preview result — not saved",
    "stu.previewBrowse": "Simulation — try the full quiz, results are not saved to the database",
    "stu.questionXofY": "Question {x} of {y}",
    "stu.answeredCount": "{x} of {y} answered",
    "stu.questionsList": "Questions",
    "stu.previousQuestion": "Previous",
    "stu.nextQuestion": "Next",
    "stu.unansweredWarning": "You have {n} unanswered question(s). Submit anyway?",
    "stu.requiredQuiz": "Required",
    "stu.answerRequiredFirst": "Answer this question first",
    "stu.requiredQuizMissingDesc": "This is a required quiz. Answer every question before submitting.",
    "stu.finalExamLabel": "Final exam",
    "stu.questionStatusAnswered": "Answered",
    "stu.questionStatusEmpty": "Empty",
    "stu.questionStatusCurrent": "Current",
    "stu.actionNextLesson": "Next lesson",
    "stu.actionNextModule": "Next module",
    "stu.actionBackToCourseShort": "Back to course",
    "stu.actionEndCourse": "Finish course",
    "stu.actionContinueAnyway": "Continue course",
    "stu.closePreview": "Close Preview",
    "stu.submitQuiz": "Submit Quiz",
    "stu.quizPassed": "Great job! You passed the quiz",
    "stu.quizFailed": "You did not pass the quiz",
    "stu.scoreLabel": "Score",
    "stu.pointsLabel": "Points",
    "stu.exhaustedBlock": "All attempts exhausted. Contact the instructor.",
    "stu.exhaustedRequest": "A request has been sent to the instructor for an extra attempt.",
    "stu.exhaustedContinue": "You can continue despite not passing the quiz.",
    "stu.continueBtn": "Continue",
    "stu.writeAnswer": "Write your answer here...",
    "stu.correctAnswer": "Correct answer:",
    "stu.loginToJoin": "Log in to join this academy",
    "stu.loginBtn": "Log In",
    "stu.registrationClosed": "Registration is closed",
    "stu.registrationClosedDesc": "This academy is not accepting new registrations at this time.",
    "stu.joinDesc": "Join this academy to access content and courses",
    "stu.joinBtn": "Join this Academy",
    "stu.maintenanceTitle": "Academy Temporarily Unavailable",
    "stu.maintenanceDesc": "This academy is currently unavailable. Please contact the instructor for more information.",
    "stu.checkoutSuccess": "Purchase completed successfully! 🎉",
    "stu.checkoutSuccessDesc": "You've been enrolled and can now access the content.",
    "stu.checkoutCancelled": "Payment was cancelled",
    "stu.checkoutCancelledDesc": "You can try again anytime.",
    "stu.comingSoonContent": "Content Coming Soon ✨",
    "stu.comingSoonDefault": "Your instructor is preparing amazing content. Come back soon!",
    "stu.loginForFree": "Log in to start the free version",
    "stu.startFreeVersion": "Start Free Version",
    "stu.alreadyHaveFree": "You already have free access to this product",
    "stu.freeAccessActivated": "Free access activated successfully! 🎉",
    "stu.freeAccessError": "Error activating free access",
    "stu.contentLocked": "This content is currently unavailable",
    "stu.contentRequiresSub": "This content requires a subscription",
    "stu.viewProductBtn": "View Product",
    "stu.orChooseAlt": "Or choose from other options",
    "stu.loginFirst": "Log in first to continue",
    "stu.productContents": "Product Contents",
    "stu.freeAccessAvail": "Free access available",
    "stu.liveSessions": "Live Sessions",
    "stu.includedSessions": "Included Live Sessions",
    "stu.noLinkedSessions": "No live sessions linked to this course",
    "stu.noIncludedSessions": "No live sessions included currently",
    "stu.joinNow": "Join Now",
    "stu.joinLive": "Join",
    "stu.recordingLabel": "Recording",
    "stu.recordingTitle": "Recording:",
    "stu.noRecordingAccess": "You don't have access to this recording",
    "stu.noRecordingUploaded": "Recording hasn't been uploaded yet",
    "stu.aboutCourse": "About the Course",
    "stu.courseContentLabel": "Course Content",
    "stu.startCourseBtn": "Start Course",
    "stu.moduleUnit": "module",
    "stu.hourUnit": "hour",
    "stu.andUnit": "and",
    "stu.contentTab": "Content",
    "stu.discussionTab": "Discussion",
    "stu.lessonsCompleted": "lessons completed",
    "stu.freeLabel": "Free",
    "stu.instructorBadge": "Instructor",
    "stu.replyBtn": "Reply",
    "stu.pinBtn": "Pin",
    "stu.unpinBtn": "Unpin",
    "stu.showBtn": "Show",
    "stu.hideBtn": "Hide",
    "stu.commentsTitle": "Comments & Questions",
    "stu.replyTo": "Reply to",
    "stu.writeComment": "Write your comment or question...",
    "stu.noComments": "No comments yet. Be the first to comment!",
    "stu.unknownAuthor": "Unknown",
    "stu.startDiscussion": "Start a discussion with your peers",
    "stu.myAnalytics": "My Analytics",
    "stu.preparingCharts": "Preparing charts...",
    "stu.chartsAppearAfter": "Charts will appear here after you start studying, solving quizzes, or booking sessions.",
    "stu.courseProgress": "Course Progress",
    "stu.weeklyActivity": "Weekly Learning Activity",
    "stu.lessonsTab": "Lessons",
    "stu.timeTab": "Time (min)",
    "stu.quizPerformance": "Quiz Performance",
    "stu.sessionAttendance": "Session Attendance",
    "stu.passRateStat": "Pass Rate",
    "stu.attemptUnit": "attempt",
    "stu.avgTimePerQuiz": "Avg time per quiz",
    "stu.allCoursesFilter": "All Courses",
    "stu.noCoursesStat": "No courses",
    "stu.noQuizzesSolved": "No quizzes attempted yet.",
    "stu.noSessionBooked": "No sessions booked yet.",
    "stu.courseProgressHint": "Progress will appear here after enrolling in your first course.",
    "stu.weeklyActivityHint": "Your daily activity will appear here after completing lessons.",
    "stu.milestoneTimeline": "Timeline",
    "stu.courseEnrollment": "Course enrollment",
    "stu.courseCompletion": "Course completion",
    "stu.certIssuedMilestone": "Certificate issued",
    "stu.statusScheduled": "Scheduled",
    "stu.statusLive": "🔴 Live Now",
    "stu.statusCompleted": "Completed",
    "stu.statusCancelled": "Cancelled",
    "stu.sessionPrivate": "Private",
    "stu.sessionGroup": "Group",
    "stu.notifTitle": "Notifications",
    "stu.unreadCount": "unread",
    "stu.viewAllNotifs": "View all notifications",
    "stu.noNotifsBell": "No notifications",
    "stu.unsubProcessing": "Processing...",
    "stu.unsubTitle": "Unsubscribe from Emails",
    "stu.unsubConfirmQ": "Do you want to stop receiving automatic emails?",
    "stu.unsubConfirmBtn": "Confirm Unsubscribe",
    "stu.unsubDone": "Unsubscribed",
    "stu.unsubDoneMsg": "You will no longer receive automatic emails.",
    "stu.unsubAlready": "Already Unsubscribed",
    "stu.unsubAlreadyMsg": "Your email is already unsubscribed.",
    "stu.unsubError": "An Error Occurred",
    "stu.unsubErrorMsg": "The link is invalid or expired.",
    "stu.unsubProcessing2": "Unsubscribing...",
    "stu.unsubDone2": "Unsubscribed",
    "stu.unsubDoneMsg2": "You will no longer receive automatic emails. You can re-subscribe from your account settings.",
    "stu.unsubFail": "An Error Occurred",
    "stu.unsubFailMsg": "Could not unsubscribe. Please try again.",
    "stu.productFree": "Free",
    "stu.discountLabel": "Discount",
    "stu.couponCode": "Discount Code",
    "stu.hasProductAccess": "You have access to this product",
    "stu.loginForPurchase": "Log in",
    "stu.loginForFreeProduct": "to get it for free",
    "stu.loginForBuy": "to purchase",
    "stu.getItFree": "Get it for free",
    "stu.subscribeNow": "Subscribe Now",
    "stu.refundPolicy": "Refund policy:",
    "stu.refund7d": "7 days",
    "stu.refund14d": "14 days",
    "stu.refund30d": "30 days",
    "stu.productCourse": "Course",
    "stu.productBundle": "Bundle",
    "stu.productMembership": "Membership",
    "stu.productSessionPkg": "Session Package",
    "stu.productGeneric": "Product",
    "stu.statusAttended": "Attended",
    "stu.statusBooked": "Booked",
    "stu.statusNoShow": "No Show",
    "stu.noSessions": "No sessions",
    "stu.lessonsCompletedChartLabel": "Completed lessons",
    "stu.minutesLabel": "Minutes",
    "stu.progressLabel": "Progress",
    "stu.averageLabel": "Average",
    "stu.milestone25": "Great! You've completed 25% of the course",
    "stu.milestone50": "Amazing! You've completed half the course",
    "stu.milestone75": "Excellent! Just a little more to finish the course",
    "stu.milestone100": "Congratulations! You've completed the entire course",
    "stu.milestoneComplete": "Finished",
    "stu.milestoneCheckCert": "Check your certificate!",

    // Academy-branded auth + admin-blocked screen
    "academyLogin.subtitle": "Sign in to the academy",
    "academyLogin.createAccount": "Create a new account",
    "academySignup.subtitle": "Create a new academy account",
    "academyAuth.emailTab": "Email",
    "academyAuth.googleTab": "Google",
    "academyAuth.signupCta": "Create account",
    "academyAuth.loginCta": "Sign in",
    "academyAuth.phoneOptional": "Phone number (optional)",
    "academyAuth.existingUserHint": "If you already have an account, sign in with the same email to join this academy.",
    "academyAuth.goToLogin": "Go to sign in",
    "academyAuth.loginGenericError": "Invalid sign-in details",
    "academyAuth.welcomeBack": "Welcome back",
    "academyAuth.existingAccountTitle": "You already have an account in this academy",
    "academyAuth.existingAccountBody": "Sign in with the same email and password.",
    "academyAuth.signupGenericError": "Couldn't create the account right now. Try again later.",
    "academyAuth.forgotPasswordLink": "Forgot password?",
    "academyAuth.recoveryTitle": "Reset your password",
    "academyAuth.recoveryEmailHint": "Enter the email you used in this academy and we'll send you an 8-digit verification code.",
    "academyAuth.recoverySendOtp": "Send verification code",
    "academyAuth.recoveryBackToLogin": "Back to sign in",
    "academyAuth.recoveryChangeEmail": "Change email",
    "academyAuth.recoveryNewPasswordTitle": "New password",
    "academyAuth.recoveryNewPasswordHint": "Pick a new password to use in this academy.",
    "academyAuth.recoveryDoneTitle": "Password updated",
    "academyAuth.recoveryDoneBody": "You can now sign in with your new password.",
    "adminBlocked.title": "You're a platform admin",
    "adminBlocked.body": "This portal is for instructors. Please use the admin portal to sign in.",
    "adminBlocked.goAdmin": "Go to admin portal",
    "adminBlocked.tryAgain": "Sign in with another account",
    "adminHandoff.noticeTitle": "Redirecting to the admin portal",
    "adminHandoff.noticeBody": "Your account is a platform admin — taking you there now.",

    // Student components
    "stu.loginToJoin": "Log in to join this academy",
    "stu.registrationClosed": "Registration is closed",
    "stu.registrationClosedDesc": "This academy is not accepting new registrations at this time.",
    "stu.joinAcademyAccess": "Join this academy to access content and courses",
    "stu.joinThisAcademy": "Join this academy",
    "stu.notMemberHere": "Your current account is not a member of this academy",
    "stu.useDifferentAccount": "Use a different account",
    "stu.createSeparateAccount": "Create a separate student account for this academy",
    "stu.blockedOwner": "You own this academy",
    "stu.blockedStaff": "You are a team member of this academy",
    "stu.blockedDesc": "You cannot register as a student in an academy you own or staff with the same account. Use “Open as student” to preview.",
    "stu.ownerSelfTitle": "You own this academy",
    "stu.staffSelfTitle": "You are a team member of this academy",
    "stu.ownerSelfDesc": "You cannot join as a student in your own academy with the same account. Use “Open as student” to preview the student experience, or go back to the instructor dashboard.",
    "stu.openAsStudent": "Open as student (simulation)",
    "stu.goToInstructor": "Go to instructor dashboard",
   "stu.accountSuspended": "Your account is suspended",
   "stu.accountSuspendedDesc": "Your access to this academy has been temporarily suspended. Your records (courses, payments, certificates) are preserved.",
   "stu.accountBanned": "Your account is banned",
   "stu.accountBannedDesc": "Your account has been banned from this academy. Past records are preserved but you cannot access content.",
   "stu.contactToReinstate": "To request reinstatement, contact:",
   "stu.orPlatformSupport": "Or platform support:",
   "stu.featureUnavailable": "This feature is currently unavailable",
   "stu.featureUnavailableDesc": "This feature is not enabled on this academy right now. Please contact the instructor for more information.",
   "stu.backToHome": "Back to home",
    "stu.myCoursesPgDesc": "Track your progress in enrolled courses",
    "stu.ent.lifetime": "Lifetime access",
    "stu.ent.oneTime": "One-time purchase",
    "stu.ent.subscription": "Subscription",
    "stu.ent.monthly": "Monthly",
    "stu.ent.annual": "Annual",
    "stu.ent.renews": "Renews",
    "stu.ent.until": "Until",
    "stu.ent.expired": "Expired",
    "stu.ent.free": "Free",
    "stu.ent.trial": "Temporary access",
    "stu.ent.renewNow": "Renew subscription",
    "stu.ent.buyAgain": "Buy again",
    "stu.ent.expiredOn": "Expired on",
    "stu.ent.scheduledCancel": "Scheduled to cancel — active until",
    "stu.ent.pastDue": "Payment past due — update your payment method",
    "stu.ent.activeSection": "Active purchases",
    "stu.ent.expiredSection": "Expired purchases",
    "stu.ent.expiredAccessTooltip": "Your access to this content has ended",
    "stu.ent.lifetimeBig": "♾ Lifetime access",
    "stu.ent.billingMonthly": "Billed monthly",
    "stu.ent.billingAnnual": "Billed annually",
    "stu.academyPaused": "Academy is temporarily paused",
    "stu.academyPausedDesc": "This academy is currently unavailable. Please contact the instructor for more information.",
    "stu.purchaseSuccess": "Purchase completed successfully! 🎉",
    "stu.purchaseSuccessDesc": "You're now enrolled and can access the content.",
    "stu.paymentCancelled": "Payment was cancelled",
    "stu.paymentCancelledDesc": "You can try again anytime.",
    "stu.contentPreparing": "Content is being prepared ✨",
    "stu.contentPreparingDesc": "Your instructor is preparing great content for you. Check back soon!",
    "stu.academyComingSoonTitle": "An amazing academy is on its way ✨",
    "stu.academyComingSoonOwnerDesc": "Head to the landing page editor to showcase your creations to the world.",
    "stu.academyComingSoonGuestDesc": "Your instructor is putting the finishing touches. Come back soon!",
    "stu.setupLandingPageBtn": "Set up landing page",
    "stu.backToDashboardBtn": "Back to dashboard",
    "stu.backToDwrrahBtn": "Back to Dwrrah",
    "stu.startConversation": "Start a conversation with the instructor",
    "stu.typeMessage": "Type your message...",
    "stu.newMessage": "New message",
    "stu.startDiscussion": "Start a discussion with your classmates",
    "stu.instructor": "Instructor",
    "stu.reply": "Reply",
    "stu.pin": "Pin",
    "stu.unpin": "Unpin",
    "stu.hide": "Hide",
    "stu.show": "Show",
    "stu.replyTo": "Reply to:",
    "stu.aboutCourse": "About the course",
    "stu.courseContent": "Course content",
    "stu.startCourse": "Start course",
    "stu.lesson": "Lesson",
    "stu.lessonsCount": "lessons",
    "stu.module": "Module",
    "stu.hour": "hour",
    "stu.and": "and",
    "stu.minute": "minute",
    "stu.question": "question",
    "stu.free": "Free",
    "stu.content": "Content",
    "stu.discussion": "Discussion",
    "stu.lessonCompleted": "lesson completed",
    "stu.of": "of",
    "stu.noLiveSessions": "No live sessions linked to this course currently",
    "stu.liveSessions": "Live sessions",
    "stu.scheduled": "Scheduled",
    "stu.liveNow": "🔴 Live now",
    "stu.ended": "Ended",
    "stu.cancelled": "Cancelled",
    "stu.join": "Join",
    "stu.joinNow": "Join now",
    "stu.details": "Details",
    "stu.recording": "Recording",
    "stu.private": "Private",
    "stu.group": "Group",
    "stu.loginFreeAccess": "Log in to start free access",
    "stu.startFreeAccess": "Start free access",
    "stu.alreadyHaveFreeAccess": "You already have free access to this product",
    "stu.freeAccessActivated": "Free access activated successfully! 🎉",
    "stu.freeAccessError": "An error occurred while activating free access",
    "stu.commentsAndQuestions": "Comments & Questions",
    "stu.replyToAuthor": "Reply to",
    "stu.writeComment": "Write your comment or question...",
    "stu.noComments": "No comments yet. Be the first to comment!",
    "stu.unknown": "Unknown",
    "stu.noLessonContent": "No content in this lesson",
    "stu.notesTitle": "My Notes 📝",
    "stu.notesPlaceholder": "Write your personal notes here...",
    "stu.notesSaving": "Saving...",
    "stu.notesSaved": "Saved",
    "stu.notesChars": "chars",
    "stu.contentLocked": "This content is currently unavailable",
    "stu.requiresSubscription": "This content requires a subscription",
    "stu.viewProduct": "View product",
    "stu.orChooseOther": "Or choose from other options",
    "stu.loginFirst": "Log in first to continue",
    "stu.course": "Course",
    "stu.bundle": "Bundle",
    "stu.membership": "Membership",
    "stu.sessionPackage": "Session package",
    "stu.product": "Product",
    "stu.productContents": "Product contents",
    "stu.freeAccessAvailable": "Free access available",
    "stu.notifications": "Notifications",
    "stu.unread": "unread",
    "stu.noNotifications": "No notifications",
    "stu.viewAllNotifications": "View all notifications",
    "support.title": "Support & reports",
    "support.type": "Report type",
    "support.description": "Description",
    "support.placeholder": "Describe your issue here...",
    "support.submit": "Send report",
    "support.success": "Report sent successfully",
    "support.error": "Failed to send",
    "support.descRequired": "Description is required",
    "support.descTooLong": "Description too long (max 2000 chars)",
    "support.studentHint": "Send your report to the academy team",
    "support.platformHint": "Send your report to the platform team",
    "support.types.bug": "Bug",
    "support.types.feature_request": "Feature request",
    "support.types.account_issue": "Account issue",
    "support.types.payment_issue": "Payment issue",
    "support.types.other": "Other",
    "support.tab": "Support & Reports",
    "support.history": "My reports",
    "support.empty": "No reports yet",
    "support.loadError": "Failed to load reports",
    "stu.now": "Now",
    "stu.minsAgo": "m",
    "stu.hoursAgo": "h",
    "stu.daysAgo": "d",
    "stu.couponCode": "Coupon code",
    "stu.discount": "Discount",
    "stu.hasAccess": "You have access to this product",
    "stu.loginToGetFree": "to get it for free",
    "stu.loginToPurchase": "to purchase",
    "stu.getItFree": "Get it for free",
    "stu.subscribeNow": "Subscribe now",
    "stu.buyNow": "Buy now",
    "stu.refundPolicy": "Refund policy:",
    "stu.refund7": "7 days",
    "stu.refund14": "14 days",
    "stu.refund30": "30 days",
    "stu.paymentNotConfigured": "This product is not available for purchase currently. Please contact the academy.",
    "stu.checkoutError": "An error occurred while creating the checkout session",
    "stu.noPaymentUrl": "No payment URL returned",
    "stu.purchaseError": "An error occurred during purchase",
    "stu.noIncludedSessions": "No included live sessions currently",
    "stu.includedSessions": "Included live sessions",
    "stu.writeAnswer": "Type your answer here...",
    "stu.correctAnswer": "Correct answer:",
    "stu.passedQuiz": "Great! You passed the quiz",
    "stu.failedQuiz": "You did not pass the quiz",
    "stu.score": "Score",
    "stu.points": "Points",
    "stu.passingGrade": "Passing grade",
    "stu.allAttemptsUsed": "All attempts used. Contact the instructor.",
    "stu.requestSentToInstructor": "A request has been sent to the instructor for an extra attempt.",
    "stu.canContinue": "You can continue despite not passing the quiz.",
    "stu.cannotRetry": "Retry is not allowed.",
    "stu.timeExceededTitle": "Time's up",
    "stu.timeExceededDesc": "The quiz was submitted automatically because the time limit was reached.",
    "stu.submitError": "Could not submit the quiz",
    "stu.retryAttempt": "Retry",
    "stu.continue": "Continue",
    "stu.reviewAnswers": "Review answers",
    "stu.previewSubmitDisabled": "Simulation — submit works locally, result is not saved",
    "stu.attempts": "Attempts:",
    "stu.attemptsExhausted": "Attempts exhausted. You can continue.",
    "stu.myAnalytics": "My Analytics",
    "stu.preparingCharts": "Preparing charts...",
    "stu.allCourses": "All courses",
    "stu.chartsAppearAfter": "Charts will appear here after starting courses, quizzes, or booking sessions.",
    "stu.courseProgress": "Course progress",
    "stu.noCourses": "No courses",
    "stu.progressAfterEnroll": "Progress will appear here after enrolling in your first course.",
    "stu.weeklyActivity": "Weekly learning activity",
    "stu.lessonsTab": "Lessons",
    "stu.timeTab": "Time (min)",
    "stu.completedLessons": "Completed lessons",
    "stu.activityAfterLessons": "Your daily activity will appear here after completing lessons.",
    "stu.quizPerformance": "Quiz performance",
    "stu.passRate": "Pass rate",
    "stu.attempt": "attempt",
    "stu.avgTimePerQuiz": "Avg time per quiz",
    "stu.noQuizYet": "No quizzes taken yet.",
    "stu.sessionAttendance": "Session attendance",
    "stu.attended": "Attended",
    "stu.booked": "Booked",
    "stu.noShow": "No show",
    "stu.cancelledStatus": "Cancelled",
    "stu.noSessionsBooked": "No sessions booked yet.",
    "stu.milestoneTimeline": "Learning milestones",
    "stu.courseEnrollment": "Course enrollment",
    "stu.courseCompletion": "Course completion",
    "stu.certIssued": "Certificate issued",
    "stu.certFor": "Certificate:",
    "stu.monthlyActive": "Monthly",
    "stu.quarterlyActive": "Quarterly",
    "stu.annuallyActive": "Annually",

    // Discussion & Comments
    "stu.startDiscussion": "Start the discussion with your classmates",
    "stu.reply": "Reply",
    "stu.unpin": "Unpin",
    "stu.pin": "Pin",
    "stu.show": "Show",
    "stu.hide": "Hide",
    "stu.replyTo": "Reply to:",
    "stu.cancel": "Cancel",
    "stu.writeMessage": "Write your message...",
    "stu.instructor": "Instructor",
    "stu.student": "Student",
    "stu.instructorBadge": "Instructor",
    "stu.unknown": "Unknown",
    "stu.commentsTitle": "Comments & Questions",
    "stu.writeComment": "Write your comment or question...",
    "stu.noCommentsYet": "No comments yet. Be the first to comment!",
    "stu.newCommentNotif": "New comment from",
    "stu.replyNotif": "Reply to your comment",
    "stu.instructorReply": "Instructor replied:",

    // Sidebar
    "stu.contentTab": "Content",
    "stu.discussionTab": "Discussion",
    "stu.lessonCompleted": "lesson completed",
    "stu.free": "Free",
    "stu.questionCount": "question",

    // Quiz
    "stu.correctAnswer": "Correct answer:",
    "stu.typeAnswer": "Type your answer here...",
    "stu.retryQuiz": "Retry",
    "stu.passingGrade": "Passing grade:",
    "stu.questionsCount": "question",
    "stu.minutesLabel": "minutes",
    "stu.exhaustedBlock": "All attempts exhausted. Contact your instructor.",
    "stu.exhaustedRequest": "A request has been sent to the instructor for an extra attempt.",
    "stu.exhaustedContinue": "Attempts exhausted. You can continue.",

    // Live Session Block
    "stu.noSessionSelected": "No live session selected",
    "stu.sessionNotFound": "Session not found",
    "stu.sessionEnded": "Session ended",
    "stu.sessionCancelled": "Session cancelled",
    "stu.liveNowLabel": "🔴 Live Now",
    "stu.aboutToStart": "Session is about to start",
    "stu.bookSeat": "Book your seat",
    "stu.retry": "Retry",
    "stu.startsIn": "Starts in",
    "stu.bookedConfirm": "✓ Booked — you can join when the session starts",
    "stu.dayUnit": "day",
    "stu.hourAndMin": "hour and",
    "stu.failedLoadRoom": "Failed to load meeting room",

    // File & Quiz inline blocks
    "stu.loadingFile": "Loading file...",
    "stu.viewOnly": "View only",
    "stu.attachedFile": "Attached file",
    "stu.quizInlineLoading": "Loading...",
    "stu.quizPassingScore": "Passing score",
    "stu.start": "Start",

    // Time ago
    "stu.agoMin": "{n}m ago",
    "stu.agoHour": "{n}h ago",
    "stu.agoDay": "{n}d ago",

    // Milestones (analytics)
    "stu.milestonesAppear": "Your achievements like enrollments, completions, and certificates will appear here.",
    "stu.progress": "Progress",
    "stu.minutes": "minutes",
    "stu.avg": "Average",

    // Instructor page titles & descriptions
    "ins.pg.coursesTitle": "Courses",
    "ins.pg.coursesDesc": "Create and manage your courses",
    "ins.pg.productsTitle": "Products",
    "ins.pg.productsDesc": "Create products, set prices, and define included content",
    "ins.pg.studentsTitle": "Students",
    "ins.pg.studentsDesc": "Track your students and their engagement",
    "ins.pg.sessionsTitle": "Scheduled Sessions",
    "ins.pg.sessionsDesc": "Create live sessions and interact with your students",
    "ins.pg.messagesTitle": "Messages",
    "ins.pg.messagesDesc": "Communicate with your students",
    "ins.pg.commentsTitle": "Comments",
    "ins.pg.commentsDesc": "Manage student comments on lessons",
    "ins.pg.walletTitle": "Wallet",
    "ins.pg.walletDesc": "Track your balance and financial transactions",
    "ins.pg.couponsTitle": "Coupons",
    "ins.pg.couponsDesc": "Create discount coupons for your products",
    "ins.pg.pagesTitle": "Public Pages",
    "ins.pg.pagesDesc": "Create and customize your academy website pages",
    "ins.pg.domainsTitle": "Domains",
    "ins.pg.domainsDesc": "Manage your academy URLs",
    "ins.pg.teamTitle": "Team",
    "ins.pg.teamDesc": "Manage your academy team members and roles",
    "ins.pg.exportTitle": "Data Export",
    "ins.pg.exportDesc": "Export your academy data as CSV or JSON",
    "ins.pg.emailTitle": "Auto Email",
    "ins.pg.emailDesc": "Send automated emails to students on specific events",
    "ins.pg.certsTitle": "Certificate Templates",
    "ins.pg.certsDesc": "Customize certificate designs issued to students",
    "ins.pg.notifTitle": "Notification History",
    "ins.pg.notifDesc": "All incoming notifications",
    "ins.pg.questionBankTitle": "Question Bank",
    "ins.pg.questionBankDesc": "Create and manage questions for your quizzes",
    "ins.pg.onboardingTitle": "Getting Started",
    "ins.pg.onboardingDesc": "Steps to set up your academy",
    "ins.pg.dashboardTitle": "Dashboard",
    "ins.pg.dashboardDesc": "Overview of your academy performance",
    "ins.pg.settingsTitle": "Settings",
    "ins.pg.settingsDesc": "General academy settings",

    // Instructor page common UI
    "ins.pg.newCourse": "New Course",
    "ins.pg.newProduct": "New Product",
    "ins.pg.newCoupon": "New Coupon",
    "ins.pg.newSession": "New Session",
    "ins.pg.newPage": "New Page",
    "ins.pg.inviteStudent": "Invite Student",
    "ins.pg.inviteMember": "Invite Member",
    "ins.pg.export": "Export",
    "ins.pg.sendEmail": "Send Message",
    "ins.pg.emailLog": "Send Log",
    "ins.pg.readAll": "Mark All Read",

    // Instructor status tabs
    "ins.tab.all": "All",
    "ins.tab.draft": "Draft",
    "ins.tab.published": "Published",
    "ins.tab.paused": "Paused",
    "ins.tab.archived": "Archived",
    "ins.tab.scheduled": "Scheduled",
    "ins.tab.live": "Live Now",
    "ins.tab.completed": "Completed",
    "ins.tab.cancelled": "Cancelled",
    "ins.tab.pending": "Pending Reply",
    "ins.tab.individual": "Individual Chats",
    "ins.tab.groups": "Groups",

    // Instructor empty states
    "ins.empty.courses": "No courses yet. Click 'New Course' to get started.",
    "ins.empty.coursesFiltered": "No matching results.",
    "ins.empty.products": "No products yet. Create your first product to start selling.",
    "ins.empty.students": "No students yet. Invite students to get started.",
    "ins.empty.studentsFiltered": "No matching results.",
    "ins.empty.coupons": "No coupons yet. Create your first discount coupon.",
    "ins.empty.couponsSearch": "No search results",
    "ins.empty.pages": "No pages yet. Click 'New Page' to get started.",
    "ins.empty.team": "No members yet. Invite your first team member.",
    "ins.empty.comments": "No comments awaiting reply 🎉",
    "ins.empty.commentsAll": "No comments",
    "ins.empty.messages": "Select a conversation to start",
    "ins.empty.notif": "No notifications",
    "ins.empty.sessions": "Create live sessions and interact with your students",

    // Instructor toast messages
    "ins.toast.deleted": "Deleted",
    "ins.toast.saved": "Saved",
    "ins.toast.activated": "Activated",
    "ins.toast.deactivated": "Deactivated",
    "ins.toast.updated": "Updated",
    "ins.toast.sent": "Sent",
    "ins.toast.created": "Created",
    "ins.toast.error": "Error",
    "ins.toast.replySent": "Reply sent",
    "ins.toast.pinned": "Pinned",
    "ins.toast.unpinned": "Unpinned",
    "ins.toast.shown": "Comment shown",
    "ins.toast.hidden": "Comment hidden",
    "ins.toast.roleChanged": "Member role changed.",
    "ins.toast.revoked": "Membership revoked.",
    "ins.toast.inviteSent": "Invitation sent.",
    "ins.toast.exportSuccess": "Export completed.",
    "ins.toast.pageCreated": "New page created.",
    "ins.toast.pageDeleted": "Page deleted.",
    "ins.toast.homepageSet": "Set as homepage.",
    "ins.toast.cantDeleteHome": "Cannot delete homepage. Set another page as homepage first.",
    "ins.toast.certSaved": "Certificate template saved",
    "ins.toast.signatureUploaded": "Signature uploaded",
    "ins.toast.sessionCreated": "Session created",
    "ins.toast.sessionCancelled": "Session cancelled",
    "ins.toast.streamStarted": "Stream started — opening session...",
    "ins.toast.slugSaved": "Academy link updated.",
    "ins.toast.domainSaved": "Domain saved. Check DNS settings then verify.",
    "ins.toast.domainVerified": "Custom domain activated.",
    "ins.toast.domainNotVerified": "Check DNS settings and try again.",
    "ins.toast.domainRemoved": "Custom domain removed.",
    "ins.toast.productDeleted": "Product deleted.",
    "ins.toast.courseDeleted": "Course deleted.",
    "ins.toast.couponDeleted": "Coupon deleted.",
    "ins.toast.cantPublish": "Cannot publish",
    "ins.toast.cantPublishDesc": "Link your bank account via Stripe first to publish paid products.",
    "ins.toast.sessionError": "Error creating session",
    "ins.toast.enterTitle": "Please enter a session title",
    "ins.toast.selectDate": "Select date and time",

    // Instructor permissions
    "ins.noAccess": "You don't have permission to access this page.",

    // Instructor Stripe gate
    "ins.stripe.linkAccount": "Link your account to receive payments",
    "ins.stripe.freePublishNote": "You can publish free products directly, but paid products remain as draft until you link your payment account.",
    "ins.stripe.linkBtn": "Link Account",

    // Instructor wallet
    "ins.wallet.monthRevenue": "This month's revenue:",
    "ins.wallet.payoutsHeld": "Payouts on hold",

    // Instructor comments
    "ins.comments.pendingReply": "Pending reply",
    "ins.comments.allCourses": "All courses",
    "ins.comments.replyPlaceholder": "Write your reply...",
    "ins.comments.replyBtn": "Reply",
    "ins.comments.replyLink": "Reply ↩",

    // Instructor sessions form
    "ins.session.createTitle": "Create New Session",
    "ins.session.titleLabel": "Session Title *",
    "ins.session.titlePlaceholder": "e.g., Weekly Review Session...",
    "ins.session.descLabel": "Description (optional)",
    "ins.session.descPlaceholder": "Short description...",
    "ins.session.typeLabel": "Session Type",
    "ins.session.groupType": "Group",
    "ins.session.privateType": "Private (1-on-1)",
    "ins.session.durationLabel": "Duration (minutes)",
    "ins.session.dateLabel": "Date & Time *",
    "ins.session.capacityLabel": "Capacity",
    "ins.session.once": "Once",
    "ins.session.weekly": "Weekly",
    "ins.session.biweekly": "Biweekly",
    "ins.session.calendarBanner": "💡 Link Google Calendar to auto-sync your session schedule",
    "ins.session.linkCalendar": "Link Calendar",
    "ins.session.sessionCount": "session",
    "ins.session.duplicated": "Session data copied — set the date and save",

    // Instructor domains
    "ins.domain.academyLink": "Academy Link",
    "ins.domain.linkLocked": "Link is locked because custom domain is active. Students access via custom domain.",
    "ins.domain.linkLabel": "Your academy link on the platform",
    "ins.domain.linkHint": "Lowercase letters, numbers, and hyphens only. 3 characters minimum.",
    "ins.domain.slugMinError": "Link must be at least 3 characters",
    "ins.domain.slugTaken": "This link is taken. Try another name.",
    "ins.domain.slugReserved": "This link is reserved. Try another name.",
    "ins.domain.customDomain": "Custom Domain",
    "ins.domain.proRequired": "Pro Plan",
    "ins.domain.proUpgrade": "Custom domain is available on the Pro plan only.",
    "ins.domain.upgradePlan": "Upgrade Plan",
    "ins.domain.dnsSettings": "Required DNS settings:",
    "ins.domain.verifyNow": "Verify Now",
    "ins.domain.remove": "Remove",
    "ins.domain.activeDomain": "Domain is active. Students access via",
    "ins.domain.disabledDomain": "Custom domain disabled due to plan downgrade. Upgrade to Pro to re-enable.",
    "ins.domain.comingSoonBadge": "Coming Soon ✨",
    "ins.domain.comingSoonMsg": "We're preparing an amazing custom domain experience for you — stay tuned!",
    "ins.domain.statusSuspended": "Suspended",
    "ins.domain.statusRemovalPending": "Removal pending",
    "ins.domain.suspendedBanner": "Your custom domain is currently suspended due to plan or subscription state. Students are automatically redirected to your platform academy link with no error. Upgrade your plan or renew the subscription to re-enable the domain.",
    "ins.domain.removalPendingBanner": "Domain removal has been requested. The domain stays active and redirects students to your platform link for 7 days, then is permanently removed. You can cancel the request or remove it immediately.",
    "ins.domain.removalPendingSince": "Removal requested on",
    "ins.domain.removalPurgeOn": "Final purge on",
    "ins.domain.requestRemoval": "Request domain removal",
    "ins.domain.cancelRemoval": "Cancel removal request",
    "ins.domain.removeNow": "Remove immediately",
    "ins.domain.confirmRequestRemoval": "The domain will be removed in 7 days. Students will be redirected to your platform link in the meantime.\n\n⚠️ Important: To ensure students see the friendly redirect instead of a browser DNS error, do NOT remove the CNAME record at your domain registrar before the 7 days are over. If you remove it now, students will see a raw DNS error page that we cannot intercept.\n\nContinue?",
    "ins.domain.confirmCancelRemoval": "Cancel the pending removal request?",
    "ins.domain.confirmRemoveNow": "The domain will be disconnected immediately and will stop working. Continue?",
    "ins.domain.removalRequested": "Removal request saved",
    "ins.domain.removalCancelled": "Removal request cancelled",
    "ins.domain.removedNow": "Domain disconnected",
    "ins.domain.removeNowWarning": "Warning: after immediate removal, students will lose access to your academy on this domain. We recommend notifying them in advance via the announcement banner before continuing.",
    "ins.domain.openAnnouncementBanner": "Open announcement banner",
    "ins.domain.providerAuthError": "Could not connect to the domain provider. Please review the platform Cloudflare API settings.",

    // Instructor pages
    "ins.pages.published": "Published",
    "ins.pages.draft": "Draft",
    "ins.pages.setHomepage": "Set as homepage",
    "ins.pages.preview": "Preview",

    // Instructor email templates
    "ins.email.proOnly": "Available on Pro plan only. Upgrade to enable automated emails.",

    // Instructor certificates
    "ins.cert.selectCourse": "Select Course",
    "ins.cert.defaultAll": "Default Template (all courses)",
    "ins.cert.perCourseHint": "Customize a template per course, or use the default for all",
    "ins.cert.templateStyle": "Template Style",
    "ins.cert.elegant": "Elegant",
    "ins.cert.elegantDesc": "Gradient with colored top bar",
    "ins.cert.minimal": "Minimal",
    "ins.cert.minimalDesc": "Simple borders, clean design",
    "ins.cert.formal": "Formal",
    "ins.cert.formalDesc": "Double borders with corner ornaments",
    "ins.cert.accentColor": "Accent Color",
    "ins.cert.settingsTitle": "Certificate Settings",
    "ins.cert.customText": "Custom Text (optional)",
    "ins.cert.customTextPlaceholder": "Added below the course name on the certificate...",
    "ins.cert.showLogo": "Show Academy Logo",
    "ins.cert.showLogoDesc": "Logo appears at the top of the certificate",
    "ins.cert.addSignature": "Add Signature",
    // Issue Certificate (manual) — Phase 3
    "ins.issueCert.navLabel": "Issue Certificate",
    "ins.issueCert.title": "Issue Certificate Manually",
    "ins.issueCert.subtitle": "Issue a certificate for any student in any course without requiring completion.",
    "ins.issueCert.openDialog": "Start Manual Issuance",
    "ins.issueCert.backToTemplates": "Back to Certificate Templates",
    "ins.issueCert.bypassNote": "Manual issuance bypasses course completion. Duplicate certificates for the same (student + course) are blocked automatically.",
    "ins.issueCert.f1Title": "Template Choice",
    "ins.issueCert.f1Desc": "Uses the saved per-course template or the default (custom HTML included).",
    "ins.issueCert.f2Title": "In-app Notification",
    "ins.issueCert.f2Desc": "Student gets an instant bell notification linking to their certificates.",
    "ins.issueCert.f3Title": "Email",
    "ins.issueCert.f3Desc": "The certificate-issued email template is sent automatically.",
    "ins.issueCert.lastIssued": "Last issued certificate",
    "ins.issueCert.noPermissionTitle": "You don't have permission to issue manually",
    "ins.issueCert.noPermissionDesc": "Manual issuance is available to academy owner and managers only.",
    "ins.cert.addSignatureDesc": "Signature appears at the bottom",
    "ins.cert.signatureImage": "Signature Image",
    "ins.cert.changeSignature": "Change Signature",
    "ins.cert.uploadSignature": "Upload Signature Image",
    "ins.cert.saveTemplate": "Save Template",
    "ins.cert.previewTemplate": "Preview",
    "ins.cert.previewNote": "Template will be applied to new certificates automatically",
    "ins.cert.livePreview": "Live preview",
    "ins.cert.pdfFinalNote": "Final student artifact: PDF",
    "ins.cert.pdfNote": "This HTML is used as a design source only — the final certificate delivered to the student is a PDF.",
    "ins.cert.customHtml": "Custom HTML/CSS (advanced)",
    "ins.cert.customHtmlDesc": "Write your own template — for advanced users only",
    "ins.cert.customHtmlLabel": "Custom HTML/CSS template",
    "ins.cert.customHtmlHint": "Use these variables in your template:",
    "ins.cert.tabTemplates": "Templates",
    "ins.cert.tabAuto": "Auto issuance",
    "ins.cert.tabIssued": "Issued certificates",
    "ins.cert.autoExplain": "Enable automatic certificate issuance for any course to issue a certificate the moment a student completes it. Auto issuance uses the per-course template (or the default).",
    "ins.cert.autoOnDesc": "A certificate will be issued automatically on course completion",
    "ins.cert.autoOffDesc": "No automatic certificate — you can still issue manually from the Issued tab",
    "ins.cert.autoSaved": "Auto-issuance setting updated",
    "ins.cert.draft": "Draft",
    "ins.cert.noCourses": "No courses in this academy yet",
    "ins.cert.totalIssued": "Total certificates: {count}",
    "ins.cert.refresh": "Refresh",
    "ins.cert.issueManual": "Issue manually",
    "ins.cert.fStudent": "Student",
    "ins.cert.fStudentPh": "Search by student name...",
    "ins.cert.fCourse": "Course",
    "ins.cert.fStatus": "Status",
    "ins.cert.fMode": "Issuance type",
    "ins.cert.fFrom": "From",
    "ins.cert.fTo": "To",
    "ins.cert.all": "All",
    "ins.cert.clearFilters": "Clear filters",
    "ins.cert.colStudent": "Student",
    "ins.cert.colCourse": "Course",
    "ins.cert.colMode": "Type",
    "ins.cert.colDate": "Issued at",
    "ins.cert.colStatus": "Status",
    "ins.cert.colActions": "Actions",
    "ins.cert.modeAuto": "Automatic",
    "ins.cert.modeManual": "Manual",
    "ins.cert.statusIssued": "Issued",
    "ins.cert.statusRevoked": "Revoked",
    "ins.cert.download": "Download PDF",
    "ins.cert.view": "View",
    "ins.cert.landscape": "Landscape",
    "ins.cert.portrait": "Portrait",
    "ins.cert.previewMatchesView": "This preview is identical to what the student will see when opening the certificate.",
    "cert.view.title": "View certificate",
    "cert.view.print": "Print / Save as PDF",
    "cert.view.notFound": "Certificate not found",
    "cert.view.loadError": "Could not load certificate",
    "cert.view.noAccess": "You may not have access to this certificate.",
    "ins.cert.noResults": "No certificates match the current filters",
    "ins.cert.regenFailed": "Could not generate PDF — please retry",

    // Instructor data export
    "ins.export.settingsTitle": "Export Settings",
    "ins.export.dataType": "Data Type",
    "ins.export.fileFormat": "File Format",
    "ins.export.exportBtn": "Export",
    "ins.export.backupTitle": "Full Backup",
    "ins.export.backupDesc": "Export all academy data as a single multi-sheet Excel file.",
    "ins.export.backupNote": "Very large academies may take longer. If it fails, export each table individually.",
    "ins.export.backupBtn": "Download full backup (Excel)",
    "ins.export.group.people": "Students & Enrollments",
    "ins.export.group.content": "Learning Content",
    "ins.export.group.finance": "Finance",
    "ins.export.group.sessionsCerts": "Sessions & Certificates",
    "ins.export.group.support": "Support",
    "ins.export.ent.students": "Students",
    "ins.export.ent.enrollments": "Enrollments",
    "ins.export.ent.product_accesses": "Product accesses",
    "ins.export.ent.courses": "Courses",
    "ins.export.ent.products": "Products",
    "ins.export.ent.quizzes": "Quizzes",
    "ins.export.ent.quiz_attempts": "Quiz attempts",
    "ins.export.ent.course_results": "Course results",
    "ins.export.ent.payments": "Payments",
    "ins.export.ent.wallet_transactions": "Wallet transactions",
    "ins.export.ent.bank_transfer_requests": "Bank transfer requests",
    "ins.export.ent.memberships": "Membership subscriptions",
    "ins.export.ent.sessions": "Sessions",
    "ins.export.ent.session_bookings": "Session bookings",
    "ins.export.ent.certificates": "Certificates",
    "ins.export.ent.support_reports": "Support reports",
    // Admin data export
    "adm.export.sidebar": "Data Export",
    "adm.export.title": "Platform Data Export",
    "adm.export.desc": "Export any academy or the entire platform as Excel, CSV, or JSON.",
    "adm.export.scope": "Scope",
    "adm.export.allAcademies": "All academies",
    "adm.export.group.platform": "Platform data (admin only)",
    "adm.export.ent.academies": "Academies",
    "adm.export.ent.instructor_subscriptions": "Instructor subscriptions",
    "adm.export.ent.platform_admins": "Platform admins",
    "adm.export.ent.bank_transfer_escalations": "Bank transfer escalations",
    "adm.export.ent.admin_audit_logs": "Admin audit logs",
    "adm.export.ent.pro_feature_requests": "Pro feature requests",
    "adm.export.ent.ops_health_snapshots": "Ops health snapshots",
    "adm.export.ent.email_logs": "Email logs",
    "adm.export.academyBackupDesc": "One-academy backup (or all academies depending on scope) in a single Excel file.",
    "adm.export.platformBackupTitle": "Full Platform Backup",
    "adm.export.platformBackupDesc": "All platform data + all academy data in one Excel file. Heavy — for full backups only.",
    "adm.export.platformBackupBtn": "Download full platform backup",

    // Instructor notification types
    "ins.notif.newComment": "New comment",
    "ins.notif.newMessage": "New message",
    "ins.notif.enrollment": "Enrollment",
    "ins.notif.discussion": "Discussion",
    "ins.notif.grant": "Grant",
    "ins.notif.purchase": "New purchase",
    "ins.notif.newStudent": "New student",
    "ins.notif.courseCompleted": "Course completed",
    "ins.notif.sessionCancelled": "Session cancelled",
    "ins.notif.certificate": "Certificate",
    "ins.notif.allTypes": "All types",
    "ins.notif.searchPlaceholder": "Search notifications...",
    "ins.notif.loading": "Loading...",
    "ins.notif.page": "Page",
    "ins.notif.prev": "Previous",
    "ins.notif.next": "Next",
    "ins.notif.proFeatureRequest": "Feature access request",
    "ins.notif.newCommentTitle": "New lesson comment",
    "ins.notif.newDiscussionTitle": "New course discussion message",
    "ins.notif.inviteConverted": "Invite converted",
    "ins.sentInvites": "Sent Invites",

    // Feature requests page (instructor)
    "ins.featureRequests.nav": "Feature Requests",
    "ins.featureRequests.title": "Feature Access Requests",
    "ins.featureRequests.subtitle": "Pro features your students requested — use this as a signal of what they need",
    "ins.featureRequests.totalRequests": "Total requests",
    "ins.featureRequests.empty": "No requests yet",
    "ins.featureRequests.unknownStudent": "Student",

    // Instructor email logs
    "ins.emailLog.title": "Email Log",
    "ins.emailLog.desc": "Track sent email status",
    "ins.emailLog.total": "Total",
    "ins.emailLog.sentLabel": "Sent",
    "ins.emailLog.failedLabel": "Failed",
    "ins.emailLog.suppressedLabel": "Suppressed",
    "ins.emailLog.noEmails": "No emails in this period",
    "ins.emailLog.recipient": "Recipient",
    "ins.emailLog.type": "Type",
    "ins.emailLog.status": "Status",
    "ins.emailLog.error": "Error",
    "ins.emailLog.date": "Date",
    "ins.emailLog.searchPlaceholder": "Search by email or type...",
    "ins.emailLog.sent": "Sent",
    "ins.emailLog.failed": "Failed",
    "ins.emailLog.suppressed": "Suppressed",

    // Instructor email campaign
    "ins.campaign.title": "Send Message",
    "ins.campaign.desc": "Send a custom email to your students",
    "ins.campaign.subject": "Subject",
    "ins.campaign.subjectPlaceholder": "Email subject...",
    "ins.campaign.content": "Content",
    "ins.campaign.contentPlaceholder": "Write your message...",
    "ins.campaign.sendBtn": "Send",
    "ins.campaign.sentTitle": "Sent!",
    "ins.campaign.sentDesc": "Messages sent successfully",
    "ins.campaign.viewLog": "Email Log",
    "ins.campaign.newMessage": "Send New Message",
    "ins.campaign.recipients": "Recipients",
    "ins.campaign.selectAll": "Select All",
    "ins.campaign.deselectAll": "Deselect All",
    "ins.campaign.noStudents": "No students",
    "ins.campaign.subjectRequired": "Subject is required",
    "ins.campaign.selectOne": "Select at least one student",
    "ins.campaign.student": "student",

    // Instructor session form extras
    "ins.session.linkCourse": "Link to course (optional)",
    "ins.session.noCourseLink": "No link — standalone session",
    "ins.session.free": "Free",
    "ins.session.allowBooking": "Allow booking",
    "ins.session.enableRecording": "Enable recording",
    "ins.session.studentAccess": "Student access",
    "ins.session.recurrence": "Recurrence",
    "ins.session.recurrenceEnd": "Recurrence end date",
    "ins.session.addContent": "Add session content (optional)",
    "ins.session.freeText": "Free text",
    "ins.session.image": "Image",
    "ins.session.file": "File",
    "ins.session.cta": "Call to action",
    "ins.session.createBtn": "Create Session",
    "ins.session.noSessions": "No sessions",
    "ins.session.noSessionsStatus": "No sessions with this status",
    "ins.session.firstSession": "Create your first session by clicking \"New Session\" above",
    "ins.session.searchPlaceholder": "Search sessions...",
    "ins.session.invitesSent": "{n} invites sent",

    // Sessions page
    "ins.sessions.title": "Scheduled Sessions",
    "ins.sessions.newBtn": "New Session",
    "ins.sessions.createTitle": "Create New Session",
    "ins.sessions.sessionTitle": "Session Title *",
    "ins.sessions.sessionTitlePlaceholder": "e.g., Weekly review session...",
    "ins.sessions.descOptional": "Description (optional)",
    "ins.sessions.descPlaceholder": "Brief description...",
    "ins.sessions.sessionType": "Session Type",
    "ins.sessions.group": "Group",
    "ins.sessions.private": "Private (1-on-1)",
    "ins.sessions.duration": "Duration (minutes)",
    "ins.sessions.dateTime": "Date & Time *",
    "ins.sessions.maxCapacity": "Max Capacity (0 = unlimited)",
    "ins.sessions.calendarBanner": "💡 Connect Google Calendar to auto-sync your session schedules",
    "ins.sessions.linkCalendar": "Connect Calendar",
    "ins.sessions.sessionCount": "{n} session(s)",
    "ins.sessions.enterTitle": "Please enter a session title",
    "ins.sessions.selectDateTime": "Select date and time",
    "ins.sessions.createdMulti": "{n} sessions created",
    "ins.sessions.createdSingle": "Session created successfully",
    "ins.sessions.createError": "Error creating session",
    "ins.sessions.streamStarted": "Stream started — opening session...",
    "ins.sessions.cancelled": "Session cancelled",
    "ins.sessions.deleted": "Deleted",
    "ins.sessions.duplicated": "Session data copied — set the date and save",
    "ins.sessions.noAccess": "You don't have access.",
    "ins.sessions.liveSessions": "Live Sessions",
    "ins.sessions.createLiveSessions": "Create live sessions and interact with your students",
    "ins.sessions.statusAll": "All",
    "ins.sessions.statusScheduled": "Scheduled",
    "ins.sessions.statusLive": "Live Now",
    "ins.sessions.statusCompleted": "Completed",
    "ins.sessions.statusCancelled": "Cancelled",
    "ins.sessions.statusOpenForBooking": "Open for booking",
    "ins.sessions.sortLabel": "Sort",
    "ins.sessions.sortNearest": "Nearest first",
    "ins.sessions.sortFurthest": "Furthest first",
    "ins.sessions.sortNewest": "Newest created",
    "ins.sessions.bookablePreset": "Bookable session",
    "ins.sessions.bookablePresetHint": "Students self-book a slot from your availability",
    "ins.sessions.bookableBadge": "Mode: Bookable session",
    "ins.sessions.convertToRegular": "Convert to scheduled session",
    "ins.sessions.dialog.title": "New session type",
    "ins.sessions.dialog.subtitle": "Pick how the time slot is set. You can edit every detail next.",
    "ins.sessions.dialog.scheduledTitle": "Scheduled session",
    "ins.sessions.dialog.scheduledDesc": "You set the date and time, students join at the scheduled moment.",
    "ins.sessions.dialog.scheduledExamples": "Lectures, workshops, live courses",
    "ins.sessions.dialog.bookableTitle": "Group bookable session",
    "ins.sessions.dialog.bookableDesc": "Small group session with limited capacity; students pick a slot you expose.",
    "ins.sessions.dialog.bookableExamples": "Small workshops, discussion circles, flexible group training",
    "ins.sessions.dialog.consultTitle": "1-on-1 consultation",
    "ins.sessions.dialog.consultDesc": "Private session with a single student who books a slot from your availability.",
    "ins.sessions.dialog.consultExamples": "Personal consult, 1:1 coaching, project review, interview",
    "ins.sessions.dialog.suitableFor": "Suitable for",
    "ins.sessions.dialog.choose": "Choose",
    "ins.sessions.dialog.creatingPreset": "Creating",
    "ins.sessions.dialog.changeType": "Change type",
    "ins.sessions.contentSectionAlwaysVisible": "Session content (shown to students inside the session room)",
    "ins.sessions.coverHint": "Add a cover so the session appears as a polished card.",
    "ins.sessions.salesViaProductsInfo": "This session is sold via products. Create a product and link it to start selling.",
    "ins.sessions.openLandingFromForm": "Customize session landing page",
    "ins.sessions.openLandingEditor": "Design session landing page",
    "ins.sessions.openLandingEditorAfterSave": "Save first, then design the landing page",
    "ins.sessions.salesAndAccess": "Sales & Access",
    "ins.sessions.salesAndAccessHint": "Sessions are sold through products. Create a product and link it to this session.",
    "ins.sessions.linkedProducts": "Linked products",
    "ins.sessions.noLinkedProducts": "No products linked to this session yet.",
    "ins.sessions.linkNewProduct": "Link a new product",
    "ins.sessions.contentSectionTitle": "Session content (optional)",
    "ins.sessions.contentSectionHint": "Shown to students after booking — welcome video, files, notes…",
    "ins.sessions.openForBookingHint": "Open for booking — no fixed date",
    "ins.sessions.bookingQuantity": "Sessions per purchase",
    "ins.sessions.once": "Once",
    "ins.sessions.weekly": "Weekly",
    "ins.sessions.biweekly": "Biweekly",
    "ins.sessions.recurrenceHint": "A session will be created {interval} until {date}",
    "ins.sessions.everyWeek": "every week",
    "ins.sessions.everyTwoWeeks": "every two weeks",

    // Course lifecycle (archive / hard delete)
    "ins.courses.statusArchived": "Archived",
    "ins.courses.archive": "Archive",
    "ins.courses.unarchive": "Republish",
    "ins.courses.hardDelete": "Delete permanently",
    "ins.courses.deleteOrArchive": "Delete / Archive",
    "ins.courses.confirmArchiveTitle": "Archive course?",
    "ins.courses.confirmArchiveDesc": "Course will be hidden from sale and the public catalog, but existing students keep their access.",
    "ins.courses.confirmHardDeleteTitle": "Delete permanently?",
    "ins.courses.confirmHardDeleteDesc": "The course and all its content will be permanently removed. This cannot be undone.",
    "ins.courses.cannotDeleteHasDeps": "Cannot permanently delete — there is linked data. Use Archive to preserve student access.",
    "ins.courses.depEnrollments": "Active enrollments",
    "ins.courses.depCertificates": "Issued certificates",
    "ins.courses.depPayments": "Payments",
    "ins.courses.depResults": "Student progress",
    "ins.courses.depComments": "Comments / discussions",
    "ins.courses.depProducts": "Linked products",
    "ins.courses.archiveInsteadCta": "Archive instead",
    "ins.courses.autoArchivedProducts": "These products were also archived:",
    "ins.courses.archived": "Archived",
    "ins.courses.unarchived": "Republished",
    "ins.courses.deleted": "Permanently deleted",
    "ins.courses.checking": "Checking...",
    "stu.courseArchivedBadge": "Archived",
    "stu.courseArchivedHint": "No longer for sale, but your access is preserved.",

    // CourseForm
    "ins.courseForm.settingsTitle": "Course Settings",
    "ins.courseForm.newCourse": "New Course",
    "ins.courseForm.previewPage": "Preview Sales Page",
    "ins.courseForm.simulateStudent": "Try as Student",
    "ins.courseForm.customizePage": "Customize Page",
    "ins.courseForm.saveChanges": "Save Changes",
    "ins.courseForm.createCourse": "Create Course",
    "ins.courseForm.basicInfo": "Basic Information",
    "ins.courseForm.courseTitleLabel": "Course Title *",
    "ins.courseForm.courseTitlePlaceholder": "e.g., Digital Marketing Basics",
    "ins.courseForm.courseDesc": "Course Description",
    "ins.courseForm.courseDescPlaceholder": "Write a detailed description...",
    "ins.courseForm.category": "Category",
    "ins.courseForm.categoryPlaceholder": "e.g., Marketing",
    "ins.courseForm.language": "Language",
    "ins.courseForm.courseImage": "Course Image",
    "ins.courseForm.clickToUpload": "Click to upload image",
    "ins.courseForm.titleRequired": "Course title is required.",
    "ins.courseForm.saved": "Course settings updated.",
    "ins.courseForm.created": "Course created successfully.",
    "ins.courseForm.notFound": "Course not found or not within academy scope.",
    "ins.courseForm.loadError": "Failed to load course settings. Please try again.",
    "ins.courseForm.loadErrorTitle": "Failed to load course",
    "ins.courseForm.uploadError": "Upload error",
    "ins.courseForm.uploadFailed": "Failed to upload image",

    // CourseContent
    "ins.courseContent.flatStructure": "Flat structure",
    "ins.courseContent.modulesAndLessons": "Modules & Lessons",
    "ins.courseContent.lessonsCount": "{n} lesson(s)",
    "ins.courseContent.preview": "Preview Sales Page",
    "ins.courseContent.simulate": "Try as Student",
    "ins.courseContent.finalExam": "Final Exam",
    "ins.courseContent.settings": "Settings",
    "ins.courseContent.newLesson": "New Lesson",
    "ins.courseContent.newModule": "New Module",
    "ins.courseContent.endOfCourseQuizzes": "End-of-Course Quizzes",
    "ins.courseContent.quizStats": "Quiz Statistics",
    "ins.courseContent.questionsCount": "{n} questions",
    "ins.courseContent.notFound": "Course not found or not within academy scope.",
    "ins.courseContent.loadError": "Failed to load course content. Please try again.",
    "ins.courseContent.loadErrorTitle": "Failed to load course content",
    "ins.courseContent.structureLabel": "Course structure",
    "ins.courseContent.courseLevelLabel": "Course level",
    "ins.courseContent.addFinalExam": "Add final exam",
    "common.moveUp": "Move up",
    "common.moveDown": "Move down",
    "common.collapse": "Collapse",
    "common.expand": "Expand",
    "stu.openSidebar": "Open sidebar",

    // LessonEditor
    "ins.lessonEditor.lessonTitlePlaceholder": "Lesson title",
    "ins.lessonEditor.hidePreview": "Hide Preview",
    "ins.lessonEditor.showPreview": "Show Preview",
    "ins.lessonEditor.save": "Save",
    "ins.lessonEditor.lessonSettings": "Lesson Settings",
    "ins.lessonEditor.published": "Published",
    "ins.lessonEditor.freePreview": "Free Preview",
    "ins.lessonEditor.durationMinutes": "Duration (minutes)",
    "ins.lessonEditor.comments": "Comments",
    "ins.lessonEditor.inheritCourse": "Inherit from course",
    "ins.lessonEditor.enabled": "Enabled",
    "ins.lessonEditor.disabled": "Disabled",
    "ins.lessonEditor.notFound": "Lesson not found or not within academy scope.",
    "ins.lessonEditor.loadError": "Failed to load lesson. Please try again.",
    "ins.lessonEditor.loadErrorTitle": "Failed to load lesson",

    // Course Builder — shared
    "ins.builder.lessonsLabel": "lesson(s)",
    "ins.builder.preview": "Preview",
    "ins.builder.addLesson": "Add lesson",
    "ins.builder.addQuiz": "Quiz",
    "ins.builder.noLessonsYet": "No lessons yet",
    "ins.builder.emptyModulesHint": "No modules yet. Click \"New Module\" to start.",
    "ins.builder.emptyFlatHint": "No lessons yet. Click \"New Lesson\" to start.",
    "ins.builder.quizQuestions": "{n} questions",
    "ins.builder.emptyLesson": "This lesson is empty. Add content to build it.",
    "ins.builder.addContent": "Add content",
    "ins.builder.addQuizAfter": "Add quiz after this lesson",
    "ins.builder.confirmDeleteLessonTitle": "Delete lesson?",
    "ins.builder.confirmDeleteLessonBody": "Lesson \"{title}\" will be permanently deleted along with all its comments, student progress, and attached quizzes. This cannot be undone.",
    "ins.builder.confirmDeleteQuizTitle": "Delete quiz?",
    "ins.builder.confirmDeleteQuizBody": "Quiz \"{title}\" will be permanently deleted along with all its questions and student attempts. This cannot be undone.",
    "ins.builder.confirmDeleteModuleTitle": "Delete module?",
    "ins.builder.confirmDeleteModuleBody": "Module \"{title}\" will be permanently deleted along with all its lessons and quizzes. This cannot be undone.",
    "ins.builder.insertHere": "Insert block here",
    "ins.blockTransfer.moveTitle": "Move blocks to another lesson",
    "ins.blockTransfer.copyTitle": "Copy blocks to another lesson",
    "ins.blockTransfer.moveDesc": "Pick the lesson you want to move the selected blocks into. They will be removed from the current lesson.",
    "ins.blockTransfer.copyDesc": "Pick the lesson you want to copy the selected blocks into. A copy stays in the current lesson.",
    "ins.blockTransfer.confirmMove": "Move blocks",
    "ins.blockTransfer.confirmCopy": "Copy blocks",
    "ins.blockTransfer.selectedCount": "Selected blocks:",
    "ins.blockTransfer.selectedN": "{n} block(s) selected",
    "ins.blockTransfer.noOtherLessons": "No other lessons in this course yet. Create another lesson first.",
    "ins.blockTransfer.selectThisBlock": "Select this block",
    "ins.blockTransfer.moveBtn": "Move to another lesson",
    "ins.blockTransfer.copyBtn": "Copy to another lesson",
    "ins.blockTransfer.clear": "Clear selection",
    "ins.blockTransfer.successTitle": "Done",
    "ins.blockTransfer.movedDesc": "Blocks moved to the selected lesson",
    "ins.blockTransfer.copiedDesc": "Blocks copied to the selected lesson",
    "ins.blockTransfer.targetMissing": "Could not find the target lesson",

    // Block picker
    "ins.blockPicker.title": "Add content",
    "ins.blockPicker.video": "Video",
    "ins.blockPicker.videoDesc": "Uploaded video or external link",
    "ins.blockPicker.text": "Text",
    "ins.blockPicker.textDesc": "Formatted text content",
    "ins.blockPicker.image": "Image",
    "ins.blockPicker.imageDesc": "Illustrative image",
    "ins.blockPicker.file": "PDF File",
    "ins.blockPicker.fileDesc": "View-only file",
    "ins.blockPicker.quiz": "Embedded quiz",
    "ins.blockPicker.quizDesc": "Quiz inside the lesson",
    "ins.blockPicker.liveSession": "Live session",
    "ins.blockPicker.liveSessionDesc": "Embed a scheduled live session",
    "ins.blockPicker.divider": "Divider",
    "ins.blockPicker.dividerDesc": "Visual separator between content",
    "ins.blockPicker.callout": "Callout",
    "ins.blockPicker.calloutDesc": "Notice or tip",
    "ins.blockPicker.externalLink": "External link",
    "ins.blockPicker.externalLinkDesc": "Link to an external page as a lesson source",

    // Block editors
    "ins.blockItem.unknownType": "Unknown type",
    "ins.blockItem.video.youtube": "YouTube",
    "ins.blockItem.video.drive": "Drive",
    "ins.blockItem.video.vimeo": "Vimeo",
    "ins.blockItem.video.upload": "Direct upload",
    "ins.blockItem.video.uploaded": "✓ Video uploaded",
    "ins.blockItem.video.uploadHint": "Click to upload a video (200 MB limit)",
    "ins.blockItem.video.storageTip": "💡 To save storage, prefer uploading large videos to YouTube or Google Drive and pasting the link.",
    "ins.blockItem.video.urlLabel": "Video URL",
    "ins.blockItem.video.durationLabel": "Duration (minutes)",
    "ins.blockItem.video.autoExtracted": "— auto-extracted",
    "ins.blockItem.video.tooBig": "File exceeds 200 MB. Please upload it to YouTube or Google Drive and paste the link.",
    "ins.blockItem.video.linkUnsupported": "Link is incomplete or invalid. Make sure you copied the full URL (starting with https://) from the browser address bar or the platform's “Copy link” button (YouTube/SharePoint/OneDrive/Drive…).",
    "ins.blockItem.video.linkTip": "For SharePoint/OneDrive files, use the “Copy link” button and make sure sharing permissions allow external viewing. If the link copied from the provider or drive itself doesn't work, try copying the URL from the address bar while the video is playing.",
    "ins.blockItem.image.uploaded": "✓ Image uploaded",
    "ins.blockItem.image.uploadHint": "Click to upload an image",
    "ins.blockItem.image.captionPlaceholder": "Image description (optional)",
    "ins.blockItem.file.tooBig": "File exceeds 50 MB. Please upload it to Google Drive and paste the link.",
    "ins.blockItem.file.uploadHint": "Click to upload a PDF (50 MB limit)",
    "ins.blockItem.file.fallbackName": "File",
    "ins.blockItem.file.remove": "Remove",
    "ins.blockItem.file.instructorOnly": "Visible to you only",
    "ins.blockItem.file.titlePlaceholder": "File title (shown to students)",
    "ins.blockItem.file.summaryPlaceholder": "Short summary of the file...",
    "ins.blockItem.callout.info": "Info",
    "ins.blockItem.callout.warning": "Warning",
    "ins.blockItem.callout.tip": "Tip",
    "ins.blockItem.callout.placeholder": "Write the note...",
    "ins.blockItem.live.selectSession": "Choose a scheduled session *",
    "ins.blockItem.live.loading": "Loading...",
    "ins.blockItem.live.noSessions": "No scheduled sessions. Create one first from the Sessions page.",
    "ins.blockItem.live.selected": "Selected",
    "ins.blockItem.live.completionRule": "Lesson completion rule",
    "ins.blockItem.live.rule.session_ended": "After session ends",
    "ins.blockItem.live.rule.attended": "When student attends",
    "ins.blockItem.live.rule.skippable": "Skippable",
    "ins.blockItem.quiz.linked": "Quiz linked",
    "ins.blockItem.quiz.embedHint": "Embedded quiz — save the lesson then create a quiz",
    "ins.blockItem.externalLink.urlLabel": "External page URL *",
    "ins.blockItem.externalLink.titleLabel": "Title (optional)",
    "ins.blockItem.externalLink.titlePlaceholder": "e.g. Reference article",
    "ins.blockItem.externalLink.descLabel": "Short description (optional)",
    "ins.blockItem.externalLink.descPlaceholder": "Tell the student what they will find at this link",

    // Block labels (chips on each block in the editor)
    "ins.blockLabel.video": "Video",
    "ins.blockLabel.text": "Text",
    "ins.blockLabel.image": "Image",
    "ins.blockLabel.file": "PDF File",
    "ins.blockLabel.divider": "Divider",
    "ins.blockLabel.callout": "Callout",
    "ins.blockLabel.quiz": "Embedded quiz",
    "ins.blockLabel.live_session": "Live session",
    "ins.blockLabel.external_link": "External link",
    "ins.blockLabel.audio": "Audio",

    // Audio block editor (instructor)
    "ins.blockPicker.audio": "Audio",
    "ins.blockPicker.audioDesc": "Uploaded audio file or external link",
    "ins.blockItem.audio.linkTab": "Audio link",
    "ins.blockItem.audio.uploadTab": "Upload from device",
    "ins.blockItem.audio.uploadHint": "Click to upload an audio file (MP3/M4A/WAV/OGG — 50 MB limit)",
    "ins.blockItem.audio.uploadingHint": "Uploading audio...",
    "ins.blockItem.audio.removeAudio": "Remove audio file",
    "ins.blockItem.audio.urlLabel": "Audio file URL",
    "ins.blockItem.audio.linkPlaceholder": "Paste a direct MP3 link or SoundCloud URL…",
    "ins.blockItem.audio.titleLabel": "Track title",
    "ins.blockItem.audio.titlePlaceholder": "Short title shown above the player",
    "ins.blockItem.audio.summaryLabel": "Short description",
    "ins.blockItem.audio.summaryPlaceholder": "A short description to help the student understand the audio",
    "ins.blockItem.audio.tooBig": "File exceeds 50 MB",
    "ins.blockItem.audio.uploadFailed": "Audio upload failed, please try again",

    // Quiz question media (instructor)
    "ins.quizForm.media.section": "Question media (optional)",
    "ins.quizForm.media.hint": "Add audio for listening questions, video for watching questions, or an image. Shown above the question text.",
    "ins.quizForm.media.audio": "Audio",
    "ins.quizForm.media.video": "Video",
    "ins.quizForm.media.image": "Image",
    "ins.quizForm.media.remove": "Remove media",
    "ins.quizForm.media.attached": "Media attached",
    "ins.quizForm.option.image": "Option image",
    "ins.quizForm.option.imageRemove": "Remove image",
    "ins.quizForm.option.imageHint": "Click to upload an image (optional)",
    "ins.quizForm.option.mediaSection": "Option media (optional)",
    "ins.quizForm.media.upload": "Upload",

    // QuizEditor
    "ins.quizEditor.titlePlaceholder": "Quiz title",
    "ins.quizEditor.aiGenerate": "AI Generate",
    "ins.quizEditor.save": "Save",
    "ins.quizEditor.saved": "Saved",
    "ins.quizEditor.saveSummary": "Saved {n} questions (added: {added}, deleted: {deleted}).",
    "ins.quizEditor.saveSummaryNew": "Quiz created and {n} questions saved.",
    "ins.quizEditor.saveFailed": "Failed to save the quiz. Please try again.",
    "ins.quizEditor.titleRequired": "Quiz title is required.",
    "ins.quizEditor.created": "Quiz created successfully.",
    "ins.quizEditor.proOnly": "Quizzes are available on Pro plan only.",
    "ins.quizEditor.aiAddedTitle": "Added as draft",
    "ins.quizEditor.aiAddedDesc": "{n} questions added for review. Click \"Save\" after reviewing.",
    "ins.quizEditor.newQuiz": "New Quiz",

    // ProductForm
    "ins.productForm.editTitle": "Edit Product",
    "ins.productForm.newTitle": "New Product",
    "ins.productForm.subtitle": "Set product type, pricing, and included content",
    "ins.productForm.save": "Save",
    "ins.productForm.basicInfo": "Basic Information",
    "ins.productForm.productTitle": "Product Title",
    "ins.productForm.titlePlaceholder": "e.g., Graphic Design Course",
    "ins.productForm.desc": "Description",
    "ins.productForm.descPlaceholder": "Brief product description...",
    "ins.productForm.productImage": "Product Image",
    "ins.productForm.clickToUpload": "Click to upload image",
    "ins.productForm.remove": "Remove",
    "ins.productForm.pricing": "Pricing",
    "ins.productForm.productPage": "Product Landing Page",
    "ins.productForm.productPageDesc": "Design a custom landing page with drag & drop editor",
    "ins.productForm.designPage": "Design Product Page",
    "ins.productForm.refundPolicy": "Refund Policy",
    "ins.productForm.noRefund": "No Refund",
    "ins.productForm.7days": "7 Days",
    "ins.productForm.14days": "14 Days",
    "ins.productForm.30days": "30 Days",
    "ins.productForm.titleRequired": "Please enter a product title.",
    "ins.productForm.savedEdit": "Product updated successfully.",
    "ins.productForm.savedNew": "Product created successfully.",
    "ins.productForm.stripeBanner": "Your product will be saved as draft",
    "ins.productForm.stripeBannerDesc": "To publish and make available, verify your account to receive payments first.",
    "ins.productForm.verifyAccount": "Verify Account",

    // SessionDetail
    "ins.sessionDetail.startStream": "Start Stream",
    "ins.sessionDetail.endSession": "End Session",
    "ins.sessionDetail.edit": "Edit",
    "ins.sessionDetail.editLandingPage": "Edit landing page",
    "ins.sessionDetail.notFound": "Session not found",
    "ins.sessionDetail.backToSessions": "Back to Sessions",
    "ins.sessionDetail.copyLink": "Copy Session Link",
    "ins.sessionDetail.copied": "Copied",
    "ins.sessionDetail.meetingRoom": "Meeting Room",
    "ins.sessionDetail.roomId": "Room ID:",
    "ins.sessionDetail.roomSecurity": "Room secured with JWT — instructor is moderator, student is participant",
    "ins.sessionDetail.inviteSubscribers": "Invite Subscribers",
    "ins.sessionDetail.inviteToBuy": "Invite to Buy",
    "ins.sessionDetail.grantFreeAccess": "Grant Free Access",
    "ins.sessionDetail.stats": "Statistics",
    "ins.sessionDetail.bookings": "Bookings (confirmed)",
    "ins.sessionDetail.actualAttendance": "Actual Attendance",
    "ins.sessionDetail.revenue": "Revenue",
    "ins.sessionDetail.type": "Type",
    "ins.sessionDetail.status": "Status",
    "ins.sessionDetail.privateType": "Private",
    "ins.sessionDetail.groupType": "Group",
    "ins.sessionDetail.statusScheduled": "Scheduled",
    "ins.sessionDetail.statusLive": "Live",
    "ins.sessionDetail.statusCompleted": "Completed",
    "ins.sessionDetail.statusCancelled": "Cancelled",
    "ins.sessionDetail.statusChanged": "Status changed to {status}",
    "ins.sessionDetail.recordingUploaded": "Recording uploaded",
    "ins.sessionDetail.linkCopied": "Link copied",

    // StudentDetail
    "ins.studentDetail.chat": "Chat",
    "ins.studentDetail.enrollInCourse": "Enroll in Course",
    "ins.studentDetail.suspend": "Suspend",
    "ins.studentDetail.activate": "Activate",
    "ins.studentDetail.ban": "Ban",
    "ins.studentDetail.unban": "Unban",
    "ins.studentDetail.activated": "Activated",
    "ins.studentDetail.suspended": "Suspended",
    "ins.studentDetail.banned": "Banned",
    "ins.studentDetail.tabOverview": "Overview",
    "ins.studentDetail.tabCourses": "Courses",
    "ins.studentDetail.tabQuizzes": "Quizzes",
    "ins.studentDetail.tabPayments": "Payments",
    "ins.studentDetail.tabCertificates": "Certificates",
    "ins.studentDetail.tabComments": "Comments",
    "ins.studentDetail.issueManually": "Issue Certificate Manually",
    "ins.studentDetail.noCertificates": "No certificates issued",
    "ins.studentDetail.issued": "Issued",
    "ins.studentDetail.download": "Download",
    "ins.studentDetail.certNumber": "No:",
    "ins.studentDetail.certError": "Failed to issue certificate",
    "ins.studentDetail.certIssued": "Certificate Issued",
    "ins.studentDetail.certIssuedDesc": "Certificate generated successfully.",
    "ins.studentDetail.certExists": "Certificate Already Exists",
    "ins.studentDetail.certExistsDesc": "This certificate was already issued.",

    // Onboarding
    "ins.onboarding.setupTitle": "Set Up Your Academy",
    "ins.onboarding.guideTitle": "Academy Setup Guide",
    "ins.onboarding.dashboard": "Dashboard",
    "ins.onboarding.stepOf": "Step {step} of {total}",
    "ins.onboarding.guideHint": "You can review setup steps here anytime",
    "ins.onboarding.loadError": "Error loading data",
    "ins.onboarding.completeError": "Failed to complete setup. Try again.",

    // Branding
    "ins.branding.title": "Visual Identity",
    "ins.branding.desc": "Customize your academy's overall appearance",
    "ins.branding.saveChanges": "Save Changes",
    "ins.branding.saved": "Visual identity updated successfully.",

    // TemplateSelection
    "ins.templates.title": "Templates",
    "ins.templates.desc": "Choose the right template for your academy — you can change it anytime",
    "ins.templates.updated": "Academy template changed successfully.",

    // QuestionBank
    "ins.questionBank.title": "Question Bank",
    "ins.questionBank.desc": "Create questions and use them in your course quizzes — {n} question(s)",
    "ins.questionBank.proOnly": "Question Bank is available on Pro plan only.",
    "ins.questionBank.searchPlaceholder": "Search questions...",
    "ins.questionBank.allTypes": "All Types",
    "ins.questionBank.allCourses": "All Courses",
    "ins.questionBank.noMatch": "No matching questions",
    "ins.questionBank.copy": "Copy",
    "ins.questionBank.copied": "Copied",
    "ins.questionBank.copiedDesc": "Question copied to \"{quiz}\"",
    "ins.questionBank.point": "point",
    "ins.questionBank.multipleChoice": "Multiple Choice",
    "ins.questionBank.multiSelect": "Multi Select",
    "ins.questionBank.trueFalse": "True/False",
    "ins.questionBank.matching": "Matching",
    "ins.questionBank.fillBlank": "Fill in the Blank",
    "ins.questionBank.ordering": "Ordering",

    // EmailTemplateEditor
    "ins.emailEditor.active": "Active",
    "ins.emailEditor.save": "Save",
    "ins.emailEditor.emailSubject": "Email Subject",
    "ins.emailEditor.emailSubjectPlaceholder": "Email subject...",
    "ins.emailEditor.content": "Content",
    "ins.emailEditor.recipientScope": "Recipient Scope",
    "ins.emailEditor.triggerTargetOnly": "Triggered student only",
    "ins.emailEditor.courseStudents": "Specific course students",
    "ins.emailEditor.allStudents": "All students",
    "ins.emailEditor.targetCourse": "Target Course",
    "ins.emailEditor.welcome": "Welcome new student",
    "ins.emailEditor.enrollment": "Course enrollment",
    "ins.emailEditor.courseCompleted": "Course completed",
    "ins.emailEditor.quizPassed": "Quiz passed",
    "ins.emailEditor.quizFailed": "Quiz failed",
    "ins.emailEditor.certificateIssued": "Certificate issued",
    "ins.emailEditor.paymentReceipt": "Payment receipt",
    "ins.emailEditor.paymentFailed": "Payment failed",
    "ins.emailEditor.sessionReminder": "Session reminder",
    "ins.emailEditor.refundProcessed": "Refund processed",
    "ins.emailEditor.inactivityReminder": "Inactivity reminder",

    // Common page messages
    // Language
    "lang.switch": "عربي",
    "lang.current": "English",

    // ===== Learning Center (instructor + admin) =====
    "learning.center.title": "Learning Center",
    "learning.center.subtitle": "Guides and tutorials to master the Dwrrah platform",
    "learning.center.searchPlaceholder": "Search the center...",
    "learning.center.pinned": "Pinned articles",
    "learning.center.categories": "Categories",
    "learning.center.empty": "No learning content yet",
    "learning.center.emptyCat": "No articles in this category yet",
    "learning.center.notFound": "Category not found",
    "learning.center.articleNotFound": "Article not found",
    "learning.center.backToHome": "Learning Center",
    "learning.center.related": "Related articles",
    "learning.center.article": "article",
    "learning.center.articles": "articles",
    // Admin · articles
    "learning.art.search": "Search articles...",
    "learning.art.allCats": "All categories",
    "learning.art.allStatuses": "All statuses",
    "learning.art.new": "New article",
    "learning.art.newTitle": "New article",
    "learning.art.empty": "No learning articles yet",
    "learning.art.noCategory": "Uncategorized",
    "learning.art.deleteTitle": "Delete article?",
    "learning.art.deleteDesc": "This action cannot be undone.",
    // Admin · editor
    "learning.editor.needsArabic": "Write the Arabic content first",
    "learning.editor.translated": "Translated — don't forget to save",
    "learning.editor.translateFailed": "Translation failed",
    "learning.editor.confirmRemoveEn": "Remove the English version?",
    "learning.editor.enRemoved": "Removed — save to confirm",
    "learning.editor.titleRequired": "Arabic title is required",
    "learning.editor.source": "Source",
    "learning.editor.removeEn": "Remove English",
    "learning.editor.translateEn": "Translate to English",
    "learning.editor.title": "Title",
    "learning.editor.excerpt": "Excerpt",
    "learning.editor.body": "Article body",
    "learning.editor.aiNotice": "This is an AI-translated version — review and edit before publishing.",
    "learning.editor.video": "Video (optional)",
    "learning.editor.embedUrl": "External video URL (YouTube/Vimeo/Loom)",
    "learning.editor.or": "or",
    "learning.editor.uploadVideo": "Upload a video (mp4/webm, up to 500MB)",
    "learning.editor.chooseVideo": "Choose video",
    "learning.editor.preview": "Preview",
    "learning.editor.publish": "Publish",
    "learning.editor.category": "Category",
    "learning.editor.noCategory": "Uncategorized",
    "learning.editor.status": "Status",
    "learning.editor.pinned": "Pinned",
    "learning.editor.slug": "Slug",
    "learning.editor.sortOrder": "Sort order",
    "learning.editor.cover": "Cover image",
    "learning.editor.uploadCover": "Upload image",
    "learning.editor.tags": "Tags",
    "learning.editor.noTags": "No tags yet",
    "learning.editor.imageTooLarge": "Image is larger than 5MB",
    "learning.editor.videoBadType": "Unsupported video type (mp4/webm only)",
    "learning.editor.videoTooLarge": "Video is larger than 500MB",
    // Admin · categories
    "learning.cat.confirmDelete": "Delete category? Articles will be unlinked.",
    "learning.cat.nameAr": "Name (AR)",
    "learning.cat.nameEn": "Name (EN)",
    "learning.cat.placeholderAr": "e.g. Courses",
    "learning.cat.icon": "Icon (lucide)",
    "learning.cat.add": "Add category",
    // Admin · tags
    "learning.tag.confirmDelete": "Delete tag?",
    "learning.tag.nameAr": "Name (AR)",
    "learning.tag.nameEn": "Name (EN)",
    "learning.tag.placeholderAr": "e.g. Beginner",
    "learning.tag.add": "Add tag",
    // Admin · settings
    "learning.settings.title": "Learning Center settings",
    "learning.settings.toggleLabel": "Show Learning Center to instructors",
    "learning.settings.toggleDesc": "When enabled, the Learning Center link appears in every instructor's sidebar. When disabled, it is fully hidden.",
    "learning.settings.enabled": "Enabled",
    "learning.settings.disabled": "Disabled",
  },
};
